/*
 * Slovenščina — the console's first language, and the SCHEMA the other one is checked
 * against: `en.ts` is `satisfies MessageSchema`, so a key missing from it (or one only it has)
 * fails `yarn type-check`. Plural messages carry the four Slovenian forms (1, 2, 3–4, 5+) in
 * that order and are called with the count (`t(key, n)`); `libs/i18n/index.ts` picks the form.
 * The labels of the API's enums are the contract's (docs/API.md → UI labels). A literal
 * variable name in a message is written with vue-i18n's literal interpolation.
 */
const sl = {
    assistance: {
        digit: 'Številčna tipka',
        title: "Oddaljena pomoč",
        subtitle: "Oglejte si zaslon in pomagajte z virtualnim daljincem.",
        start: "Začni pomoč",
        connected: "Pomoč je aktivna",
        stopped: "Pomoč je ustavljena",
        remaining: "Še {seconds} s",
        screen_alt: "Trenutni zaslon naprave {name}",
        waiting: "Čakam posnetek zaslona …",
        no_frame: "Za ogled zaslona začnite pomoč.",
        stale: "Posnetek ni več svež",
        captured: "Zajeto: {at}",
        refresh_hint: "Zaslon se osvežuje približno vsake 4 sekunde.",
        scope: "Prikaz in upravljanje aplikacije Imprion TV. Video je lahko prikazan črno.",
        up: "Gor",
        down: "Dol",
        left: "Levo",
        right: "Desno",
        ok: "OK",
        back: "Nazaj",
        home: "Domov",
        channel_up: "Naslednji kanal",
        channel_down: "Prejšnji kanal",
        play_pause: "Predvajaj ali ustavi",
        keyboard: "Uporabite lahko tudi puščice, Enter, Backspace, Home in številke na tipkovnici.",
        sending: "Čakam potrditev pritiska …",
        guest_notice: "Na TV je prikazano obvestilo o oddaljeni pomoči.",
        stop: "Končaj pomoč",
        reconnect: "Ponovno poveži",
        forbidden: "Oddaljena pomoč je na voljo skrbnikom.",
        offline: "Naprava mora biti povezana in dosegljiva.",
        upgrade: "Za ogled zaslona posodobite Android aplikacijo na STB.",
        failed: "Posnetka ni bilo mogoče pridobiti. Preverite povezavo naprave in poskusite znova.",
        timeout: "Petminutna pomoč se je zaključila. Po potrebi se povežite znova.",
        key_failed: "Naprava pritiska ni potrdila. Pomoč je ustavljena; po potrebi se povežite znova.",
    },
    general: {
        add:      'Dodaj',
        edit:     'Uredi',
        delete:   'Izbriši',
        cancel:   'Prekliči',
        save:     'Shrani',
        close:    'Zapri',
        confirm:  'Potrdi',
        search:   'Išči …',
        loading:  'Nalagam …',
        saving:   'Shranjujem',
        sign_out: 'Odjava',
        open:     'Odpri',
        actions:  'Možnosti',
        none:     '—',
    },
    changelog: {
        title:     'Novosti',
        open:      'Verzija in novosti',
        running:   'Trenutno teče',
        console:   'Konzola',
        screen:    'Zaslon',
        guest:     'Aplikacija za goste',
        android:   'Android',
        api:       'API',
        new_badge: 'Novo',
    },
    layout: {
        open_menu:  'Odpri meni',
        close_menu: 'Zapri meni',
    },
    color_mode: {
        switch_to_light: 'Preklopi na svetli način',
        switch_to_dark:  'Preklopi na temni način',
    },
    context: {
        label:       'Prostor stranke',
        help:        'Nastavitve se uporabljajo za izbrani prostor.',
        placeholder: 'Izberite stranko',
        inactive:    '{name} (izklopljena)',
        not_found:   'Tega prostora stranke ni mogoče odpreti.',
    },
    role: {
        admin:          'Skrbnik',
        editor:         'Urednik',
        reception:      'Recepcija',
        kitchen:        'Kuhinja',
        technician:     'Tehnik',
        housekeeping:   'Gospodinjstvo',
        provider_admin: 'Skrbnik ponudnika',
    },
    field_view: {
        open:              'Pogled za teren',
        provisioning:      'Namestitev sprejemnikov',
        provisioning_help: 'Na prenosniku z agentom',
        title:             'Teren',
        hotel:             'Hotel',
        full_console:      'Celotna konzola',
        refresh:           'Osveži',
        refreshed:         'osveženo {ago}',
        stale_data:        'Ni povezave — prikazani so zadnji prejeti podatki.',
        load_failed:       'Podatkov ni bilo mogoče naložiti. Poskusim znova čez 30 s.',
        search:            'Išči sobo ali napravo',
        clear_search:      'Počisti iskanje',
        filters:           'Filtri',
        filter: {
            offline:  'Brez zveze',
            outdated: 'Zastarela verzija',
            errors:   'Napake',
            pending:  'Čaka na potrditev',
        },
        empty:             'Hotel še nima sob ne naprav.',
        no_match:          'Nobena soba ali naprava ne ustreza.',
        no_device:         'Ni naprave',
        no_room:           'Brez sobe',
        pair_here:         'Seznani',
        new_device:        'Nova naprava',
        inactive:          'Izklopljena',
        outdated_short:    'Zastarela',
        error_short:       'Napaka',
        not_paired:        'Ni seznanjena — čaka na zaslon',
        version_screen:    'Zaslon {version}',
        version_app:       'Aplikacija {version}',
        version_webview:   'WebView {version}',
        pending_help:      'Zasloni s kodo, ki čakajo v tem hotelu. Zaslon v drugem omrežju seznanite z vnosom kode (Nova naprava).',
        pending_none:      'Noben zaslon ne čaka.',
        pending_gone:      'Ta zaslon ne čaka več (že seznanjen ali zavrnjen).',
        pair_this:         'Seznani ta zaslon',
        back:              'Seznam',
        device_gone:       'Te naprave ni več.',
        rename:            'Preimenuj',
        change_room:       'Zamenjaj sobo',
        search_room:       'Išči sobo …',
        pick_room:         'Izberite sobo',
        saved:             'Shranjeno.',
        active_help:       'Izklopljena naprava kaže le zaslon »ni aktivna«. Soba ima lahko več televizorjev; eden je glavni.',
        activated:         'Naprava je vklopljena.',
        deactivated:       'Naprava je izklopljena.',
        reset_pending:     'Ponastavitev čaka',
        pair_screen:       'Seznani zaslon',
        time_title:        'Čas in ura',
        time_now:          'Zdaj: {zone} · {clock}',
        set_zone:          'Nastavi časovni pas',
        copy_support:      'Kopiraj podatke za podporo',
        copied:            'Podatki so v odložišču.',
        copy_failed:       'Kopiranje ni uspelo.',
        recent:            'Nedavno',
        nothing_recent:    'V zadnjih 7 dneh ni bilo opozoril ne ukazov.',
        resolved_at:       'rešeno {at}',
        still_open:        'še odprto',
        confirm_collect_logs: 'Zaslon »{name}« bo zbral svoj dnevnik in ga poslal podpori.',
        reset_again_title: 'Res ponastavim?',
        reset_again:       'Gost v sobi {room} bo odjavljen iz YouTuba, Netflixa, Disney+, HBO Max in Prime Video, budilka bo izbrisana in povezava za telefon zamenjana. Tega ni mogoče razveljaviti.',
        reset_confirm:     'Da, ponastavi',
        fact: {
            network:    'Omrežje',
            tv:         'TV vhod',
            screen:     'Na zaslonu',
            screen_app: 'Zaslon',
            shell:      'Aplikacija',
            webview:    'WebView',
            latest:     'Najnovejša aplikacija',
            model:      'Model',
            ip:         'IP naslov',
            time:       'Časovni pas',
            reported:   'Zadnje poročilo',
            error:      'Zadnja napaka',
        },
        summary: {
            hotel:    'Hotel',
            device:   'Naprava',
            room:     'Soba',
            status:   'Stanje',
            seen:     'Nazadnje viden',
            model:    'Model',
            versions: 'Verzije',
            latest:   'Na voljo',
            network:  'Omrežje',
            ip:       'IP',
            tv:       'TV vhod',
            screen:   'Na zaslonu',
            time:     'Čas',
            error:    'Napaka',
            alerts:   'Opozorila',
            id:       'ID',
        },
        pair_help:         'Vpišite kodo, ki jo kaže televizor, izberite sobo in seznanite.',
        pair_pending_help: 'Izberite sobo in seznanite zaslon s to kodo.',
        code_label:        'Koda na televizorju',
        code_placeholder:  'ABC 123',
        code_short:        'Koda ima 6 znakov.',
        code_unknown:      'Noben zaslon ne kaže te kode. Preverite jo — koda velja 24 ur.',
        code_throttled:    'Preveč poskusov. Počakajte minuto.',
        pair_target:       'Naprava',
        pair_into:         'V obstoječo: {name}',
        pair_new:          'Nova naprava',
        pair_new_instead:  'Raje nova naprava',
        default_name:      '{room} — TV',
        pair:              'Seznani',
        paired:            'Seznanjeno: {name}',
        pair_failed:       'Seznanitev ni uspela.',
    },
    locale: {
        label: 'Jezik',
        sl:    'Slovenščina',
        en:    'English',
    },
    success: {
        add:    'Dodano.',
        edit:   'Shranjeno.',
        delete: 'Izbrisano.',
    },
    validation: {
        required: 'Obvezno polje.',
        email:    'Neveljaven e-poštni naslov.',
        too_long: 'Predolgo.',
        invalid:  'Neveljavna vrednost.',
        url:      'Naslov se začne s http:// ali https://.',
    },
    date: {
        pick:  'Izberi datum',
        clear: 'Počisti datum',
    },
    time: {
        today_at:    'danes {time}',
        for_a_minute: 'eno minuto',
        for_an_hour:  'eno uro',
        tomorrow_at: 'jutri {time}',
    },
    auth: {
        username:          'Uporabniško ime',
        password:          'Geslo',
        password_show:     'Pokaži geslo',
        password_hide:     'Skrij geslo',
        sign_in:           'Prijava',
        or:                'ali',
        failed:            'Napačno uporabniško ime ali geslo.',
        too_many_attempts: 'Preveč neuspelih poskusov. Poskusite znova čez nekaj minut.',
        welcome:           'Prijavite se v svoj prostor.',
    },
    passkey: {
        passkeys:        'Ključi za prijavo (passkeys)',
        subtitle:        'Prijava s prstnim odtisom, obrazom ali varnostnim ključem namesto gesla.',
        sign_in:         'Prijava s passkeyem',
        sign_in_failed:  'Prijava s passkeyem ni uspela.',
        add:             'Dodaj passkey',
        added:           'Passkey je dodan.',
        failed:          'Passkeya ni bilo mogoče dodati.',
        name:            'Ime',
        name_help:       'Po imenu boste ključ prepoznali na seznamu, npr. »Službeni prenosnik«.',
        default_name:    'Moja naprava',
        continue:        'Nadaljuj',
        created:         'Dodan',
        last_used:       'Zadnja uporaba',
        never_used:      'Še ni bil uporabljen',
        empty:           'Nimate še nobenega passkeya.',
        delete:          'Izbriši passkey',
        confirm_delete:  'Izbrišem passkey »{name}«? S to napravo se ne boste več mogli prijaviti brez gesla.',
        removed:         'Passkey je izbrisan.',
        delete_failed:   'Passkeya ni bilo mogoče izbrisati.',
        unavailable:     'Prijava s passkeyi na tem strežniku ni nastavljena.',
        unsupported:     'Ta brskalnik ne podpira passkeyev (potrebna je varna povezava HTTPS).',
    },
    password: {
        change:        'Spremeni geslo',
        current:       'Trenutno geslo',
        new:           'Novo geslo',
        repeat:        'Ponovite novo geslo',
        mismatch:      'Gesli se ne ujemata.',
        changed:       'Geslo je spremenjeno.',
        failed:        'Gesla ni bilo mogoče spremeniti.',
        wrong_current: 'Trenutno geslo ni pravilno.',
    },
    user: {
        users:               'Uporabniki',
        subtitle_tenant:     'Kdo se lahko prijavi v prostor stranke in kaj sme urejati.',
        subtitle_provider:   'Uporabniki vseh strank in skrbniki ponudnika.',
        add:                 'Nov uporabnik',
        edit:                'Uredi uporabnika',
        delete:              'Izbriši uporabnika',
        name:                'Ime in priimek',
        username:            'Uporabniško ime',
        username_rule:       '3–32 znakov: male črke, številke, pika, podčrtaj ali vezaj.',
        username_fixed:      'Uporabniškega imena ni mogoče spremeniti.',
        username_taken:      'To uporabniško ime že obstaja.',
        email:               'E-pošta',
        role:                'Vloga',
        role_help:           'Urednik ureja kanale, EPG, videz in vsebino; skrbnik še sobe, naprave, povezavo s PMS, uvoz in uporabnike. Recepcija obdeluje zahteve gostov in ponastavitve sob, ne ureja vsebine; kuhinja vidi samo naročila v sobo in jedilnik; tehnik vidi na telefonu le sobe in naprave, jih seznani, premesti in jim pošlje varne ukaze.',
        space:               'Prostor',
        tenants:             'Prostori',
        other_tenants:       'Drugi prostori',
        no_memberships:      'Brez prostora',
        filter_all:          'Vsi prostori',
        filter_provider:     'Skrbniki ponudnika',
        password:            'Geslo',
        password_reset:      'Novo geslo (ponastavitev)',
        password_keep:       'Pustite prazno, da geslo ostane nespremenjeno.',
        password_rule:       'Najmanj 8 znakov.',
        password_too_short:  'Geslo mora imeti najmanj 8 znakov.',
        active:              'Aktiven',
        active_help:         'Neaktiven uporabnik se ne more prijaviti.',
        status:              'Stanje',
        status_active:       'Aktiven',
        status_inactive:     'Neaktiven',
        last_login:          'Zadnja prijava',
        never_logged_in:     'Še nikoli',
        search:              'Išči po imenu ali e-pošti …',
        show_inactive:       'Pokaži neaktivne',
        empty:               'Ni uporabnikov.',
        you:                 'Vi',
        cannot_delete_self:  'Svojega računa ne morete izbrisati.',
        confirm_delete:      'Izbrišem uporabnika {name}? Račun se izbriše v vseh prostorih in se ne bo mogel več prijaviti.',
        deleted:             'Uporabnik je izbrisan.',
        delete_failed:       'Uporabnika ni bilo mogoče izbrisati.',
        last_admin:          'To je zadnji aktivni skrbnik — ostati mora vsaj eden.',
        locale:              'Jezik',
        locale_help:         'Jezik aplikacije za tega uporabnika.',
        role_in_tenant:      'Vloga v tem prostoru',
        not_manageable:      'Uporabnik je tudi v drugih prostorih — osebne podatke ureja ponudnik.',
        remove:              'Odstrani iz prostora',
        confirm_remove:      'Odstranim uporabnika {name} iz tega prostora? Če je član še drugih prostorov, njegov račun tam ostane; sicer se ne bo mogel več prijaviti.',
        removed:             'Uporabnik je odstranjen iz prostora.',
        remove_failed:       'Uporabnika ni bilo mogoče odstraniti iz prostora.',
        cannot_remove_self:  'Sebe ne morete odstraniti iz prostora.',
        add_existing:        'Dodaj obstoječega',
        add_existing_title:  'Dodaj obstoječega uporabnika',
        add_existing_help:   'Točno uporabniško ime osebe, ki že ima račun v drugem prostoru.',
        add_existing_action: 'Dodaj v prostor',
        exists_offer:        'Uporabnik s tem imenom že obstaja. Dodaj obstoječega uporabnika v ta prostor?',
        member_added:        'Uporabnik je dodan v prostor.',
        member_not_found:    'Aktivnega uporabnika s tem uporabniškim imenom ni.',
        already_member:      'Ta uporabnik je že član tega prostora.',
        provider_admin:      'Skrbnik ponudnika',
        provider_admin_help: 'Upravlja vse stranke, uporabnike in nastavitve ponudnika; v vsakem prostoru ima pravice skrbnika.',
        memberships:         'Prostori',
        memberships_help:    'V katerih prostorih strank uporabnik dela in s katero vlogo.',
        membership_add:      'Dodaj prostor',
        membership_remove:   'Odstrani prostor',
        memberships_none:    'Uporabnik še ni v nobenem prostoru.',
        memberships_required: 'Dodajte vsaj en prostor ali vklopite skrbnika ponudnika.',
        memberships_duplicate: 'Vsak prostor je lahko naveden le enkrat.',
    },
    location: {
        label:             'Kraj',
        label_placeholder: 'Ljubljana',
        lat:               'Zemljepisna širina',
        lon:               'Zemljepisna dolžina',
        lat_range:         'Med −90 in 90.',
        lon_range:         'Med −180 in 180.',
    },
    brand: {
        product_name: 'Imprion TV',
        provided_by:  'Ponudnik',
    },
    nav: {
        overview:       'Pregled',
        rooms:          'Sobe',
        devices:        'Naprave',
        tv:             'TV',
        content:        'Vsebina',
        group_hotel:    'Hotel',
        connector:      'Povezava s PMS',
        import:         'Uvoz',
        tenant_users:   'Uporabniki',
        group_provider: 'Ponudnik',
        tenants:        'Stranke',
        users:          'Uporabniki',
        android:        'Android aplikacija',
        tizen:          'Samsung TV (Tizen)',
        screen_themes:  'Teme (knjižnica)',
        milo:           'Milo',
        milo_provider:  'Milo (AI)',
        settings:       'Nastavitve',
        requests:       'Zahteve gostov',
        room_service:   'Postrežba v sobo',
        occupancy:      'Zasedenost',
        feedback:       'Pohvale, pritožbe in predlogi',
    },
    errors: {
        generic_error:            'Prišlo je do napake.',
        invalid_data:             'Podatki niso veljavni.',
        invalid_field:            'Polje »{field}« ni veljavno.',
        not_found:                'Ne obstaja (več).',
        unauthorized:             'Prijava je potekla. Prijavite se znova.',
        forbidden:                'Za to nimate dovoljenja.',
        on_add:                   'Dodajanje ni uspelo.',
        on_edit:                  'Shranjevanje ni uspelo.',
        on_delete:                'Brisanje ni uspelo.',
        on_load:                  'Nalaganje ni uspelo.',
        tenant_inactive:          'Prostor stranke je izklopljen — prijava ni mogoča.',
        weather_unavailable:      'Vremenska napoved trenutno ni dosegljiva.',
        in_use:                   'Še je v uporabi, zato je ni mogoče izbrisati.',
        in_use_list:              'Še je v uporabi, zato je ni mogoče izbrisati: {list}',
        not_available:            'To zdaj ni mogoče.',
        connector_not_configured: 'Povezava s PMS ni nastavljena.',
        connector_failed:         'PMS ni odgovoril ali je zahtevo zavrnil.',
        connector_failed_reason:  'PMS je zahtevo zavrnil: {reason}',
        epg_failed:               'Vir EPG ni odgovoril ali ni datoteka XMLTV.',
        epg_failed_reason:        'Vir EPG ni uspel: {reason}',
        request_status:     'Zahtevo je medtem obdelal že nekdo drug ({status}). Seznam je osvežen.',
        pos_failed:         'Imprion POS se ni odzval.',
        pos_failed_reason:  'Imprion POS: {reason}',
        pos_not_configured: 'Povezava z Imprion POS ni nastavljena (žeton in poslovna enota).',
        tms_not_configured: 'TMS (ATV Bridge) ni nastavljen: vpišite naslov, API ključ in ime opravila ter ga vključite.',
        tms_failed:         'TMS (ATV Bridge) se ni odzval.',
        tms_failed_reason:  'TMS (ATV Bridge): {reason}',
        no_device_mac:      'Naprava nima MAC naslova, po katerem bi jo TMS poznal. Vpišite ga v urejanju naprave.',
        item_unavailable:   'Artikla ni več na jedilniku.',
        too_many_open:      'Soba ima preveč odprtih zahtev (največ {max}).',
        stay_overlap:       'Soba je v teh dneh že zasedena ({arrival} – {departure}).',
        stay_state:         'Bivanje je že »{state}« — tega zdaj ni mogoče.',
        room_has_stays:     'Sobe ni mogoče izbrisati: ima {n} bivanje (gost v sobi ali prihodnja rezervacija). Na strani Zasedenost ga odjavite, prestavite ali izbrišite. | Sobe ni mogoče izbrisati: ima {n} bivanji (gost v sobi ali prihodnja rezervacija). Na strani Zasedenost ju odjavite, prestavite ali izbrišite. | Sobe ni mogoče izbrisati: ima {n} bivanja (gost v sobi ali prihodnje rezervacije). Na strani Zasedenost jih odjavite, prestavite ali izbrišite. | Sobe ni mogoče izbrisati: ima {n} bivanj (gost v sobi ali prihodnje rezervacije). Na strani Zasedenost jih odjavite, prestavite ali izbrišite.',
        stay_dates:         'Datumi tega ne dopuščajo danes: prijava je mogoča od dneva prihoda do dneva odhoda, gostu v sobi pa prihoda ni mogoče prestaviti v prihodnost.',
    },
    settings: {
        title:             'Nastavitve',
        subtitle:          'Vaš račun, jezik in prijava.',
        profile:           'Profil',
        profile_subtitle:  'Ime in e-poštni naslov vašega računa.',
        account:           'Račun',
        tenant:            'Prostor stranke',
        tenants:           'Prostori strank',
        role:              'Vloga',
        locale_help:       'Jezik konzole.',
        provider:          'Ponudnik',
        provider_subtitle: 'Ime ponudnika, ki ga konzola in zasloni pokažejo kot izdajatelja izdelka, in naslov Leviathana.',
        provider_name:     'Ime ponudnika',
        leviathan_url:      'Naslov Leviathana',
        leviathan_url_help: 'IT TEAM-ov Leviathan (upravljanje STB-jev): »Odpri v Leviathanu« pri napravi odpre njegov seznam terminalov. Prazno = privzeti naslov.',
        leviathan_url_rule: 'Naslov se začne s https://, brez ? in #.',
        version:           'Različica',
    },
    mail_settings: {
        title:              'Pošta',
        subtitle:           'Strežnik SMTP, prek katerega konzola pošilja e-pošto — zdaj opozorila naprav.',
        source_console:     'Iz konzole',
        source_env:         'Iz strežnika (.env)',
        source_none:        'Ni nastavljeno',
        env_note:           'Velja nastavitev s strežnika (.env). Ko shranite nastavitve tukaj, veljajo te — v celoti.',
        none_note:          'Pošta ni nastavljena: opozorila se samo zapišejo in pokažejo v konzoli.',
        console_note:       'Velja nastavitev iz konzole; nastavitev s strežnika (.env) se ne uporablja.',
        env_password_set:   'Geslo je nastavljeno na strežniku.',
        env_password_unset: 'Na strežniku ni gesla (brez prijave).',
        set_in_console:     'Nastavi v konzoli',
        use_env:            'Uporabi nastavitve strežnika',
        remove:             'Odstrani nastavitve',
        use_env_confirm:    'Odstranim nastavitve pošte iz konzole? Znova bodo veljale nastavitve s strežnika (.env).',
        remove_confirm:     'Odstranim nastavitve pošte? Opozorila se potem samo zapišejo, po e-pošti ne gredo.',
        removed:            'Nastavitve pošte so odstranjene.',
        host:               'Strežnik',
        host_help:          'Ime strežnika ali IP-naslov, brez vrat in brez smtp://.',
        host_rule:          'Ime strežnika (črke, številke, - in .) ali IP-naslov.',
        port:               'Vrata',
        port_help:          '587 = STARTTLS, 465 = TLS od začetka.',
        user:               'Uporabnik',
        user_help:          'Prazno = brez prijave.',
        from:               'Pošiljatelj',
        from_help:          "Naslov, lahko z imenom: Imprion TV <tv-no-reply{'@'}imprion.io>. Mora biti uporabnikov predal ali vzdevek, s katerim sme pošiljati.",
        from_rule:          'Vpišite e-poštni naslov, lahko z imenom (Ime <naslov>).',
        password:           'Geslo',
        password_help:      'Shrani se šifrirano in se nikoli ne pokaže.',
        password_set:       'Shranjeno {hint}',
        password_unset:     'Ni gesla',
        password_replace:   'Zamenjaj geslo',
        password_keep:      'Obdrži shranjeno',
        password_required:  'Z uporabnikom je geslo obvezno.',
        password_user_none: 'Brez uporabnika se geslo ne uporablja (ob shranjevanju se odstrani).',
        test_title:         'Testna pošta',
        test_help:          'Pošlje kratko sporočilo prek shranjenih nastavitev, ki veljajo.',
        test_unsaved:       'Spremembe še niso shranjene — preizkusi se shranjeno.',
        test_to:            'Prejemnik',
        test_send:          'Pošlji testno pošto',
        test_ok:            'Testna pošta je bila poslana ob {time} na {to}.',
        test_ok_source:     'Uporabljene nastavitve — {source}',
        test_failed:        'Testna pošta ni bila poslana',
        server_said:        'Strežnik: {message}',
        failure: {
            not_configured:     'Pošta ni nastavljena.',
            unreachable:        'Strežnik ni dosegljiv — preverite ime strežnika in vrata.',
            timeout:            'Strežnik ni odgovoril pravočasno.',
            tls:                'Varna povezava (TLS) ni uspela — potrdilo strežnika ne ustreza imenu ali ni veljavno.',
            no_starttls:        'Strežnik ne ponuja STARTTLS, geslo ne bi bilo zaščiteno — uporabite vrata 587 s STARTTLS ali 465.',
            auth_failed:        'Prijava ni uspela — uporabnik ali geslo ni pravilen.',
            auth_required:      'Strežnik zahteva prijavo — vpišite uporabnika in geslo.',
            sender_rejected:    'Strežnik ne dovoli pošiljanja s tega naslova pošiljatelja. Pošiljatelj mora biti uporabnikov predal ali vzdevek, s katerim sme pošiljati (Mailcow: vzdevki predala oziroma sender_acl).',
            recipient_rejected: 'Strežnik je zavrnil prejemnika — preverite naslov.',
            rejected:           'Strežnik je sporočilo zavrnil.',
        },
    },
    tenant: {
        tenants:          'Stranke',
        subtitle:         'Vsak hotel ima svoj prostor: sobe, naprave, kanale, vsebino in uporabnike.',
        add:              'Nova stranka',
        edit:             'Uredi stranko',
        delete:           'Izbriši stranko',
        name:             'Ime',
        name_placeholder: 'Grill Oaza',
        slug:             'Oznaka',
        slug_help:        'Male črke, številke in vezaj (2–40 znakov). Nastane iz imena.',
        slug_rule:        'Samo male črke, številke in vezaj, 2–40 znakov.',
        slug_taken:       'To oznako že uporablja druga stranka.',
        timezone:         'Časovni pas',
        timezone_help:    'Po tem času tečejo program, EPG in ure na zaslonih.',
        currency:         'Valuta',
        currency_help:    'Koda ISO 4217, npr. EUR.',
        currency_rule:    'Tri črke, npr. EUR.',
        domain:           'Lastna domena',
        domain_help:      'Npr. tv.hotel.si — prijava v konzolo in zasloni na tej domeni pripadajo tej stranki. Prazno = brez.',
        domain_rule:      'Neveljavna domena.',
        location:         'Kraj za vreme',
        location_help:    'Vremenska napoved na zaslonih je za ta kraj.',
        active:           'Aktivna',
        active_help:      'Izklopljena stranka se ne more prijaviti, njeni zasloni pokažejo »ni aktivno«.',
        status:           'Stanje',
        status_active:    'Aktivna',
        status_inactive:  'Izklopljena',
        rooms:            'Sobe',
        devices:          'Naprave',
        users:            'Uporabniki',
        connector:        'PMS',
        created:          'Ustvarjena',
        open:             'Odpri prostor',
        confirm_delete:   'Izbrišem stranko {name}? Njeni uporabniki se ne bodo mogli več prijaviti, njeni zasloni se odjavijo.',
        deleted:          'Stranka je izbrisana.',
        delete_failed:    'Stranke ni bilo mogoče izbrisati.',
        empty:            'Ni še nobene stranke.',
        empty_help:       'Dodajte prvi hotel, nato odprite njegov prostor.',
        search:           'Išči po imenu ali oznaki …',
        activated:        'Stranka {name} je vklopljena.',
        deactivated:      'Stranka {name} je izklopljena.',
    },
    language: {
        sl: 'Slovenščina',
        en: 'English',
        de: 'Deutsch',
        it: 'Italiano',
        hr: 'Hrvatski',
        hu: 'Magyar',
    },
    localized: {
        editing_in:     'Urejam v',
        field_language: 'Jezik polja',
        show_all:       'Vsi jeziki',
        all_translated: 'Vse prevedeno',
        missing:        'Manjka {n} prevod | Manjkata {n} prevoda | Manjkajo {n} prevodi | Manjka {n} prevodov',
        lang_missing:   '{name} — manjka v {n} polju | {name} — manjka v {n} poljih | {name} — manjka v {n} poljih | {name} — manjka v {n} poljih',
        lang_complete:  '{name} — vse prevedeno',
        mark_filled:    '{name} — vneseno',
        mark_fallback:  '{name} — ni prevoda, zaslon pokaže {fallback}',
        mark_empty:     '{name} — prazno',
        translate_missing:        'Prevedi manjkajoče',
        translating:              'Milo prevaja v {n} jezik … | Milo prevaja v {n} jezika … | Milo prevaja v {n} jezike … | Milo prevaja v {n} jezikov …',
        translated:               'Prevedeno: {texts} v {languages} — preglejte in shranite.',
        n_texts:                  '{n} besedilo | {n} besedili | {n} besedila | {n} besedil',
        n_languages:              '{n} jezik | {n} jezika | {n} jezike | {n} jezikov',
        translate_partly:         'Nekaj besedil ostaja neprevedenih.',
        translate_none:           'Milo ni prevedel nobenega besedila.',
        translate_failed:         'Prevajanje ni uspelo. Poskusite znova.',
        translate_no_provider:    'Milo nima nastavljenega ponudnika umetne inteligence — obrnite se na ponudnika.',
        translate_quota:          'Današnja omejitev Mila je porabljena — poskusite znova jutri.',
        translate_provider_error: 'Milo se ta trenutek ne more povezati. Poskusite znova.',
        translate_bad_answer:     'Milo ni vrnil berljivega prevoda. Poskusite znova.',
    },
    status: {
        online:   'Na zvezi',
        offline:  'Brez zveze',
        unpaired: 'Ni seznanjen',
    },
    usage: {
        guest:   'Gostova soba',
        signage: 'Javni zaslon',
    },
    orientation: {
        auto:             'Samodejno',
        landscape:        'Ležeče',
        portrait:         'Pokončno',
        portrait_flipped: 'Pokončno (obrnjeno)',
    },
    platform: {
        android: 'Android',
        tizen:   'Samsung Tizen',
        webos:   'LG webOS',
        web:     'Brskalnik',
    },
    connector_type: {
        none:        'Brez PMS',
        imprion_pms: 'Imprion PMS',
        bentral:     'Bentral',
    },
    bentral_status: {
        confirmed:    'Potrjena',
        paid:         'Plačana',
        paid_part:    'Delno plačana',
        notpaid:      'Neplačana',
        pending:      'V obdelavi',
        available:    'Povpraševanje',
        paying:       'Plačilo v teku',
        noresponse:   'Brez odziva',
        noshow:       'Neprihod',
        notavailable: 'Ni na voljo',
        canceled:     'Odpovedana',
    },
    capability: {
        stay:           'Bivanje',
        stays:          'Zasedenost (obdobje)',
        bill:           'Račun',
        services:       'Storitve',
        booking:        'Rezervacije',
        room_service:   'Sobna postrežba',
        program:        'Program',
        program_signup: 'Prijave na program',
        voucher:        'Bon',
        nearby:         'Partnerji v okolici',
        nearby_booking: 'Rezervacije pri partnerjih',
        venue:          'Dvorane',
    },
    epg_status: {
        ok:       'V redu',
        unmapped: 'Brez EPG',
        no_data:  'Ni podatkov',
    },
    stream_health: {
        ok:                   'Dela',
        down:                 'Ne dela',
        down_for:             'Ne dela že {for}',
        never:                'Še ni preverjeno',
        not_checkable:        'Ni mogoče preveriti',
        down_since:           'brez signala od {at}',
        checked:              'preverjeno {ago}',
        disabled_not_checked: 'izklopljen kanal se samodejno ne preverja',
        error_http:           '{status} pri viru',
        error_timeout:        'vir se ni odzval',
        error_unreachable:    'do vira ni povezave',
        error_redirects:      'preveč preusmeritev',
        error_empty_playlist: 'prazen seznam',
        error_not_http:       'vir ni http(s)',
        error_resolve_failed: 'vira ni bilo mogoče poiskati',
        error_not_stream:     'vir ni tok (spletna stran)',
    },
    stream_check: {
        badge_ok:        'Deluje',
        badge_down:      'Ne deluje',
        badge_relay:     'Ne gre na Android škatlah — strežnik pretvarja',
        badge_android:   'Ne gre na Android škatlah',
        badge_unknown:   'Ni preverjeno',
        never:           'Še ni preverjeno — preveri se ponoči ali s »Preveri zdaj«.',
        checked:         'preverjeno {ago}',
        format_unknown:  'oblika ni znana',
        check_now:       'Preveri zdaj',
        checked_ok:      '{name}: preverjeno.',
        platform: {
            android_web:    'Android škatla (radio, videi)',
            android_native: 'Android škatla (TV kanali)',
            tizen:          'Samsung',
            webos:          'LG',
        },
        play: {
            yes:     'predvaja',
            no:      'ne predvaja',
            relay:   'strežnik pretvori v MP3',
            unknown: 'ni znano',
        },
        code: {
            music_failed: 'postaja se ni predvajala',
            native:       'napaka predvajalnika škatle',
        },
    },
    auto_hide: {
        badge:          'Samodejno skrito (ni dosegljivo od {since})',
        badge_detail:   'Gostje tega ne vidijo, dokler ni spet dosegljivo. Zadnja napaka: {reason}. Naslednje preverjanje {next}.',
        next_unknown:   'kmalu',
        title:          'Samodejno skrivanje',
        subtitle:       'Kanali in postaje, ki niso dosegljivi, se gostom skrijejo in vrnejo, ko spet delujejo.',
        switch:         'Samodejno skrij nedosegljive kanale in postaje',
        switch_help:    'Skrije se šele, ko tok trikrat zapored v vsaj 15 minutah ne odgovori (ali dvakrat, če ga zaslon javi kot pokvarjenega). Vrne se ob prvem uspešnem preverjanju. Ročno izklopljenih ne vklaplja.',
        mail:           'Pošlji e-pošto, ko se kaj skrije ali vrne',
        mail_help:      'Prejemniki in razmik so iz nastavitev obveščanja (Naprave › Nastavitve).',
        mail_needs:     'Za e-pošto vklopite obveščanje in dodajte prejemnike v Naprave › Nastavitve.',
        admin_only:     'Nastavitev lahko spremeni skrbnik.',
        how:            'Ko je vklopljeno, strežnik preverja vklopljene kanale in postaje približno vsake 45 minut, skrite vsakih 10 minut. Zaslon, ki trenutno predvaja skrito postajo ali kanal, predvaja naprej; budilka na skriti postaji zazvoni s prvo postajo hiše ali s tonom. Številke kanalov se ne zamaknejo.',
        saved_on:       'Samodejno skrivanje je vklopljeno.',
        saved_off:      'Samodejno skrivanje je izklopljeno — vse skrito je spet vidno.',
        saved:          'Shranjeno.',
        hidden_now:     'Skrito zdaj: {n}',
        none_hidden:    'Trenutno ni skrito nič.',
        events:         'Zgodovina skrivanja',
        events_empty:   'V tem obdobju se ni nič skrilo ali vrnilo.',
        change_hidden:  'skrito',
        change_shown:   'vrnjeno',
        reason_working: 'spet deluje',
        reason_http:    'vir vrača napako',
        reason_option_off: 'samodejno skrivanje izklopljeno',
        back:           '{name} je spet dosegljiv in viden gostom.',
        still:          '{name} še vedno ni dosegljiv.',
    },
    stream_type: {
        auto: 'Samodejno',
        hls:  'HLS',
        ts:   'MPEG-TS',
    },
    proxy: {
        auto:        'Samodejno',
        always:      'Vedno prek strežnika',
        never:       'Nikoli',
        auto_help:   'Viri http:// gredo prek strežnika Imprion TV, https:// se predvajajo neposredno.',
        always_help: 'Vsi tokovi gredo prek strežnika — naslov in žeton ponudnika nikoli ne prideta do zaslona.',
        never_help:  'Zaslon predvaja vir neposredno (tudi žeton je v naslovu).',
    },
    adult: {
        hidden:      'Skriti',
        hidden_help: 'Kanali za odrasle se na sobnem zaslonu ne pokažejo.',
        pin:         'Za kodo PIN',
        pin_help:    'Kanali so na seznamu, odprejo pa se šele s kodo PIN (privzeto).',
        shown:       'Prikazani',
        shown_help:  'Kanali za odrasle so na seznamu kot vsi drugi.',
    },
    screen: {
        home:         'Domov',
        letter:       'Pozdravno pismo',
        program:      'Program',
        services:     'Storitve',
        local:        'Okolica',
        house:        'Hiša',
        bill:         'Račun',
        booking:      'Rezervacije',
        music:        'Glasba',
        alarm:        'Budilka',
        tv:           'Televizija',
        room_service: 'Sobna postrežba',
        nearby:       'Partnerji',
        invitations:  'Vabila',
        cast:         'Aplikacije',
        milo:         'Milo',
        requests:     'Vaše zahteve',
        room_guide:   'Vaša soba',
        feedback:     'Pohvale, pritožbe in predlogi',
    },
    display_theme: {
        default: 'Privzeto (temno)',
        dark:    'Temno',
        light:   'Svetlo',
        auto:    'Samodejno (po uri)',
    },
    typeface: {
        default:            'Privzeto',
        system:             'Sistemska',
        trirong_montserrat: 'Trirong + Montserrat',
    },
    radius: {
        default: 'Privzeto',
        rounded: 'Zaobljeni',
        square:  'Ravni',
    },
    invitation_kind: {
        dinner:   'Večerja',
        event:    'Dogodek',
        tasting:  'Degustacija',
        wellness: 'Wellness',
        offer:    'Ponudba',
        other:    'Drugo',
    },
    place_kind: {
        sight:      'Znamenitost',
        restaurant: 'Restavracija',
        activity:   'Aktivnost',
        shop:       'Trgovina',
        other:      'Drugo',
    },
    music_source: {
        stream: 'Tok (radio)',
        file:   'Datoteka',
    },
    music_audience: {
        guest:      'Sobe',
        house:      'Javni prostori',
        guest_help: 'Postaje, ki jih gost izbere na sobnem zaslonu.',
        house_help: 'Za javne zaslone — potrebna je licenca za javno predvajanje.',
    },
    slide_kind: {
        welcome:        'Dobrodošlica',
        program:        'Program dneva',
        weather:        'Vreme',
        weather_hourly: 'Vreme po urah',
        services:       'Storitve',
        local:          'Okolica',
        hours:          'Delovni čas',
        poster:         'Plakat',
        video:          'Video',
    },
    program_source: {
        tenant:         'Ročno v konzoli',
        tenant_help:    'Program dneva vnašate na zavihku Program.',
        connector:      'Iz PMS',
        connector_help: 'Program prihaja iz PMS (potrebna je povezava z zmožnostjo Program).',
    },
    order: {
        up:   'Premakni gor',
        down: 'Premakni dol',
    },
    asset: {
        upload:            'Naloži',
        choose:            'Izberi',
        remove:            'Odstrani',
        choose_title:      'Izberite datoteko',
        count:             '{n} datoteka | {n} datoteki | {n} datoteke | {n} datotek',
        empty:             'Ni še naloženih datotek.',
        confirm_delete:    'Izbrišem datoteko »{name}«?',
        kind_image:        'Slika',
        kind_video:        'Video',
        too_large:         'Datoteka je prevelika.',
        too_large_named:   '{name}: {kind} je prevelika (največ {limit}).',
        unsupported:       'Te vrste datoteke ni mogoče naložiti.',
        unsupported_named: '{name}: te vrste datoteke ni mogoče naložiti (JPEG, PNG, WebP, SVG; MP4, WebM).',
        webm_pending:      'WebM: pripravljam…',
        webm_ready:        'WebM: pripravljeno ({size})',
        webm_failed:       'WebM: napaka',
        webm_failed_help:  'Pretvorba ni uspela: {error}',
        webm_help:         'Za Android škatle (ohm) se video samodejno pretvori v WebM; Samsung in LG predvajata izvirnik.',
        convert_again:     'Pretvori znova',
        convert_started:   'Pretvorba v WebM se je začela.',
    },
    overview: {
        title:                     'Pregled',
        subtitle:                  'Stanje naprav, sob in TV vodiča v hotelu.',
        online:                    'Na zvezi',
        offline:                   'Brez zveze',
        of_total:                  'od {total} naprav',
        unpaired_hint:             'neseznanjenih: {n}',
        inactive_hint:             'neaktivnih naprav: {n}',
        rooms_without_device:      'Sobe brez naprave',
        rooms_without_device_help: 'Sobe, v katerih ni aktivne naprave — tam TV ne kaže ničesar.',
        of_rooms:                  'od {total} sob',
        without_epg:               'Kanali brez EPG',
        of_channels:               'od {total} kanalov',
        epg_updated:               'EPG osvežen',
        providers:                 '{n} ponudnik | {n} ponudnika | {n} ponudniki | {n} ponudnikov',
        outdated_apps:             'Zastarele aplikacije',
        all_rooms:                 'Vse sobe',
        no_rooms:                  'Ni še nobene sobe.',
        every_room_has_device:     'Vsaka soba ima aktivno napravo.',
        no_device:                 'Brez naprave',
        no_active_device:          'Nobena aktivna',
        epg:                       'TV vodič (EPG)',
        no_providers:              'Ni še nobenega ponudnika EPG.',
        updated_ago:               'osveženo {ago}',
        never_updated:             'še ni bilo osveženo',
        connector:                 'Povezava s PMS',
        connector_ok:              'zadnji uspešen klic {ago}',
        connector_open:            'Odpri povezavo',
        no_signal:                 'Kanali brez signala',
        no_signal_help:            'Njihov vir se ne oglasi — gost jih ne more gledati.',
        every_channel_has_signal:  'Vsi vklopljeni kanali imajo signal.',
        all_channels:              'Vsi kanali',
    },
    pending: {
        title:             'Čakajo na potrditev ({n})',
        badge:             'Čaka na potrditev: {n}',
        badge_open:        'Odpri naprave',
        code_on_screen:    'koda na zaslonu',
        ip:                'Naslov IP, s katerega se je oglasil zaslon',
        unknown_device:    'Neznana naprava',
        no_tenant:         'Brez prostora',
        same_network:      'Isto omrežje',
        same_network_help: 'Zaslon je na istem omrežju, s katerega dela skrbnik tega prostora.',
        suggested:         'Verjetno: {name}',
        suggested_short:   'predlagana',
        confirm:           'Potrdi',
        dismiss:           'Zavrni',
        dismissed:         'Zavrnjeno — zaslon bo pokazal novo kodo.',
        approve_title:     'Potrdi zaslon',
        approve_help:      'Zaslon s kodo {code} bo seznanjen s tem hotelom.',
        approve_failed:    'Potrditev ni uspela.',
        approved:          'Zaslon je seznanjen.',
        target:            'Naprava',
        new_device:        'Nova naprava',
        gone:              'Tega zaslona ni več — začel je znova z novo kodo.',
    },
    room: {
        rooms:               'Sobe',
        subtitle:            'Sobe hotela z napravami v njih in povezavo na sobo v PMS.',
        add:                 'Nova soba',
        edit:                'Uredi sobo',
        delete:              'Izbriši sobo',
        name:                'Ime',
        name_help:           'Vsaj v enem jeziku; zaslon pokaže ime v jeziku gosta.',
        name_required:       'Vpišite ime vsaj v enem jeziku.',
        location:            'Lokacija',
        location_help:       'Prosto besedilo, npr. »Gozd« ali »2. nadstropje«.',
        external_id:         'ID v PMS',
        external_id_help:    'Soba v PMS — po tem povezava najde gosta. Prazno = brez.',
        order:               'Vrstni red',
        order_help:          'Manjše število je prej.',
        devices:             'Naprave',
        active_device:       'Aktivna naprava',
        inactive_device:     'Neaktivna naprava',
        primary_device:      'Glavni TV',
        search:              'Išči po imenu, lokaciji ali ID …',
        only_without_device: 'Samo brez aktivne naprave',
        empty:               'Ni še nobene sobe.',
        empty_help:          'Dodajte sobe ročno ali jih uvozite iz starega sistema.',
        confirm_delete:      'Izbrišem sobo {name}?',
        wifi_password:       'Geslo Wi-Fi te sobe',
        wifi_password_help:  'Če hotel uporablja eno omrežje z geslom na sobo (VLAN po sobah). Prazno = geslo hotela.',
        wifi_password_placeholder: 'Geslo hotela',
        wifi_column:         'Geslo Wi-Fi',
        wifi_hotel:          'Geslo hotela',
        wifi_show:           'Pokaži geslo',
        wifi_hide:           'Skrij geslo',
    },
    room_type: {
        title:          'Tipi sob',
        subtitle:       'Vsebino »Vaša soba« napišete za tip sobe in jo po potrebi dopolnite za posamezno sobo.',
        add:            'Nov tip',
        edit:           'Uredi tip sobe',
        delete:         'Izbriši tip sobe',
        name:           'Ime',
        name_help:      'Npr. »Dvoposteljna soba« ali »Apartma«; vsaj v enem jeziku.',
        empty:          'Ni še tipov sob. Brez njih »Vaša soba« velja za vse sobe hkrati ali za vsako sobo posebej.',
        rooms_n:        '{n} soba | {n} sobi | {n} sobe | {n} sob',
        field:          'Tip sobe',
        field_help:     '»Vaša soba« vzame vsebino tega tipa.',
        none:           'Brez tipa',
        in_use:         'Tip ima {n} sobo — najprej ji določite drug tip. | Tip ima {n} sobi — najprej jima določite drug tip. | Tip ima {n} sobe — najprej jim določite drug tip. | Tip ima {n} sob — najprej jim določite drug tip.',
        in_use_now:     'Tip ima medtem sobe — najprej jim določite drug tip.',
        confirm_delete: 'Izbrišem tip sobe {name}? Izbriše se tudi njegova vsebina »Vaša soba«.',
    },
    room_wifi: {
        placeholder: 'Soba;Geslo\nSoba 101;Geslo-101\nSoba 102;Geslo-102',
        import:      'Uvozi gesla Wi-Fi',
        title:       'Uvoz gesel Wi-Fi po sobah',
        description: 'Ena vrstica za sobo: v prvem stolpcu ime sobe ali ID v PMS, v drugem geslo. Prazno geslo vrne geslo hotela.',
        drop:        'Datoteka CSV',
        drop_help:   'Ločilo ; ali , — ali pa tabelo prilepite spodaj iz Excela.',
        text:        'Vrstice',
        text_help:   'Prva vrstica je lahko glava (Soba;Geslo). Odvečni stolpci se ne upoštevajo.',
        check:       'Preveri',
        apply:       'Uvozi ({n})',
        rows:        '{n} vrstica | {n} vrstici | {n} vrstice | {n} vrstic',
        to_write:    '{n} sprememba | {n} spremembi | {n} spremembe | {n} sprememb',
        unknown_n:   '{n} neznana | {n} neznani | {n} neznane | {n} neznanih',
        invalid_n:   '{n} neveljavna | {n} neveljavni | {n} neveljavne | {n} neveljavnih',
        reveal:      'Pokaži gesla',
        line:        'vrstica {n}',
        line_column: 'Vrstica',
        change:      'Sprememba',
        change_set:   'Novo geslo',
        change_clear: 'Geslo hotela',
        change_same:  'Brez spremembe',
        skipped:     'Te vrstice se ne uvozijo',
        unknown:     'Ni take sobe',
        problem_duplicate: 'Soba je že v eni od prejšnjih vrstic',
        problem_ambiguous: 'To ime ima več sob — uporabite ID v PMS',
        problem_too_long:  'Geslo je daljše od 120 znakov',
        problem_columns:   'Manjka stolpec z geslom',
        dry_note:    'To je le preverjanje — nič še ni zapisano. Za zapis kliknite »Uvozi«.',
        unreadable:  'Vrstic ni bilo mogoče prebrati (prazno ali več kot 2000 vrstic).',
        done:        'Gesla Wi-Fi so uvožena ({n}).',
    },
    monitoring: {
        group_when:              'Kdaj opozoriti',
        group_mail:              'Pošiljanje',
        status: {
            online:   'Na zvezi',
            stale:    'Ne odziva se',
            offline:  'Brez zveze',
            unpaired: 'Ni seznanjen',
        },
        signal: {
            excellent: 'Odličen signal',
            good:      'Dober signal',
            fair:      'Šibek signal',
            weak:      'Slab signal',
            unknown:   'Wi-Fi',
        },
        network: {
            wifi:     'Wi-Fi',
            ethernet: 'Kabel',
            other:    'Drugo',
            none:     'Brez omrežja',
            unknown:  'Neznano',
        },
        update_mode: {
            device_owner: 'tiho (Device Owner)',
            system:       'tiho (sistemsko)',
            technician:   'tehnik',
        },
        screens: {
            home:         'Domači zaslon',
            letter:       'Pismo dobrodošlice',
            program:      'Program',
            services:     'Storitve',
            local:        'Okolica',
            house:        'Vaše bivanje',
            bill:         'Račun',
            booking:      'Rezervacije',
            music:        'Glasba',
            alarm:        'Budilka',
            tv:           'TV',
            room_service: 'Hrana v sobo',
            nearby:       'Partnerji',
            invitations:  'Vabila',
            cast:         'Aplikacije',
            milo:         'Milo',
            requests:     'Vaše zahteve',
            room_guide:   'Vaša soba',
            feedback:     'Pohvale, pritožbe in predlogi',
        },
        alert_kind: {
            offline:        'Brez zveze',
            playback_error: 'Napaka predvajanja',
        },
        ops: {
            reload:         'Osveži zaslon',
            reboot:         'Ponovni zagon',
            update_now:     'Posodobi zdaj',
            reset_guest:    'Ponastavi za novega gosta',
            set_timezone:   'Časovni pas',
            set_clock:      'Oblika ure',
            open_tech_menu: 'Tehnični meni',
            collect_logs:   'Zberi dnevnik',
            capture_screen: 'Zajemi zaslon',
            remote_key: 'Pritisk daljinca',
            wake_tv:        'Prižgi TV (prijava gosta)',
            tv_play:        'Gost: kanal',
            tv_stop:        'Gost: ustavi TV',
            music_play:     'Gost: radio',
            music_stop:     'Gost: ustavi radio',
            volume_set:     'Gost: glasnost',
            alarm_set:      'Gost: budilka',
            alarm_cancel:   'Gost: prekliči budilko',
        },
        command_state: {
            queued:    'Čaka',
            delivered: 'Poslano',
            done:      'Opravljeno',
            failed:    'Ni uspelo',
            expired:   'Poteklo',
        },
        confirm: {
            reload:         'Zaslon »{name}« se bo na novo naložil. Kar gost gleda, se za trenutek ustavi.',
            reboot:         'Naprava »{name}« se bo ponovno zagnala (približno minuto brez slike).',
            update_now:     'Naprava »{name}« bo takoj preverila in tiho namestila novejšo različico aplikacije, če obstaja.',
            reset_guest:    'Naprava »{name}« bo ponastavljena za novega gosta (odjava iz YouTuba, Netflixa, Disney+, HBO Max in Prime Video, jezik, budilka, nova povezava za telefon).',
            set_timezone:   'Časovni pas naprave »{name}« bo nastavljen na {tz}. Zaslon ob naslednjem zagonu spet nastavi pas hotela.',
            set_clock:      'Zaslon »{name}« bo kazal čas v {clock}-urni obliki.',
            open_tech_menu: 'Na zaslonu »{name}« se odpre tehnični meni (z vnosom PIN). To je za tehnika ob televizorju.',
            collect_logs:   'Zaslon »{name}« bo zbral svoj dnevnik in ga poslal; prenesete ga spodaj.',
            update_webview: 'Naprava »{name}« bo takoj prenesla in tiho namestila novejši WebView, če je na voljo (najprej novejšo aplikacijo, če obstaja). Zaslon se ob namestitvi za trenutek ugasne in zažene znova.',
        },
        update_webview:      'Posodobi WebView zdaj',
        blocked_role:        'Za to nimate dovoljenja.',
        blocked_unpaired:    'Naprava ni seznanjena.',
        blocked_cap:         'Naprava tega ne podpira (manjka »{cap}«).',
        free_of:             '{free} od {total} GB prosto',
        outdated:            'Na voljo {version}',
        update_available:    '→ {version}',
        outdated_line:       'Aplikacija {version}, na voljo {latest}',
        no_telemetry:        'Zaslon še ni poslal podatkov o napravi (starejša različica ali brskalnik).',
        timeline:            'Časovnica',
        range_24h:           '24 h',
        range_7d:            '7 dni',
        no_errors:           'V tem obdobju ni bilo napak predvajanja.',
        commands:            'Ukazi',
        apply:               'Nastavi',
        send:                'Pošlji',
        sent:                'Poslano: {op}.',
        recent_commands:     'Zadnji ukazi',
        no_commands:         'V zadnjih 7 dneh ni bilo ukazov.',
        guest_phone:         'telefon gosta',
        system:              'Sistem',
        download_logs:       'Prenesi dnevnik',
        download:            'Prenesi',
        log_bundles:         'Dnevniki naprave',
        no_bundles:          'Ni zbranih dnevnikov (hranijo se 7 dni).',
        bundle_line:         '{entries} vnosov zaslona · {source} · {size}',
        log_source: {
            logcat_all: 'sistemski dnevnik',
            logcat_own: 'dnevnik aplikacije',
            none:       'brez sistemskega dnevnika',
        },
        seen_ago:            'nazadnje {ago}',
        screen_app:          'Zaslon',
        shell:               'Aplikacija',
        showing:             'Prikazuje',
        network_label:       'Omrežje',
        addresses:           'Naslova IP',
        uptime:              'Deluje',
        app_uptime:          'aplikacija {up}',
        ram:                 'Pomnilnik',
        storage:             'Shramba',
        webview:             'WebView',
        time:                'Čas',
        alarm:               'Budilka',
        alarm_on:            'nastavljena',
        alarm_off:           'ni nastavljena',
        last_reset:          'Zadnja ponastavitev',
        reported:            'Podatki z dne',
        details:             'Podrobnosti',
        errors_badge:        'Napake',
        signal_column:       'Signal',
        filter_errors:       'Z napakami (zadnja ura)',
        filter_outdated:     'Zastarela aplikacija',
        card_title:          'Nadzor zaslonov',
        card_subtitle:       'Vseh naprav: {total}',
        all_devices:         'Vse naprave',
        all_well:            'Vsi zasloni delujejo.',
        alerts_title:        'Opozorila',
        alerts_subtitle:     'E-pošta, ko zaslon izgubi zvezo ali mu predvajanje ne uspe.',
        mail_not_configured: 'Strežnik nima nastavljene pošte (SMTP): opozorila se le beležijo in prikazujejo tukaj.',
        alerts_enabled:      'Opozorila',
        alerts_enabled_help: 'Ko so izklopljena, se odprta opozorila zaprejo.',
        offline_minutes:     'Brez zveze po (min)',
        offline_minutes_help: 'Toliko minut brez stika in naprava velja za »brez zveze«.',
        playback_errors:     'Napake predvajanja',
        playback_errors_help: 'Opozori, ko zaslonu ne uspe predvajati kanala ali radia.',
        stream_digest:       'Jutranje poročilo o kanalih in postajah',
        stream_digest_help:  'Po nočnem preverjanju e-pošta s kanali in postajami, ki ne delujejo, in tistimi z največ napakami na zaslonih.',
        digest_minutes:      'Največ ena e-pošta na (min)',
        digest_minutes_help: 'Opozorila se zberejo v eno sporočilo.',
        recipients:          'Prejemniki',
        recipients_help:     'Do 10 e-poštnih naslovov; ločite jih z Enter, vejico ali presledkom.',
        recipients_rule:     'Vsak prejemnik mora biti e-poštni naslov (največ 10).',
        recipients_placeholder: "tehnika{'@'}hotel.si",
        admin_only:          'Nastavitve opozoril ureja skrbnik.',
    },
    device: {
        tab_list:                'Seznam',
        tab_streams:             'Predvajanje',
        tab_settings:            'Nastavitve',
        tab_provisioning:        'Namestitev',
        devices:        'Naprave',
        subtitle:       'Sobni televizorji in javni zasloni: stanje, signal, kaj prikazujejo, aplikacija in ukazi na daljavo.',
        add:            'Dodaj napravo',
        add_help:       'Naprava brez seznanitve (npr. STB, ki še ni nameščen) — zaslon se pozneje seznani vanjo.',
        edit:           'Uredi napravo',
        delete:         'Izbriši napravo',
        name:           'Ime',
        usage:          'Uporaba',
        room:           'Soba',
        room_required:  'Izberite sobo.',
        no_room:        'Brez sobe',
        venue:          'Dvorana v PMS',
        venue_help:     'Za zaslon pred dvorano: ID dvorane v PMS. Prazno = navaden javni zaslon.',
        orientation:    'Usmerjenost',
        playlist:       'Signage zanka',
        no_playlist:    'Brez zanke',
        mac:            'MAC',
        mac_rule:       'MAC je šest parov šestnajstiških števk, npr. 1C:A0:EF:D8:3D:8B.',
        mac_copy:       'Kopiraj MAC',
        mac_copied:     'MAC kopiran.',
        mac_copy_failed: 'MAC-a ni bilo mogoče kopirati – prepišite ga.',
        mac_missing:    'MAC ni sporočen',
        leviathan_open: 'Odpri v Leviathanu',
        leviathan_copied: 'MAC kopiran – prilepite ga v iskalnik MAC v Leviathanu',
        serial:         'Serijska številka',
        reported_help:  'Ko se naprava oglasi, ju prepiše sama.',
        cast_name:      'Ime TV-ja na telefonu',
        cast_name_help: 'Ime, ki ga telefon pokaže pri AirPlay, Smart View in Cast (na Samsungu: Nastavitve › Splošno › Upravitelj sistema › Ime naprave). Gost ga izbere na telefonu. Škatla Android se imenuje sama, po nastavitvi »Ime naprave na TV« (privzeto »Imprion TV«).',
        notes:          'Opombe',
        active:         'Aktivna',
        active_short:   'Aktivna',
        active_help:    'Soba ima lahko več televizorjev (apartma); vsi so aktivni, eden je glavni.',
        name_place_help: 'Pri sobi z več televizorji kratko ime mesta, npr. »Spalnica« ali »Dnevna soba« — po njem ga gost izbere na telefonu.',
        primary:         'Glavni TV v sobi',
        primary_short:   'glavni',
        make_primary:    'Nastavi kot glavni',
        primary_set:     '{name} je zdaj glavni TV v sobi.',
        primary_help:    'Gostov telefon in Milo uporabljata glavni TV, kadar gost ne izbere drugega; ob prijavi se prižge glavni TV (ali vsi, po nastavitvi HDMI-CEC).',
        another_tv:      'Soba {room} že ima TV ({names}). Ta bo še en TV v sobi, drugi ostanejo vklopljeni — vpišite kratko ime mesta, npr. »Spalnica«.',
        place_example:   'npr. Spalnica',
        status:         'Stanje',
        software:       'Naprava in aplikacija',
        platform:       'Platforma',
        model:          'Model',
        app_version:    'Aplikacija',
        system_version: 'Ovoj',
        lan_ip:         'LAN IP',
        public_ip:      'Javni IP',
        last_seen:      'Zadnjič na zvezi',
        never_seen:     'Še nikoli',
        paired_at:      'Seznanjena',
        pairing_code:   'Koda seznanitve',
        capabilities:   'Zmožnosti',
        volume_path:    'Glasnost s telefona',
        volume_path_key:    'Na televizor (HDMI-CEC)',
        volume_path_stream: 'Glasnost naprave',
        search:         'Išči po imenu, sobi, MAC …',
        all_usages:     'Vse uporabe',
        all_statuses:   'Vsa stanja',
        inactive_only:  'Samo neaktivne',
        empty:          'Ni še nobene naprave.',
        how_title:      'Kako dodam televizor',
        how_help:       'Na televizorju zaženite aplikacijo Imprion TV ali odprite {address} — pokaže kodo in se pojavi tukaj med napravami, ki čakajo na potrditev.',
        activated:      '{name} je aktivna.',
        deactivated:    '{name} ni več aktivna.',
        unpair:         'Odjavi',
        unpaired:       'Naprava je odjavljena — zaslon bo pokazal novo kodo.',
        confirm_unpair: 'Odjavim napravo {name}? Zaslon izgubi ključ in mora biti znova potrjen.',
        confirm_delete: 'Izbrišem napravo {name}? Zaslon izgubi ključ.',
        logs:           'Dnevnik',
        logs_title:     'Dnevnik: {name}',
        logs_help:      'Zadnja sporočila zaslona (hranjena 7 dni).',
        logs_empty:     'Ni sporočil.',
    },
    hdmi_cec: {
        title:                   'HDMI-CEC',
        subtitle:                'Kako škatla upravlja televizor prek kabla HDMI: vhod in vklop.',
        group_input:             'Vhod in ime',
        group_check_in:          'Ob prijavi gosta',
        help:                    'Škatla sama vrne televizor na naš vhod ob vsaki tipki našega daljinca in po ponastavitvi za novega gosta. Ugasnjenega televizorja ne prižge nikoli — razen ob prijavi gosta, če to vklopite spodaj. Deluje le na napravah s HDMI-CEC (sistemska različica aplikacije).',
        input_return:            'Vedno vrni na naš vhod',
        input_return_help:       'Ko gost preklopi na drug vir (npr. HDMI 2), ga škatla po tem času vrne na naš vhod. »Takoj« je za javne zaslone in avlo.',
        wake_on_check_in:        'Prižgi TV ob prijavi gosta',
        wake_on_check_in_help:   'Televizor se ob prijavi prižge na pozdravu z gostovim imenom.',
        idle_standby:            'Ugasni, če ga nihče ne uporabi v',
        idle_standby_help:       'Po prijavi: če nihče ne pritisne tipke, se televizor spet ugasne.',
        device_name:             'Ime naprave na TV',
        device_name_help:        'Ime, s katerim se škatla predstavi televizorju (seznam vhodov) in telefonom (Cast, YouTube). Največ 14 znakov brez šumnikov; starejši televizorji ga ne prikažejo.',
        wake_scope:              'Ob prijavi gosta prižgi',
        wake_scope_help:         'Za sobe z več televizorji (apartmaji). Soba z enim televizorjem ga prižge vedno.',
        wake_scope_values: {
            primary: 'Glavni TV',
            all:     'Vse TV-je v sobi',
        },
        admin_only:              'Nastavitve HDMI-CEC ureja skrbnik.',
        never:                   'Nikoli',
        minutes:                 '{minutes} min',
        input_return_values: {
            off:       'Izklopljeno',
            after_5:   'Po 5 min',
            after_10:  'Po 10 min',
            after_30:  'Po 30 min',
            after_60:  'Po 60 min',
            immediate: 'Takoj',
        },
        wake_values: {
            off:    'Izklopljeno',
            manual: 'Samo ob ročni prijavi',
            all:    'Tudi ob samodejni prijavi',
        },
        device_name_values: {
            imprion: 'Imprion TV',
            room:    'Ime sobe',
        },
        osd_name:                'Ime na TV: {value}',
        device_override:         'Vrni na naš vhod',
        device_override_help:    'Za to napravo namesto nastavitve hiše (npr. »Takoj« za zaslon v avli).',
        as_house:                'Kot hiša',
        drawer_title:            'HDMI-CEC',
        tv_input_label:          'TV vhod: {value}',
        tv_input_unknown:        'neznano',
        through_device:          ' (prek naprave)',
        tv_vendor:               'proizvajalec {value}',
        tv_name:                 'ime {value}',
        address:                 'naslov {value}',
        supported:               'podprto',
        unsupported:             'ni podprto',
        no_report:               'podprto, še brez poročila',
        disabled_android:        'izklopljen v Androidu',
        active_source:           'aktiven vir: {value}',
        active_unknown:          'ni znano',
        yes:                     'da',
        no:                      'ne',
        tv:                      'TV: {value}',
        returns:                 'vrne: {value}',
        last:                    'zadnji preklop {at} ({reason} → {result})',
        action: {
            take:    'Preklop na naš vhod',
            standby: 'Ugasnitev TV',
        },
        reason: {
            key:             'tipka daljinca',
            reset:           'ponastavitev za gosta',
            policy:          'vedno vrni',
            check_in:        'prijava gosta',
            idle:            'nihče ga ni uporabil',
            tv_off:          'TV ugasnjen',
            tv_on:           'TV prižgan',
            tv_other_source: 'TV preklopil na drug vir',
            tv_our_input:    'TV se je vrnil na STB',
        },
        result: {
            taken:      'preklopljeno',
            already:    'že na našem vhodu',
            tv_standby: 'TV ugasnjen — ne prižgem',
            tv_unknown: 'TV se ne odziva',
            sent:       'poslano',
            not_active: 'drug vir — ne ugasnem',
            limited:    'omejitev na uro',
            failed:     'napaka',
        },
        tv_power: {
            on:      'prižgan',
            standby: 'v pripravljenosti',
            unknown: 'ne odziva se',
        },
    },
    device_events: {
        title:       'Dogodki naprave',
        filter:      'Vrsta dogodka',
        all_kinds:   'Vse vrste',
        days:        '{days} dan | {days} dneva | {days} dnevi | {days} dni',
        empty:       'V tem obdobju ni dogodkov.',
        error:       'Dogodkov ni bilo mogoče naložiti.',
        retry:       'Poskusi znova',
        capped:      'Prikazanih je zadnjih {count} dogodkov.',
        offline_for: 'brez zveze {minutes} min',
        kind: {
            boot:         'Zagon naprave',
            app_start:    'Zagon aplikacije',
            page_load:    'Nalaganje zaslona',
            cec:          'HDMI-CEC',
            connectivity: 'Povezava',
            tms:          'TMS',
        },
        reason: {
            boot: {
                power_off:     'Izklop z daljincem ali gumbom',
                power_loss:    'Izpad elektrike (hladni zagon)',
                app_reboot:    'Ponovni zagon iz konzole',
                adb:           'Ponovni zagon prek ADB',
                system_update: 'Posodobitev sistema',
                crash:         'Zrušitev sistema (watchdog, kernel panic)',
                reboot:        'Ponovni zagon',
                unknown:       'Neznano',
            },
            app_start: {
                update:  'Posodobitev aplikacije',
                boot:    'Ob zagonu naprave',
                crash:   'Ponovni zagon po zrušitvi',
                home:    'Tipka Domov',
                manual:  'Ročni zagon (tehnik)',
                unknown: 'Neznano',
            },
            page_load: {
                first_load:     'Prvo nalaganje',
                deploy:         'Nova različica zaslona',
                console_reload: 'Osveži zaslon iz konzole',
                error_recovery: 'Po napaki',
                network:        'Omrežje spet dela',
                technician:     'Tehnik je osvežil',
                renderer:       'Po zrušitvi brskalnika',
                app_start:      'Ob zagonu aplikacije',
                reload:         'Osvežitev',
            },
            connectivity: {
                offline: 'Brez zveze',
                online:  'Spet na zvezi',
            },
            tms: {
                mac_auto:    'MAC iz TMS (samodejno)',
                mac_console: 'MAC iz TMS (izbran v konzoli)',
            },
        },
    },
    guest_reset: {
        title:                  'Ponastavitev za novega gosta',
        subtitle:               'Televizor pozabi prejšnjega gosta: odjava iz YouTuba, Netflixa, Disney+, HBO Max in Prime Video, budilka, jezik in glasnost.',
        help_manual:            'Brez PMS: na recepciji v seznamu spodaj označite »Odhod danes« — ob uri odjave se televizorji teh sob ponastavijo sami. Kadarkoli lahko sobo ali napravo ponastavite tudi z gumbom.',
        help_connector:         'S povezavo na PMS se zasloni ponastavijo sami ob odjavi in ob prihodu novega gosta. Gumb in samodejna ponastavitev ob uri odjave sta za izjeme (npr. selitev v drugo sobo).',
        auto:                   'Samodejno ob uri odjave',
        auto_help:              'Sobe z oznako »Odhod danes« se ponastavijo ob izbrani uri.',
        at:                     'Ura odjave',
        at_help:                'Po krajevnem času hotela. Ob tej uri na dan odhoda gostu poteče tudi povezava na telefonu (QR koda s televizorja) — vedno, tudi brez samodejne ponastavitve. Soba, označena po tej uri, se ponastavi takoj.',
        expire_unbound:         'Povezave brez bivanja potečejo vsak dan',
        expire_unbound_help:    'Za hiše brez PMS in brez bivanj v Zasedenosti: povezava na telefonu poteče ob uri odjave tudi, ko soba nima bivanja. Gost, ki ostane več noči, QR kodo na televizorju vsak dan znova skenira.',
        at_rule:                'Vpišite uro v obliki HH:MM.',
        volume:                 'Glasnost po ponastavitvi',
        volume_help:            'Odstotek glasnosti, ki ga televizor nastavi (kjer naprava to omogoča).',
        volume_set:             'Nastavi',
        volume_keep:            'Ne spreminjaj',
        max_volume:             'Največja glasnost',
        max_volume_help:        'Gost ne more naviti televizorja glasneje od tega odstotka — ne s telefonom ne z daljincem v sobi (kjer naprava to omogoča).',
        max_volume_set:         'Omeji',
        max_volume_none:        'Brez omejitve',
        volume_over_max:        'Glasnost po ponastavitvi ne sme biti večja od največje glasnosti.',
        admin_only:             'Nastavitve lahko spremeni skrbnik.',
        column:                 'Ponastavitev',
        departure:              'Odhod',
        departs_today:          'Odhod danes',
        action:                 'Ponastavi za novega gosta',
        cancel:                 'Prekliči ponastavitev',
        pending:                'Čaka na zaslon',
        pending_since:          'Zahtevano {at}',
        never:                  'Še nikoli',
        last:                   'Ponastavljeno {ago}',
        youtube_cleared:        'YouTube počiščen',
        netflix_cleared:        'Netflix počiščen',
        disney_plus_cleared:    'Disney+ počiščen',
        hbo_max_cleared:        'HBO Max počiščen',
        prime_video_cleared:    'Prime Video počiščen',
        reason: {
            console: 'v konzoli',
            auto:    'ob odjavi',
            stay:    'ob menjavi gosta',
        },
        confirm_title:          'Ponastavim {name} za novega gosta?',
        confirm_device_youtube: 'Televizor odjavi Google račun iz YouTuba (zgodovina in povezani telefoni se izbrišejo) ter račune iz Netflixa, Disney+, HBO Max in Prime Video, pozabi pare za zrcaljenje zaslona, izbriše budilko, ustavi predvajanje in vrne jezik in glasnost na hišne nastavitve.',
        confirm_device_screen:  'Ta naprava ne zmore počistiti aplikacij, zato se ponastavi le sobni zaslon: budilka, predvajanje, jezik in glasnost. YouTuba, Netflixa, Disney+, HBO Max in Prime Video ne odjavi.',
        confirm_room:           'Vsi televizorji v sobi se ponastavijo: odjava iz YouTuba, Netflixa, Disney+, HBO Max in Prime Video (kjer naprava to zmore), budilka, predvajanje, jezik in glasnost.',
        confirm_when:           'Prižgan televizor to naredi v eni minuti, ugasnjen pa, ko ga prižgete.',
        requested:              'Ponastavitev je zahtevana — zaslon jo izvede v eni minuti.',
        cancelled:              'Ponastavitev je preklicana.',
    },
    provisioning: {
        other_tenant: 'Namestitev še teče v drugi hiši. Seznam in poročilo spodaj sta njena, dokler se ne konča.',
        agent_error: {
            unreachable:   'Agent na prenosniku ne odgovarja.',
            timeout:       'Agent na prenosniku ni odgovoril pravočasno.',
            session:       'Agent te povezave ne pozna več (je bil ponovno zagnan?). Vpišite novo kodo.',
            wrong_code:    'Koda ni prava. Vpišite šest števk, ki jih je izpisal agent.',
            locked:        'Preveč napačnih kod. Ponovno zaženite agenta in vpišite novo kodo.',
            failed:        'Agent je zavrnil ukaz.',
            failed_reason: 'Agent: {reason}',
            wrong_code_left: 'Koda ni prava. Poskusov še: {n}.',
            origin:        'Agent ne sprejema te strani. Zaženite ga za naslov te konzole (--origin).',
            no_adb:        'Agent ne najde adb.',
            adb_failed:    'adb ni uspel ({step}): {output}',
            not_connected: 'Sprejemnik v adb ni povezan ({state}).',
            no_apk:        'Konzola nima aplikacije te različice — naložite APK pri ponudniku.',
            download:      'Agent aplikacije ni mogel prenesti s konzole.',
            mismatch:      'Prenesena aplikacija se ne ujema z izdajo (velikost ali sha256).',
        },
        agent: {
            title:           '1. Agent na prenosniku',
            subtitle:        'Program tv-provision-agent na prenosniku v omrežju hotela doseže sprejemnike prek ADB.',
            connected:       'Povezan',
            check:           'Preveri znova',
            not_running:     'Agent ne teče ali ga brskalnik ne doseže.',
            timeout:         'Agent ni odgovoril pravočasno.',
            watching:        'Stran ga vsakih nekaj sekund poišče znova — ko ga zaženete, se poveže sama.',
            help_start:      'Na prenosniku zaženite tv-provision-agent in ga pustite odprtega; izpiše šestmestno kodo.',
            help_permission: 'Chrome lahko vpraša, ali stran sme dostopati do naprav v lokalnem omrežju — izberite Dovoli.',
            help_denied:     'Če ste dostop zavrnili, ga dovolite v nastavitvah strani (ikona levo od naslova) in stran osvežite.',
            help_browser:    'Uporabite Chrome ali Edge na istem prenosniku, kjer teče agent; Safari povezave s strani https na prenosnik ne dovoli.',
            address:         'Naslov agenta: {address}',
            version:         'Verzija agenta',
            no_adb:          'adb ni najden',
            adb_key_own:     'Agent uporablja svoj ključ ADB: vsak sprejemnik ob prvi povezavi vpraša »Allow USB debugging« — potrdi ga TMS s tipkami (Nastavitve › TMS).',
            no_adb_help:     'Agent brez adb ne doseže sprejemnikov. Namestite Android platform-tools ali zaženite agenta z --adb <pot>.',
            code:            'Koda agenta',
            code_help:       'Šest števk, ki jih je agent izpisal ob zagonu. Velja za ta zavihek brskalnika.',
            code_rule:       'Koda ima šest števk.',
            pair:            'Poveži',
            paired:          'Agent je povezan.',
            paired_help:     'Agent je povezan s tem zavihkom.',
            forget:          'Prekini povezavo',
        },
        boxes: {
            title:           '2. Sprejemniki iz TMS',
            subtitle:        'Sprejemnikom, ki jih namestite, izberite sobo, ime in uporabo — v tabeli ali z uvozom CSV.',
            import:          'Uvozi CSV',
            truncated:       'TMS je vrnil več sprejemnikov, kot jih seznam prikaže; prikazani so prvi.',
            search:          'Išči MAC, IP, sobo …',
            only_selected:   'Le izbrani',
            summary:         'Izbranih {selected} · dosegljivih {reachable} od {total}',
            select_all:      'Izberi vse dosegljive',
            select:          'Izberi {mac}',
            box:             'Sprejemnik',
            device:          'Obstoječa naprava',
            no_lan_ip_short: 'brez LAN IP',
            replace:         'Zamenjaj',
            room_pick:       'Izberite sobo',
            empty:           'TMS te hiše ne pozna nobenega sprejemnika.',
            offline:         'Sprejemnik ni na zvezi.',
            no_lan_ip:       'TMS ne pozna LAN naslova sprejemnika.',
            needs_replace:   'Ta MAC ima že naprava — označite Zamenjaj.',
            needs_room:      'Izberite sobo.',
            needs_name:      'Vpišite ime (do 60 znakov).',
            load_failed:     'Seznama sprejemnikov iz TMS ni bilo mogoče naložiti.',
        },
        csv: {
            title:       'Uvoz sprejemnikov (CSV)',
            description: 'Ena vrstica na sprejemnik: mac,soba,ime. Soba po imenu v kateremkoli jeziku; ime je neobvezno.',
            drop:        'Spustite datoteko CSV sem ali jo izberite',
            drop_help:   'CSV ali besedilo, ločeno z vejico, podpičjem ali tabulatorjem.',
            text:        'Ali prilepite vrstice',
            text_help:   'Prva vrstica je lahko glava (mac,room,name). Brez imena se sprejemnik imenuje po sobi (»Soba 101 — TV«).',
            apply:       'Uvozi',
            applied:     'Uvožena {n} vrstica | Uvoženi {n} vrstici | Uvožene {n} vrstice | Uvoženih {n} vrstic',
            taken:       '{n} vrstica uvožena | {n} vrstici uvoženi | {n} vrstice uvožene | {n} vrstic uvoženih',
            skipped_n:   '{n} preskočena | {n} preskočeni | {n} preskočene | {n} preskočenih',
            skipped:     'Preskočene vrstice',
            line:        'Vrstica {n}',
            problem: {
                columns:        'premalo stolpcev',
                mac:            'ni MAC naslov',
                unknown_mac:    'TMS tega MAC ne pozna',
                duplicate:      'MAC se ponovi',
                room:           'te sobe ni',
                room_ambiguous: 'več sob s tem imenom',
            },
        },
        developer: {
            title:       '3. Developer v TMS',
            subtitle:    'ADB na sprejemniku odgovori šele, ko je v TMS vklopljen »Enable Developer«.',
            help:        'Tega stikala Open API TMS nima: v spletnem vmesniku TMS (manage.atvbridge.com) poiščite izbrane sprejemnike po MAC in jim vklopite Developer. Nato označite spodaj.',
            none:        'Najprej izberite sprejemnike v koraku 2.',
            list:        'MAC naslovi izbranih sprejemnikov',
            copy_commas: 'Kopiraj {n} MAC z vejicami | Kopiraj {n} MAC z vejicami | Kopiraj {n} MAC z vejicami | Kopiraj {n} MAC z vejicami',
            copy_lines:  'Kopiraj {n} MAC po vrsticah | Kopiraj {n} MAC po vrsticah | Kopiraj {n} MAC po vrsticah | Kopiraj {n} MAC po vrsticah',
            open_tms:    'Odpri TMS',
            done:        'Developer je vklopljen',
            done_help:   'Za vse izbrane sprejemnike v TMS.',
            copied:      'MAC naslovi so kopirani.',
            copy_failed: 'Kopiranje ni uspelo – označite seznam in ga kopirajte sami.',
        },
        run: {
            title:                '4. Namestitev',
            subtitle:             'Vsak sprejemnik po korakih: ADB, aplikacija, žeton, zagon, nastavitve, seznanitev, skupina v TMS.',
            concurrency:          'Hkrati',
            concurrency_help:     'Koliko sprejemnikov naenkrat (1–5).',
            start:                'Začni namestitev ({n})',
            stop:                 'Ustavi',
            stopping:             'Ustavljam …',
            retry:                'Ponovi',
            left_out:             '{n} izbran sprejemnik nima vsega (soba, ime, Zamenjaj) in ne bo nameščen. | {n} izbrana sprejemnika nimata vsega (soba, ime, Zamenjaj) in ne bosta nameščena. | {n} izbrani sprejemniki nimajo vsega (soba, ime, Zamenjaj) in ne bodo nameščeni. | {n} izbranih sprejemnikov nima vsega (soba, ime, Zamenjaj) in ne bodo nameščeni.',
            need_agent:           'Zaženite agenta na prenosniku (korak 1).',
            need_adb:             'Agent nima adb (korak 1).',
            need_pairing:         'Povežite agenta s kodo (korak 1).',
            need_boxes:           'Izberite sprejemnike s sobo in imenom (korak 2).',
            need_developer:       'Vklopite Developer v TMS in to označite (korak 3).',
            seconds:              '{s} s',
            minutes:              '{m} min {s} s',
            replace_report_title: 'Začnem novo namestitev?',
            replace_report:       'Poročilo zadnje namestitve na tej strani se zamenja z novim. Prenesite ga prej, če ga potrebujete.',
            group_set:            'skupina {group}',
            group_none:           'skupina v TMS ni nastavljena',
            group_line:           'Skupina v TMS: {group} · tipke za ADB: {keys}',
            group_line_none:      'Skupina v TMS ni nastavljena (korak Skupina bo preskočen) · tipke za ADB: {keys}',
        },
        run_error: {
            unreachable:  'Sprejemnik se na ADB ne odziva — je Developer v TMS vklopljen in je prenosnik v istem omrežju?',
            unauthorized: 'Sprejemnik ni dovolil ADB (»Allow USB debugging«). Preverite tipke v Nastavitve › TMS ali potrdite na televizorju.',
            allowed:      'ADB dovoljen z daljincem TMS',
            pair_timeout: 'Sprejemnik se v 90 s ni seznanil.',
            expired:      'Žeton je potekel.',
            no_token:     'Ni žetona.',
            failed:       'Korak ni uspel.',
            keys:         'TMS tipk ni pritisnil.',
            mac_taken:    'Ta MAC ima že naprava »{name}« — označite Zamenjaj.',
            token:        'Žetona za namestitev ni bilo mogoče ustvariti.',
            poll:         'Stanja seznanitve ni bilo mogoče prebrati.',
            group:        'TMS sprejemnika ni dodal v skupino.',
            group_unknown: 'TMS nima skupine »{group}« — popravite ime v Nastavitve › TMS.',
            rate_limited: 'Preveč zahtev za TMS. Počakajte nekaj minut in ponovite.',
        },
        step: {
            connect:  'ADB',
            install:  'Aplikacija',
            token:    'Žeton',
            launch:   'Zagon',
            settings: 'Nastavitve',
            paired:   'Seznanjen',
            group:    'Skupina',
        },
        step_status: {
            pending: 'čaka',
            running: 'teče',
            ok:      'uspelo',
            failed:  'ni uspelo',
            skipped: 'preskočeno',
        },
        box_status: {
            idle:    'ni zagnano',
            queued:  'v vrsti',
            running: 'teče',
            done:    'nameščen',
            failed:  'ni uspelo',
            stopped: 'ustavljeno',
        },
        report: {
            title:     '5. Poročilo',
            download:  'Prenesi CSV',
            retry_all: 'Ponovi neuspele ({n})',
        },
    },
    tms: {
        title:                'TMS (ATV Bridge)',
        subtitle:             'Sprejemniki TG MAXX (Translite Global): ob ponastavitvi za novega gosta TMS počisti aplikacije, kjer jih naša aplikacija sama ne more.',
        help:                 'Na sprejemnikih TG MAXX naša aplikacija gosta ne more odjaviti iz Netflixa, YouTuba, Disney+, HBO Max in Prime Video. To naredi opravilo »Clear App Data« v TMS ATV Bridge (manage.atvbridge.com): enkrat ga pripravite tam — seznam aplikacij, ki jim podatki ostanejo (med njimi io.imprion.tv), vse ostalo se počisti; po želji tudi odstranitev Google računa in aplikacij, ki jih je namestil gost. Tukaj vpišete njegovo ime.',
        enabled:              'Vključeno',
        enabled_help:         'Ob ponastavitvi za novega gosta (gumb, samodejno ob odjavi, menjava gosta) TMS dobi opravilo za ta sprejemnik.',
        base_url:             'Naslov Open API',
        base_url_help:        'Osnovni naslov Open API vašega TMS (https), npr. https://manage.atvbridge.com/openApi — točno pot potrdi Translite.',
        api_key:              'API ključ',
        api_key_help_set:     'Ključ je shranjen in se ne prikaže. Pustite prazno, da ostane.',
        api_key_help_new:     'Ključ Open API iz TMS.',
        clear_task:           'Ime opravila »Clear App Data«',
        clear_task_help:      'Točno ime opravila, kot ga ima TMS.',
        mode:                 'Kdaj počistiti prek TMS',
        mode_help:            'Sprejemniki z našo ponastavitvijo aplikacij (Device Owner) jih počistijo sami.',
        mode_fallback:        'Le kjer aplikacija sama ne more (priporočeno)',
        mode_always:          'Ob vsaki ponastavitvi',
        mode_never:           'Nikoli samodejno (le z gumbom na napravi)',
        test:                 'Preizkusi povezavo',
        test_mac:             'MAC naprave za preizkus (neobvezno)',
        test_ok:              'Povezava deluje — TMS {version}.',
        test_failed:          'Povezava ne deluje: {error}',
        test_terminal:        'Naprava {mac}: {state}',
        test_unsaved:         'Preizkus uporabi vpisane podatke, tudi neshranjene.',
        admin_only:           'Nastavitve lahko spremeni skrbnik.',
        terminal_unknown:     'TMS te naprave (MAC {mac}) ne pozna.',
        online:               'na zvezi',
        offline:              'ni na zvezi',
        software:             'Programska oprema',
        model:                'Model',
        group:                'Skupina',
        public_ip:            'Javni IP',
        last_contact:         'Zadnji stik',
        serial:               'Serijska številka',
        refresh:              'Osveži',
        reboot:               'Ponovni zagon (TMS)',
        clear_apps:           'Počisti aplikacije (TMS)',
        confirm_reboot_title: 'Ponovno zaženem {name} prek TMS?',
        confirm_reboot:       'TMS sprejemnik ponovno zažene. Televizor bo minuto ali dve brez slike.',
        confirm_clear_title:  'Počistim aplikacije na {name} prek TMS?',
        confirm_clear:        'TMS na sprejemniku izvede opravilo »Clear App Data«: podatki vseh aplikacij razen izbranih v TMS se izbrišejo (odjava iz Netflixa, YouTuba, Disney+, HBO Max in Prime Video). Ne naredite tega, ko je gost še v sobi.',
        rebooted:             'TMS je sprejel ponovni zagon.',
        cleared:              'TMS je sprejel čiščenje aplikacij.',
        last:                 'Zadnje prek TMS',
        manual:               'z gumbom',
        find:                 'Poišči v TMS',
        replace:              'Zamenjaj',
        no_mac:               'Naprava nima MAC naslova, zato je TMS ne prepozna. Poiščite jo med sprejemniki v TMS.',
        no_mac_reception:     'Naprava nima MAC naslova, zato je TMS ne prepozna. Skrbnik jo lahko poišče v TMS.',
        pick_title:           'Sprejemnik v TMS za {name}',
        pick_description:     'Sprejemniki iz TMS, ki jih nima še nobena naprava te hiše — najverjetnejši na vrhu.',
        pick_summary:         'Sprejemnikov v TMS: {terminals} · že dodeljenih drugim napravam: {assigned}',
        pick_truncated:       'Prikazanih je le prvih 2000 sprejemnikov iz TMS.',
        pick_empty:           'V TMS ni prostega sprejemnika — vsi so že dodeljeni drugim napravam.',
        pick_choose:          'Izberi',
        pick_lan_ip:          'LAN IP',
        pick_confirm_title:   'Dodelim MAC {mac} napravi {name}?',
        pick_confirm:         'Naprava dobi MAC sprejemnika iz TMS ({model}). Prek njega jo TMS ob ponastavitvi počisti in ponovno zažene. Zamenjate ga lahko kadarkoli.',
        pick_confirm_replace: 'Naprava ima zdaj MAC {old}; zamenja ga MAC sprejemnika iz TMS ({model}).',
        pick_use:             'Dodeli MAC',
        picked:               'MAC {mac} je shranjen.',
        pick_taken:           'Ta MAC ima že naprava »{name}«.',
        pick_unknown:         'TMS tega MAC naslova ne pozna več. Osvežite seznam.',
        group_name:           'Skupina za Namestitev',
        group_help:           'Ime skupine v TMS, v katero Namestitev doda sprejemnike. Prazno = ne doda jih v nobeno skupino.',
        adb_keys:             'Tipke za »Allow USB debugging«',
        adb_keys_help:        'Tipke, ki jih TMS ob Namestitvi pritisne na daljincu, ko sprejemnik vpraša za dovoljenje ADB — po vrsti. Privzeto desno, OK.',
        adb_key_remove:       'Odstrani {key}',
        adb_keys_empty:       'Ni tipk.',
        adb_keys_default:     'Privzeto (OK, dol, levo, OK)',
        adb_keys_required:    'Vsaj ena tipka.',
        key: {
            home:  'Domov',
            back:  'Nazaj',
            up:    'Gor',
            down:  'Dol',
            left:  'Levo',
            right: 'Desno',
            ok:    'OK',
        },
        match: {
            lan_ip:    'isti LAN IP',
            serial:    'ista serijska številka',
            public_ip: 'isti javni IP',
            online:    'na zvezi',
        },
        outcome: {
            pending: 'TMS: čaka',
            pushed:  'TMS: poslano',
            failed:  'TMS: ni uspelo',
            skipped: 'TMS: preskočeno',
        },
        skip_reason: {
            no_mac:    'naprava nima MAC naslova',
            own_reset: 'aplikacija počisti sama',
            platform:  'televizor',
            never:     'samodejno izklopljeno',
        },
    },
    tv: {
        title:          'TV',
        subtitle:       'Kanali, kategorije, TV vodič in videz seznama na sobnih zaslonih.',
        tab_channels:   'Kanali',
        tab_categories: 'Kategorije',
        tab_epg:        'EPG',
        tab_themes:     'Videz',
        tab_settings:   'Nastavitve TV',
    },
    channel: {
        channels:           'Kanali',
        channel:            'Kanal',
        subtitle:           '{n} kanalov, vklopljenih {enabled}. Povlecite ali premaknite s puščicami.',
        add:                'Nov kanal',
        edit:               'Uredi kanal',
        delete:             'Izbriši kanal',
        name:               'Ime',
        note:               'Opomba',
        source:             'Vir',
        source_help:        'Del za osnovnim naslovom tokov (običajno ID kanala) ali celoten naslov http(s)://.',
        source_placeholder: '12',
        source_mediaklikk:  'splet (MTVA)',
        mediaklikk_help:    'Madžarska javna TV s spleta: mediaklikk:<id> — M1 mtv1live, M2 mtv2live, M4 Sport mtv4live, M5 mtv5live, Duna dunalive, Duna World dunaworldlive. Vedno predvajano prek strežnika.',
        mediaklikk_pick:    'MTVA:',
        mediaklikk_invalid: 'Za mediaklikk: sledi ID kanala: 3–30 malih črk in številk (mtv1live).',
        resolved:           'Naslov: {url}',
        type:               'Vrsta toka',
        type_help:          'Samodejno je pravo za skoraj vse; HLS ali MPEG-TS le, če ga zaslon ne prepozna.',
        categories:         'Kategorije',
        no_categories:      'Brez kategorij',
        all_categories:     'Vse kategorije',
        logo:               'Logotip',
        enabled:            'Vklopljen',
        enabled_help:       'Izklopljen kanal se na zaslonih ne pokaže.',
        adult:              'Za odrasle',
        adult_help:         'Kako se pokaže, določa Nastavitve TV.',
        adult_badge:        '18+',
        epg:                'EPG',
        search:             'Išči kanal …',
        only_without_epg:   'Samo brez EPG',
        signal:             'Signal',
        check_now:          'Preveri zdaj',
        only_without_signal: 'Samo kanali brez signala',
        check_ok:           'Kanal {name} dela.',
        check_down:         'Kanal {name} ne dela: {reason}.',
        check_unknown:      'Kanala {name} ni mogoče preveriti.',
        order_filtered:     'Vrstni red lahko spreminjate, ko je filter izklopljen.',
        empty:              'Ni še nobenega kanala.',
        empty_help:         'Dodajte kanale ročno ali jih uvozite iz starega sistema.',
        confirm_delete:     'Izbrišem kanal {name}? Z njim gre tudi njegova preslikava EPG.',
        languages:          'Jeziki',
        languages_help:     'V katerem jeziku kanal oddaja — običajno en. Gost s TV-jem v tem jeziku vidi kanal med prvimi in v kategoriji tega jezika.',
        languages_max:      'Največ {n} jeziki.',
        no_languages:       'Jezik ni določen',
        all_languages:      'Vsi jeziki',
        no_language:        'Brez jezika',
    },
    stream: {
        button:           'Vir tokov',
        title:            'Vir tokov',
        help:             'En osnovni naslov in en žeton za vse kanale hotela — kanal je le ID na koncu.',
        base:             'Osnovni naslov',
        base_help:        '{\'{\'}token{\'}\'} v naslovu se zamenja z žetonom. Prazno = vsak kanal ima celoten naslov.',
        base_placeholder: 'http://avis2.ittim-online.net/stream/hotel/{\'{\'}token{\'}\'}/',
        base_rule:        'Naslov se začne s http:// ali https://.',
        no_base:          'Ni osnovnega naslova — kanali imajo celotne naslove.',
        token:            'Žeton',
        token_set:        'Žeton je shranjen. Prazno pusti obstoječega.',
        token_unset:      'Žeton ni nastavljen.',
        token_clear:      'Odstrani',
        token_keep:       'Obdrži',
        proxy:            'Predvajanje prek strežnika',
        change:           'Spremeni na zavihku Kanali',
    },
    category: {
        categories:     'Kategorije',
        subtitle:       'Skupine kanalov na zaslonu, z imeni v vseh jezikih in lastnim vrstnim redom.',
        language_note:  'Za temi kategorijami zaslon sam doda kategorije po jezikih kanalov (Deutsch, English …) — vklopite ali izklopite jih v Nastavitvah TV.',
        add:            'Nova kategorija',
        edit:           'Uredi kategorijo',
        delete:         'Izbriši kategorijo',
        name:           'Ime',
        name_help:      'Ime v vsakem jeziku hotela.',
        translated:     'Prevedeno',
        not_translated: 'Manjka prevod',
        channel_count:  '{n} kanal | {n} kanala | {n} kanali | {n} kanalov',
        empty:          'Ni še nobene kategorije.',
        empty_help:     'Kategorije združijo kanale v pasove, npr. »Slovenski«, »Novice«.',
        confirm_delete: 'Izbrišem kategorijo {name}? Odstrani se s kanalov ({n}).',
    },
    epg: {
        providers:               'Ponudniki EPG',
        providers_subtitle:      'Viri XMLTV, ki jih strežnik redno prenaša.',
        providers_empty:         'Ni še nobenega ponudnika.',
        provider:                'Ponudnik',
        provider_add:            'Nov ponudnik',
        provider_edit:           'Uredi ponudnika',
        provider_name:           'Ime',
        url:                     'Naslov XMLTV',
        url_help:                'Datoteka XMLTV prek http(s); sprejme tudi .gz.',
        language:                'Jezik vodnika',
        language_help:           'V katerem jeziku so naslovi oddaj. Sobni zaslon pokaže vodnik ponudnika v jeziku gosta.',
        update_minutes:          'Osveži vsakih (min)',
        update_minutes_help:     '15–1440 minut.',
        update_minutes_range:    'Med 15 in 1440.',
        enabled:                 'Vklopljen',
        enabled_help:            'Izklopljen ponudnik se ne osvežuje.',
        disabled:                'Izklopljen',
        test:                    'Preizkusi vir',
        test_help:               'Pred shranjevanjem prenese datoteko in preveri, da je XMLTV.',
        last_update:             'Zadnja osvežitev',
        never:                   'Še nikoli',
        every:                   'vsakih {n} min',
        channels:                'Kanali',
        counts:                  '{channels} v viru · {mappings} preslikanih',
        refresh:                 'Osveži zdaj',
        refreshed:               '{name} je osvežen.',
        refresh_failed:          'Osvežitev ni uspela.',
        confirm_delete_provider: 'Izbrišem ponudnika {name}?',
        mappings:                'Preslikava kanalov',
        mappings_subtitle:       'Kateri kanali ponudnikov polnijo naš kanal — največ en na ponudnika. Brez EPG: {n}.',
        suggest:                 'Predlagaj',
        suggest_title:           'Predlogi preslikav',
        suggest_help:            'Za vsak kanal brez EPG najboljši kanal vseh ponudnikov po imenu. Označene sprejmete naenkrat.',
        suggest_help_provider:   'Za vsak kanal, ki ga ta ponudnik še ne polni, njegov najboljši kanal po imenu. Označene sprejmete naenkrat.',
        suggest_none:            'Ni predlogov — vsi kanali so pri tem ponudniku preslikani ali pa ni ujemanj po imenu.',
        suggest_from:            'Predlagaj iz {name}',
        suggest_all:             'Vsi ({n})',
        suggest_selected:        'izbranih: {n}',
        suggest_accept:          'Sprejmi ({n})',
        suggest_done:            'Preslikanih kanalov: {n}.',
        all_providers:           'Vsi ponudniki (kanali brez EPG)',
        all_providers_used:      'Kanal je že preslikan pri vseh ponudnikih.',
        sources:                 'Kanali ponudnikov',
        entries:                 'Vnosi',
        entry_count:             '{n} vnos | {n} vnosa | {n} vnosi | {n} vnosov',
        max_days:                '{n} dni naprej',
        max_days_label:          'Dni naprej',
        max_days_help:           '0 = vse, kar vir ima; sicer 1–30.',
        max_days_help_short:     '0 = vse',
        not_mapped:              'Ni preslikan',
        mapping_unknown:         'Ni v viru',
        channel_disabled:        'izklopljen',
        map:                     'Preslikaj',
        map_another:             'Dodaj ponudnika',
        unmap:                   'Odstrani preslikavo',
        confirm_unmap:           'Odstranim preslikavo kanala {name} pri ponudniku {provider}? Njegovi shranjeni vnosi EPG se izbrišejo.',
        map_title:               'EPG za {name}',
        map_help:                'Izberite ponudnika in njegov kanal. Iskanje ne loči velikih črk, šumnikov in oznak HD.',
        search_provider_channel: 'Išči kanal ponudnika …',
        no_provider_channels:    'Ni zadetkov.',
        used_elsewhere:          'že uporabljen',
        already_mapped:          'Ta kanal že ima preslikavo EPG pri tem ponudniku.',
        entries_title:           'Program: {name} · {provider}',
        entries_help:            'Shranjeni vnosi EPG po datumu (največ 7 dni naenkrat).',
        entries_empty:           'V tem obdobju ni vnosov.',
        from:                    'Od',
        to:                      'Do',
        reload:                  'Osveži',
        range_order:             'Datum »do« je pred »od«.',
        range_max:               'Največ {n} dni naenkrat.',
        delete_range:            'Izbriši obdobje',
        confirm_delete_range:    'Izbrišem vnose v tem obdobju ({n})? Ob naslednjem uvozu se vrnejo, dokler je kanal preslikan.',
        deleted_entries:         'Izbrisanih vnosov: {n}.',
    },
    theme: {
        saved:               'Shranjene teme',
        new:                 'Nova tema',
        edit:                'Uredi temo',
        empty:               'Ni še shranjene teme — zasloni uporabljajo vgrajen videz.',
        active:              'Aktivna',
        active_help:         'Vsi sobni zasloni uporabljajo temo »{name}«.',
        none_active:         'Nobena tema ni aktivna — zasloni uporabljajo vgrajen videz.',
        activate:            'Uporabi',
        activated:           'Tema »{name}« je zdaj aktivna.',
        deactivate:          'Uporabi vgrajen videz',
        deactivated:         'Zasloni uporabljajo vgrajen videz.',
        duplicate:           'Podvoji',
        copy_of:             '{name} (kopija)',
        default_name:        'Tema {n}',
        revert:              'Razveljavi spremembe',
        saved_inactive:      'Tema je shranjena (še ni aktivna).',
        confirm_delete:      'Izbrišem temo »{name}«?',
        name:                'Ime',
        background:          'Ozadje',
        kind_solid:          'Enobarvno',
        kind_gradient:       'Preliv',
        color:               'Barva',
        angle:               'Smer',
        stops:               'Barve preliva ({n})',
        stops_help:          '2–5 barv; položaj 0–100 %.',
        stops_rule:          'Preliv potrebuje 2–5 barv s položaji 0–100 po vrsti.',
        stop_at:             'Položaj %',
        stop_add:            'Dodaj barvo',
        stop_remove:         'Odstrani barvo',
        opacity:             'Prosojnost',
        card:                'Kartica kanala',
        card_background:     'Ozadje kartice',
        card_text:           'Besedilo',
        card_text_secondary: 'Drugotno besedilo',
        focus_background:    'Izbrana kartica',
        focus_text:          'Besedilo izbrane',
        progress_divider:    'Napredek in ločilo',
        progress_track:      'Podlaga napredka',
        progress_fill:       'Napredek EPG',
        divider:             'Ločilo',
        preview:             'Predogled',
        preview_next:        'Nato: Vreme · Šport · Film',
        pick_color:          'Izberi barvo',
        hex_rule:            'Barva v obliki #RRGGBB.',
    },
    tv_settings: {
        title:      'Nastavitve TV',
        subtitle:   'Kako sobni zasloni ponudijo kanale.',
        adult:      'Kanali za odrasle',
        adult_help: 'Velja za kanale z oznako »za odrasle«.',
        channel_order:      'Vrstni red kanalov',
        channel_order_help: 'Po jezikih kanalov (Kanali › Jeziki).',
        language_categories:      'Kategorije po jezikih',
        language_categories_help: 'Za vaše kategorije doda kategorijo za vsak jezik z vsaj dvema kanaloma (Deutsch, English …) — samodejno, iz jezikov kanalov; jezik gosta je vedno med njimi, in na vrhu, če ni privzeti jezik hiše. Kanali za odrasle za kodo PIN v njih niso.',
    },
    channel_order: {
        guest_language:      'Najprej kanali v jeziku gosta',
        guest_language_help: 'Gost, ki ima TV v nemščini, vidi najprej nemške kanale, nato vse druge v vašem vrstnem redu (privzeto).',
        house:               'Vrstni red hiše',
        house_help:          'Vsi gostje vidijo kanale v vašem vrstnem redu.',
    },
    content: {
        title:        'Vsebina',
        subtitle:     'Kaj gost vidi na sobnem zaslonu.',
        tab_general:  'Splošno',
        tab_welcome:  'Pozdrav',
        tab_home:     'Domači zaslon',
        tab_hotel:    'Hotel',
        tab_services: 'Storitve',
        tab_music:    'Glasba',
        tab_program:  'Program',
        tab_signage:  'Signage',
        tab_invitations: 'Vabila',
        tab_themes:   'Teme',
        tab_seasonal: 'Sezonske teme',
        tab_room_guide: 'Vaša soba',
    },
    room_guide: {
        housekeeping:           'Gumb »Nekaj manjka?«',
        housekeeping_help:      'Gost na TV-ju in telefonu sporoči, kaj manjka ali ne deluje; zahteva pride v Zahteve gostov (Gospodinjstvo).',
        reception:              'Zahteve za recepcijo',
        reception_help:         'Gost prek Mila prosi za poznejšo odjavo, dodatno noč, taksi ali mizo v restavraciji; zahteva pride v Zahteve gostov (Recepcija). Sistema PMS to ne spremeni.',
        on:                     'Vklopljeno',
        off:                    'Izklopljeno',
        scope:                  'Za katere sobe',
        scope_search:           'Išči tip ali sobo …',
        scope_house:            'Vse sobe',
        scope_house_help:       'Velja za vse sobe. Tipi sob in posamezne sobe to podedujejo in lahko dopolnijo.',
        scope_type_help:        'Velja za sobe tipa {name} ({n}): podeduje »Vse sobe«, vnose lahko prilagodite, skrijete ali dodate.',
        scope_room_help:        'Velja samo za to sobo: podeduje »Vse sobe«, vnose lahko prilagodite, skrijete ali dodate.',
        scope_room_type_help:   'Velja samo za to sobo: podeduje »Vse sobe« in tip {type}, vnose lahko prilagodite, skrijete ali dodate.',
        additions_room:         'Samo v tej sobi',
        additions_type:         'Samo za ta tip',
        inherited:              'Podedovano',
        customize:              'Prilagodi',
        customized:             'Prilagojeno',
        hide:                   'Skrij',
        hidden_here:            'Skrito',
        show_again:             'Pokaži',
        reset:                  'Ponastavi',
        apply:                  'Uporabi',
        untitled:               'Brez imena',
        icon:                   'Ikona',
        icon_change:            'Zamenjaj',
        photo:                  'Fotografija',
        photo_help:             'Neobvezno; namesto ikone.',
        where_title:            'Kje je kaj',
        where_subtitle:         'Npr. »Fen — v predalu pod ogledalom v kopalnici« ({n} od največ {max})',
        where_add:              'Dodaj stvar',
        where_edit:             'Uredi stvar',
        where_empty:            'Še ni nobene stvari.',
        where_name:             'Kaj',
        where_name_help:        'Npr. »Fen«, »Dodatna odeja«, »Sef«.',
        where_text:             'Kje je',
        where_text_help:        'Npr. »V predalu pod ogledalom v kopalnici«.',
        howto_title_section:    'Kako deluje',
        howto_subtitle:         'Kratka navodila po korakih: klima, sef, žaluzije, TV, kavni aparat … ({n} od največ {max})',
        howto_add:              'Dodaj navodilo',
        howto_edit:             'Uredi navodilo',
        howto_empty:            'Še ni nobenega navodila.',
        howto_title:            'Naslov',
        howto_title_help:       'Npr. »Klima« ali »Sef«.',
        steps:                  'Koraki ({n} od največ {max})',
        steps_n:                '{n} korak | {n} koraka | {n} koraki | {n} korakov',
        steps_required:         'Vpišite vsaj en korak.',
        step_placeholder:       'Npr. »Pritisnite ON na daljincu na nočni omarici.«',
        step_add:               'Dodaj korak',
        step_remove:            'Odstrani korak',
        minibar_title:          'Minibar',
        minibar_subtitle:       'Kaj je v minibaru in po kakšni ceni — se zaračuna na račun sobe ({n} od največ {max})',
        minibar_note:           'Opomba pod minibarjem',
        minibar_note_default:   'Se zaračuna na račun sobe',
        minibar_note_help:      'Prazno = »Se zaračuna na račun sobe«.',
        minibar_note_help_scope: 'Prazno = podedovana opomba (siva).',
        minibar_add:            'Dodaj artikel',
        minibar_edit:           'Uredi artikel',
        minibar_empty:          'Minibar je prazen.',
        minibar_from_menu:      'Iz menija blagajne',
        minibar_manual:         'Ročno',
        minibar_item:           'Artikel',
        minibar_item_help:      'Katerikoli artikel uvoženega menija, tudi če ni ponujen za postrežbo v sobo; cena sledi meniju.',
        minibar_item_pick:      'Izberite artikel …',
        minibar_item_required:  'Izberite artikel.',
        minibar_no_menu:        'Meni še ni uvožen (Postrežba v sobo) — artikel vpišite ročno.',
        minibar_missing:        'Artikla ni več v meniju ali nima cene — gost ga ne vidi.',
        minibar_missing_short:  'Ni v meniju',
        minibar_price_from_menu: 'Cena iz menija',
        minibar_name_override:  'Ime za gosta',
        minibar_name_override_help: 'Prazno = ime artikla v meniju.',
        minibar_name:           'Ime',
    },
    general_settings: {
        languages:        'Jeziki',
        languages_help:   'Jeziki, v katerih je vsebina in ki jih gost izbere na zaslonu.',
        languages_keep:   'Odstranjen jezik obdrži besedila — vrnejo se, ko ga spet vklopite.',
        default_language: 'Privzeti jezik',
        location:         'Kraj za vreme',
        location_help:    'Vremenska napoved na zaslonih je za ta kraj.',
    },
    appearance: {
        title:             'Videz zaslona',
        subtitle:          'Ime, barve, logotip in slog sobnega zaslona.',
        display_name:      'Ime na zaslonu',
        display_name_help: 'Prazno = ime stranke.',
        tagline:           'Podnaslov',
        monogram:          'Monogram',
        monogram_help:     '1–2 črki, dokler ni logotipa.',
        primary:           'Glavna barva',
        accent:            'Poudarek',
        color_help:        'Prazno = barve izdelka.',
        reset_color:       'Privzeta barva',
        theme:             'Tema',
        typeface:          'Pisava',
        radius:            'Vogali',
        logo:              'Logotip',
    },
    welcome: {
        title:               'Pozdrav',
        subtitle:            'Pozdrav na začetnem zaslonu, v vsakem jeziku hotela.',
        greeting:            'Pozdrav gostu',
        greeting_help:       '{\'{\'}name{\'}\'} = ime gosta (iz PMS), {\'{\'}room{\'}\'} = ime sobe.',
        empty_greeting:      'Pozdrav brez gosta',
        empty_greeting_help: 'Ko v sobi ni gosta ali hotel nima PMS; brez {\'{\'}name{\'}\'}.',
        subtitle_field:      'Podnaslov',
        show_departure:      'Pokaži odhod',
        show_departure_help: 'Datum odhoda gosta (iz PMS).',
        show_room_number:    'Pokaži sobo',
        show_weather:        'Pokaži vreme',
        unknown_variable:    'Dovoljeni sta le {\'{\'}name{\'}\'} in {\'{\'}room{\'}\'}.',
        no_name_in_empty:    'Pozdrav brez gosta ne sme vsebovati {\'{\'}name{\'}\'}.',
        preview:             'Predogled',
        preview_language:    'Jezik predogleda',
        preview_guest:       'Gost v sobi',
        sample_name:         'Ime gosta',
        sample_room:         'Soba',
        preview_room:        'Soba {room}',
        preview_departure:   'Odhod: nedelja',
        preview_weather:     '☀ 18 °C',
    },
    home: {
        title:         'Domači zaslon',
        subtitle:      'Ploščice na domačem zaslonu: katere so vidne, njihov naslov, ozadje in vrstni red.',
        shown:         'Vidna',
        hidden:        'Skrita',
        tile_title:    'Naslov (prazno = privzeti)',
        tile_subtitle: 'Podnaslov (prazno = privzeti)',
        background:    'Ozadje',
        features_help: 'Zaslon izpusti ploščice, ki jih brez povezave s PMS ne more napolniti (npr. račun).',
        size:          'Velikost',
        size_help:     'Velikost ploščice (Samodejno, 1×1, 2×1, 1×2, 2×2): ko jo ima vsaj ena ploščica, je domači zaslon mreža in ploščice se postavijo po vrsti — predogled je pod seznamom.',
        home_greeting: 'Domov ni ploščica — pozdrav na vrhu TV-ja in telefona urejate v ',
        home_greeting_link: 'Vsebina › Dobrodošlica',
        merged_note: 'Ta ploščica je zdaj prikazana znotraj drugega zaslona (npr. pismo v »Vaše bivanje«). Naslov, podnaslov in ozadje veljajo, ko ima spet svojo ploščico.',
        cast_help:     'Aplikacije se pokažejo le na TV-jih, ki imajo sprejemnik za telefon (Android z Imprion TV 1.2.0 ali novejšim).',
        edit_in:       'Uredi: {place}',
        edit_place: {
            home: 'Vsebina › Pozdrav',
            letter: 'Vsebina › Hotel › Pozdravno pismo',
            house: 'Vsebina › Hotel › Hiša',
            local: 'Vsebina › Hotel › Okolica',
            booking: 'Vsebina › Hotel › Rezervacije in vreme',
            program: 'Vsebina › Program',
            services: 'Vsebina › Storitve',
            music: 'Vsebina › Glasba',
            invitations: 'Vsebina › Vabila',
            room_guide: 'Vsebina › Vaša soba',
            alarm: 'Vsebina › Teme',
            tv: 'TV › Kanali',
            room_service: 'Postrežba v sobo',
            requests: 'Zahteve gostov',
            bill: 'Povezava s PMS',
            nearby: 'Povezava s PMS',
            milo: 'Milo › Nastavitve',
            feedback: 'Pohvale, pritožbe in predlogi › Nastavitve',
        },
    },
    hotel: {
        help:                'Hiša, pozdravno pismo, okolica, rezervacije in vreme. Različica {revision}.',
        house:               'Hiša',
        house_help:          'Naslov, Wi-Fi, delovni čas in telefonske številke.',
        address:             'Naslov',
        wifi_network:        'Omrežje Wi-Fi',
        wifi_password:       'Geslo Wi-Fi',
        reception_key:       'Tipka za recepcijo',
        reception_key_help:  'Številka na sobnem telefonu.',
        photo:               'Fotografija',
        emergency:           'V sili',
        hours:               'Delovni čas ({n}/{max})',
        hours_label:         'Kaj (npr. Zajtrk)',
        hours_value:         'Kdaj (npr. 7.00–10.00)',
        hours_add:           'Dodaj vrstico',
        phone_keys:          'Telefonske tipke ({n}/{max})',
        phone_key:           'Tipka',
        phone_label:         'Kdo (npr. Restavracija)',
        phone_add:           'Dodaj tipko',
        letter:              'Pozdravno pismo',
        letter_help:         'Uvod, napotki z ikonami in podpis.',
        intro:               'Uvod',
        directions:          'Napotki ({n}/{max})',
        direction_add:       'Dodaj napotek',
        signature:           'Podpis',
        local:               'Okolica',
        local_help:          'Dogodki in kraji v bližini.',
        qr:                  'Koda QR za zemljevid',
        qr_help:             'Pri vnosih z naslovom zemljevida zaslon nariše kodo QR.',
        events:              'Dogodki ({n}/{max})',
        event_name:          'Dogodek',
        event_date:          'Datum',
        event_time:          'Ura',
        event_venue:         'Prizorišče',
        event_add:           'Dodaj dogodek',
        places:              'Kraji ({n}/{max})',
        place_name:          'Ime',
        place_kind:          'Vrsta',
        place_hours:         'Odprto',
        place_add:           'Dodaj kraj',
        distance:            'Oddaljenost',
        price:               'Cena',
        discount:            'Popust',
        note:                'Opomba',
        maps_url:            'Naslov zemljevida',
        photo_credit:        'Avtor in licenca fotografije',
        photo_credit_help:   'Prikaže se ob fotografiji (npr. fotografije z Wikimedia Commons).',
        event_source_url:    'Vir (spletna stran dogodka)',
        event_description:   'Opis',
        event_description_help: 'Daljše besedilo v podrobnostih na TV-ju in telefonu (do 600 znakov).',
        photo_focus:         'Slika — točka v ospredju',
        photo_focus_help:    'Kliknite del slike, ki mora ostati viden. TV in telefon obrežeta okoli te točke; v podrobnostih je slika vedno cela. Točko premikajo tudi puščice.',
        photo_focus_value:   'Točka v ospredju: {x} % od leve, {y} % od vrha',
        photo_focus_reset:   'Ponastavi na sredino',
        photo_poster:        'To je plakat (veliko besedila) — na kartici pokaži cel plakat, ne obreži ga',
        preview_tv:          'Kartica na TV-ju',
        preview_phone:       'Kartica na telefonu',
        preview_sample:      'Ime dogodka',
        booking:             'Rezervacije',
        booking_help:        'Rezervacije storitev na zaslonu (le s PMS, ki to podpira).',
        booking_enabled:     'Omogoči rezervacije',
        lead_hours:          'Najmanj ur vnaprej',
        lead_hours_help:     'Koliko ur pred terminom še lahko rezervira.',
        booking_note:        'Opomba',
        weather:             'Vreme',
        weather_help:        'Napoved za kraj hotela (Splošno).',
        weather_enabled:     'Pokaži vreme',
        weather_days:        'Dni napovedi',
        weather_hourly:      'Tudi po urah',
        weather_preview:     'Preizkusi napoved',
        program_source:      'Program dneva',
        program_source_help: 'Od kod zaslon vzame program dneva.',
        tab_house:           'Hiša',
        tab_hours:           'Urniki in telefoni',
        tab_letter:          'Pozdravno pismo',
        tab_events:          'Okolica: dogodki',
        tab_places:          'Okolica: kraji',
        tab_more:            'Rezervacije in vreme',
        hours_help:          'Delovni čas in tipke sobnega telefona.',
        unsaved:             'Neshranjeno',
        leave_unsaved:       'Sprememb na strani Hotel niste shranili. Zapustim stran?',
        leave:               'Zapusti',
    },
    local_events: {
        title:          'Samodejni pregled Okolice',
        help:           'Ob izbranih dnevih poišči nove dogodke v okolici in predlagaj odstranitev preteklih. Predlogi se zberejo tukaj.',
        enabled:        'Vklopi samodejni pregled',
        enabled_help_schedule: '{when} ob {time} poišče nove dogodke v bližini.',
        days:           'Dnevi',
        days_help:      'Ob katerih dnevih v tednu teče pregled.',
        days_none:      'Izberite vsaj en dan.',
        day_short:      { mon: 'Pon', tue: 'Tor', wed: 'Sre', thu: 'Čet', fri: 'Pet', sat: 'Sob', sun: 'Ned' },
        every_first:    { mon: 'Vsak ponedeljek', tue: 'Vsak torek', wed: 'Vsako sredo', thu: 'Vsak četrtek', fri: 'Vsak petek', sat: 'Vsako soboto', sun: 'Vsako nedeljo' },
        day_name:       { mon: 'ponedeljek', tue: 'torek', wed: 'sredo', thu: 'četrtek', fri: 'petek', sat: 'soboto', sun: 'nedeljo' },
        every_day:      'Vsak dan',
        and:            'in',
        time_format:    '{h}.{m}',
        time:           'Ura',
        time_help:      'Po krajevnem času hotela.',
        run_now:        'Zaženi zdaj',
        running:        'Milo pregleduje okolico … To lahko traja do tri minute.',
        run_done:       'Pregled končan: novih predlogov {added}, preteklih dogodkov za odstranitev {removed}.',
        run_not_done:   'Pregled ni iskal',
        run_busy:       'Pregled že teče — počakajte, da se konča.',
        run_too_soon:   'Pregled je pravkar tekel. Znova ga lahko zaženete {time}.',
        run_error:      'Pregleda ni bilo mogoče zagnati.',
        next_review:    'Naslednji pregled: {when}',
        suggestions_n:  '{n} predlog | {n} predloga | {n} predlogi | {n} predlogov',
        outcome:        { not_configured: 'ni nastavljen ključ ali lokacija', over_budget: 'dnevna poraba za Mila je dosežena', failed: 'iskanje ni uspelo', interrupted: 'prekinjen ob ponovnem zagonu' },
        photo_credit:   'Foto: {credit}',
        photo_license:  'Licenca',
        on_map:         'Na zemljevidu',
        place_added:    'Kraj dodan v Okolico.',
        radius:         'Polmer (km)',
        radius_help:    'Kako daleč od hotela išče.',
        kinds:          'Vrste dogodkov',
        kinds_help:     'Pusti prazno za vse vrste.',
        kinds_any:      'Vse vrste',
        not_configured: 'Za pregled je potreben ponudnikov ključ Anthropic in nastavljena lokacija hotela.',
        last_review:    'Zadnji pregled: {when}, {result}',
        review_button:  'Predlogi',
        review_title:   'Predlogi za Okolico',
        empty:          'Ni novih predlogov.',
        source:         'Vir:',
        add_action:     'Dodaj',
        add_reject:     'Zavrni',
        remove_action:  'Odstrani',
        remove_keep:    'Obdrži',
        added:          'Dogodek dodan v Okolico.',
        removed:        'Dogodek odstranjen iz Okolice.',
        dismissed:      'Predlog zavrnjen.',
        saved:          'Nastavitve shranjene.',
        save_error:     'Nastavitev ni bilo mogoče shraniti.',
        decide_error:   'Predloga ni bilo mogoče obdelati.',
        translating:    'Milo prevaja v {n} jezik in shranjuje … | Milo prevaja v {n} jezika in shranjuje … | Milo prevaja v {n} jezike in shranjuje … | Milo prevaja v {n} jezikov in shranjuje …',
        saving:         'Milo shranjuje …',
        added_translated: 'Dodano in prevedeno v {n} jezik. | Dodano in prevedeno v {n} jezika. | Dodano in prevedeno v {n} jezike. | Dodano in prevedeno v {n} jezikov.',
        added_untranslated: 'Dodano; prevod ni uspel',
        added_untranslated_help: 'Manjkajoče jezike dopolnite v Vsebina › Hotel.',
    },
    local_places: {
        title:        'Iskanje krajev',
        help:         'Milo na spletu poišče zanimive znamenitosti, restavracije, dejavnosti, trgovine in storitve v izbranem polmeru, s fotografijo z odprto licenco (Wikimedia Commons), kjer obstaja. Kraji, ki so že na seznamu ali ste jih zavrnili, se ne ponovijo.',
        kinds:        'Vrste krajev',
        search:       'Poišči kraje',
        running:      'Milo išče kraje v okolici … To lahko traja do tri minute.',
        done:         'Iskanje končano: {n} nov predlog. | Iskanje končano: {n} nova predloga. | Iskanje končano: {n} novi predlogi. | Iskanje končano: {n} novih predlogov.',
        last_search:  'Zadnje iskanje: {when}, {result}',
        review_title: 'Predlogi krajev',
    },
    local_event_kind: {
        concerts:  'Koncerti',
        sport:     'Šport',
        culture:   'Kultura',
        markets:   'Sejmi',
        family:    'Družina',
        food:      'Hrana',
        festivals: 'Festivali',
        outdoors:  'Narava',
    },
    service: {
        services:       'Storitve',
        subtitle:       'Ponudba hotela na zaslonu (informativno).',
        connector_note: 'Ko ima PMS storitve, zaslon pokaže njegove; ta seznam velja, kadar jih PMS nima ali PMS ni povezan.',
        add:            'Nova storitev',
        edit:           'Uredi storitev',
        name:           'Ime',
        note:           'Opis',
        group:          'Skupina',
        group_help:     'Npr. »Velnes« — zaslon jih združi.',
        price:          'Cena',
        price_label:    'Cena ({currency})',
        informational:  'Informativna ponudba',
        informational_help: 'Predstavitev brez objavljene cene in rezervacije na TV. Gost se za podrobnosti obrne na osebje.',
        per_day:        '/ dan',
        photo:          'Fotografija',
        daily:          'Na dan',
        daily_help:     'Cena velja za dan, ne za obisk.',
        hidden:         'Skrita',
        hidden_help:    'Na zaslonu se ne pokaže.',
        empty:          'Ni še nobene storitve.',
        confirm_delete: 'Izbrišem storitev {name}?',
    },
    invitation: {
        title:            'Vabila',
        subtitle:         'Povabite goste na večerjo, dogodek, degustacijo … — s fotografijami, opisom in QR kodo za rezervacijo.',
        add:              'Novo vabilo',
        edit:             'Uredi vabilo',
        empty:            'Ni še nobenega vabila.',
        limit:            'Največ {max} vabil.',
        confirm_delete:   'Izbrišem vabilo {name}?',
        kind:             'Vrsta',
        name:             'Naslov',
        summary:          'Kratek opis',
        summary_help:     'Ena vrstica na kartici.',
        description:      'Opis',
        texts:            'Besedila',
        photos:           'Fotografije',
        photo:            'Glavna fotografija',
        gallery:          'Galerija ({n}/{max})',
        gallery_add:      'Dodaj fotografijo',
        when:             'Kdaj',
        starts:           'Začetek',
        ends:             'Konec',
        date:             'Datum',
        time:             'Ura',
        recurring:        'Ponavljanje',
        recurring_help:   'Npr. »vsak petek ob 19.00« — namesto datuma ali poleg njega.',
        validity:         'Prikaz na zaslonu',
        valid_from:       'Od',
        valid_to:         'Do',
        validity_help:    'Prazno pomeni brez omejitve. Vabilo z datumom izgine samo, ko dogodek mine.',
        price:            'Cena',
        price_help:       'Prosto besedilo, npr. »45 € na osebo«.',
        booking:          'Rezervacija',
        cta_label:        'Besedilo poziva',
        cta_default:      'Rezervirajte',
        booking_url:      'Povezava za rezervacijo',
        booking_url_help: 'Na TV-ju postane QR koda, ki jo gost skenira s telefonom.',
        booking_phone:    'Telefon ali tipka recepcije',
        qr_preview:       'QR koda na TV-ju',
        qr_invalid:       'Povezava se mora začeti s https:// ali http://',
        active:           'Aktivno',
        featured:         'Izpostavljeno',
        featured_help:    'Pokaže se tudi v velikem okvirju na domačem zaslonu (prvo izpostavljeno).',
        on_screen:        'Na zaslonu',
        off_screen:       'Ni na zaslonu',
        always:           'Stalno',
        preview:          'Predogled kartice',
        scan:             'Skenirajte za rezervacijo',
        no_title:         '(brez naslova)',
        end_before_start: 'Konec mora biti po začetku.',
        to_before_from:   '»Do« ne sme biti pred »Od«.',
    },
    music: {
        title:          'Glasba',
        subtitle:       'Radijske postaje in datoteke za sobe in javne prostore.',
        add:            'Nova postaja',
        edit:           'Uredi postajo',
        name:           'Ime',
        note:           'Opomba',
        url:            'Naslov',
        source:         'Vir',
        audience:       'Za koga',
        active:         'Aktivna',
        inactive:       'Neaktivna',
        ambience:       'Glasba v ozadju',
        ambience_help:  'Javni zasloni jo predvajajo sami; le ena.',
        languages:      'Jeziki',
        languages_help: 'Na zaslonu gosta so najprej postaje v njegovem jeziku, nato tiste brez jezika (za vse), nato ostale.',
        empty:          'Ni še nobene postaje.',
        confirm_delete: 'Izbrišem postajo {name}?',
        playback:       'Predvajanje',
        logo:                'Logotip',
        logo_help:           'Prikazan na seznamu postaj na TV-ju, pri budilki in v aplikaciji za goste.',
        find_logo:           'Poišči logotip',
        logo_found:          'Logotip najden — shranite postajo.',
        logo_not_found:      'Logotipa ni bilo mogoče najti',
        logo_not_found_help: 'V javnem imeniku postaj ni dovolj velikega logotipa. Naložite ga sami.',
    },
    streams: {
        title:          'Kanali in postaje z napakami',
        subtitle:       'Napake, ki so jih javili zasloni, in zadnje preverjanje vsakega toka (vsako noč).',
        days:           'Zadnjih {n} dni',
        check_all:      'Preveri vse',
        check_started:  'Preverjam kanale in postaje ({n}) …',
        check_running:  'Preverjanje že teče.',
        checking:       'Preverjanje teče …',
        empty:          'V zadnjih {n} dneh ni bilo napak in vse preverjeno deluje.',
        item:           'Kanal ali postaja',
        kind_channel:   'TV kanal',
        kind_station:   'Radijska postaja',
        disabled:       'izklopljen',
        errors:         'Napake',
        errors_count:   '{n}×',
        devices_count:  'na {n} napravi | na {n} napravah | na {n} napravah | na {n} napravah',
        no_errors:      'brez napak',
        last_error:     'Zadnja napaka',
        last_check:     'Zadnje preverjanje',
        open:           'Odpri',
        codes:          'Kode',
        load_failed:    'Pregleda ni bilo mogoče naložiti.',
        retry:          'Poskusi znova',
    },
    program: {
        title:               'Program dneva',
        subtitle:            'Program, ki ga vidijo gostje — urejate delovno kopijo in jo objavite.',
        publish:             'Objavi',
        published:           'Program je objavljen.',
        published_at:        'Objavljeno {at}',
        never_published:     'Še ni objavljeno',
        unpublished_changes: 'spremembe še niso objavljene',
        closed:              'Program prihaja iz PMS',
        closed_help:         'Na zavihku Hotel je vir programa »Iz PMS« — program urejate v PMS.',
        standing_info:       'Stalna vrstica',
        standing_info_help:  'Npr. »Zajtrk 7.00–10.00«, vsak dan.',
        items:               'Točke ({n}/20)',
        add:                 'Nova točka',
        edit:                'Uredi točko',
        from:                'Od',
        to:                  'Do',
        time_rule:           'Ura v obliki HH:MM.',
        item_title:          'Naslov',
        note:                'Opomba',
        signup:              'Prijave',
        signup_help:         'Gost se lahko prijavi z zaslona (potreben je PMS z bivanjem).',
        signup_count:        'Prijav: {n}',
        empty:               'Za ta dan ni točk.',
    },
    signage: {
        title:           'Signage',
        subtitle:        'Zanke za javne zaslone v recepciji, lobiju ali pred dvorano.',
        add:             'Nova zanka',
        edit:            'Uredi zanko',
        name:            'Ime',
        note:            'Opomba',
        active:          'Aktivna',
        inactive:        'Neaktivna',
        default_seconds: 'Privzeto trajanje (s)',
        slides:          'Prosojnice',
        slide_count:     '{n} prosojnica | {n} prosojnici | {n} prosojnice | {n} prosojnic',
        devices:         'Zasloni',
        device_count:    '{n} zaslon | {n} zaslona | {n} zasloni | {n} zaslonov',
        slide_add:       'Dodaj prosojnico',
        no_slides:       'Zanka še nima prosojnic.',
        slide_enabled:   'Vklopljena',
        slide_title:     'Naslov',
        body:            'Besedilo',
        seconds:         'Trajanje (s)',
        seconds_help:    '0 = privzeto ali dolžina videa.',
        from:            'Od',
        to:              'Do',
        photo:           'Slika',
        video:           'Video',
        poster:          'Slika pred videom',
        muted:           'Brez zvoka',
        loop:            'Ponavljaj video',
        empty:           'Ni še nobene zanke.',
        assign_help:     'Zanko javnemu zaslonu dodelite na strani Naprave.',
        confirm_delete:  'Izbrišem zanko {name}? Zasloni z njo ({n}) ostanejo brez nje.',
    },
    connector: {
        title:                     'Povezava s PMS',
        subtitle:                  'Od kod Imprion TV dobi gosta, račun, storitve in rezervacije.',
        settings:                  'Nastavitve',
        type:                      'Sistem',
        none_help:                 'Brez PMS zasloni kažejo vsebino hiše, pozdrav brez imena in storitve brez rezervacij.',
        base_url:                  'Naslov API',
        base_url_help:             'Npr. https://pms.imprion.dev/api/pms/v1',
        token:                     'Ključ integracije',
        token_help:                'Ključ, izdan v PMS (Nastavitve → Integracije).',
        token_set:                 'Ključ je shranjen. Prazno pusti obstoječega.',
        property_external_id:      'ID objekta v PMS',
        property_external_id_help: 'Le za informacijo — objekt določa ključ.',
        test:                      'Preizkusi',
        test_ok:                   'Povezava deluje: {name}, sob: {rooms}.',
        test_failed:               'Povezava ne deluje.',
        status:                    'Stanje',
        property:                  'Objekt',
        last_ok:                   'Zadnji uspešen klic',
        last_error:                'Zadnja napaka',
        capabilities:              'Zmožnosti',
        no_capabilities:           'PMS ne javlja nobene zmožnosti.',
        rooms:                     'Sobe v PMS',
        rooms_help:                'Vsaki sobi PMS določite našo sobo — po tem zaslon najde gosta.',
        match:                     'Poveži po imenu',
        matched:                   'Sobe z enakim imenom so povezane.',
        pms_room:                  'Soba v PMS',
        our_room:                  'Soba v Imprion TV',
        no_room:                   '— ni povezana —',
        bentral_help:              'Bentral ne pošilja sprememb — Imprion TV ga prebere vsakih 5 minut (bivanja od včeraj do 30 dni naprej). Iz Bentrala pridejo ime in jezik gosta ter datumi bivanja; računa, storitev in sobne postrežbe Bentral nima.',
        bentral_token:             'API ključ Bentral',
        bentral_token_help:        'V Bentralu: Nastavitve → API. Shrani se šifrirano in se nikoli več ne prikaže.',
        bentral_base_url_help:     'Prazno = Bentral (api.bentral.com). Drug naslov le za preizkus.',
        property_pick:             'Objekt v Bentralu',
        property_pick_help:        'Ključ lahko doseže več objektov — izberite hotel, katerega sobe so v Imprion TV.',
        property_placeholder:      'Izberite objekt',
        property_required:         'Izberite objekt.',
        load_properties:           'Naloži objekte',
        properties_loaded:         'Ključ doseže {n} objekt. | Ključ doseže {n} objekta. | Ključ doseže {n} objekte. | Ključ doseže {n} objektov.',
        no_properties:             'Ključ ne doseže nobenega objekta.',
        key_needed:                'Najprej vpišite API ključ.',
        stay_statuses:             'Statusi rezervacij, ki so bivanje',
        stay_statuses_help:        'Rezervacije s temi statusi se pokažejo v Zasedenosti in na TV-ju. Privzeto: potrjena, plačana, delno plačana, neplačana.',
        stay_statuses_required:    'Izberite vsaj en status.',
        test_ok_bentral:           'Povezava deluje: {name}.',
        test_units:                'Vrst sob: {types} · sob: {rooms}',
        test_today:                'Danes prihodov: {arrivals} · odhodov: {departures}',
        sync:                      'Zadnje branje',
        sync_never:                'še ne — prvo v nekaj minutah ali z »Osveži zdaj«',
        sync_counts:               '{stays} bivanj v 30 dneh · brez sobe: {unassigned} · v nepovezanih sobah: {unmapped}',
        sync_now:                  'Osveži zdaj',
        synced:                    'Podatki iz Bentrala so prebrani.',
        sync_failed:               'Branje ni uspelo: {error}',
        group:                     'Vrsta sobe',
        suggestion:                'Predlog: {room}',
        accept_suggestion:         'Poveži s predlagano sobo',
        unmapped_pms:              '{n} soba v PMS še ni povezana | {n} sobi v PMS še nista povezani | {n} sobe v PMS še niso povezane | {n} sob v PMS še ni povezanih',
        unmapped_pms_help:         'Gostje teh sob se ne pokažejo v Zasedenosti in jih TV ne pozdravi.',
        unmapped_ours:             '{n} naša soba nima sobe v PMS | {n} naši sobi nimata sobe v PMS | {n} naše sobe nimajo sobe v PMS | {n} naših sob nima sobe v PMS',
        all_mapped:                'Vse sobe so povezane.',
        matched_rooms:             'Sobe z enakim imenom ali številko so povezane.',
    },
    import: {
        title:         'Uvoz iz Hospitality',
        subtitle:      'Kanali z logotipi, kategorije, EPG, sobe, naprave, pozdrav in teme iz starega sistema.',
        file:          'Datoteka',
        file_help:     'JSON, ki ga je izvozil izvoznik (format hospitality-v1), do 20 MB.',
        drop:          'Spustite datoteko JSON sem',
        drop_help:     'ali kliknite za izbiro',
        source:        'Vir',
        exported_at:   'Izvoženo',
        dry_run:       'Preizkusni uvoz',
        run:           'Uvozi',
        rerun_help:    'Uvoz lahko ponovite: zapisi z istim starim ID se posodobijo, nič se ne izbriše.',
        confirm:       'Uvozim podatke v {name}? Obstoječi zapisi z istim starim ID se posodobijo.',
        done:          'Uvoz je končan.',
        failed:        'Uvoz ni uspel.',
        not_json:      'Datoteka ni veljaven JSON.',
        wrong_format:  'To ni izvoz Hospitality (pričakovan format {format}).',
        invalid:       'Dokument ni veljaven.',
        invalid_help:  'Napak: {n}. Popravite izvoz in poskusite znova.',
        result:        'Rezultat',
        result_dry:    'Rezultat preizkusa (nič ni zapisano)',
        result_done:   'Uvoženo',
        result_empty:  'Izberite datoteko in zaženite preizkusni uvoz.',
        what:          'Kaj',
        created:       'Novih',
        updated:       'Posodobljenih',
        skipped:       'Preskočeno ({n})',
        logo_failures: 'Logotipi, ki jih ni bilo mogoče prenesti ({n})',
        dry_run_note:  'Pri preizkusu se logotipi ne prenašajo. Če je vse prav, kliknite »Uvozi«.',
        kind_categories:    'Kategorije',
        kind_channels:      'Kanali',
        kind_epg_providers: 'Ponudniki EPG',
        kind_epg_mappings:  'Preslikave EPG',
        kind_rooms:         'Sobe',
        kind_devices:       'Naprave',
        kind_themes:        'Teme',
        kind_assets:        'Logotipi',
    },
    tizen: {
        title:             'Samsung TV (Tizen)',
        subtitle:          'Aplikacija Imprion TV za Samsungove hotelske televizorje: televizor jo namesti sam z naslova spodaj in jo posodobi, ko je na voljo novejša.',
        install_url:       'Naslov za namestitev (Custom App URL)',
        install_url_help:  'V Tizen Business Managerju: Entity → Profile → Default Home → Custom App URL. URL Launcher na televizorju vzame mapo: {folder}',
        copy:              'Kopiraj naslov',
        copied:            'Naslov je kopiran.',
        none:              'Aplikacija za TV še ni objavljena.',
        none_help:         'Televizor na tem naslovu ne najde ničesar za namestitev. Aplikacijo je treba zgraditi in podpisati ter objaviti skupaj z zaslonom.',
        version:           'Objavljena različica',
        file:              'Datoteka',
        size:              'Velikost',
        steps_title:       'Namestitev na televizor',
        step_1:            'Televizor v hotelskem načinu, vpisan v Tizen Business Manager (Entity → Screen → Device).',
        step_2:            'Entity → Profile → Default Home: Custom App URL = naslov zgoraj, Home Key Action = Hotel App.',
        step_3:            'Action → Zero Touch Configuration: profil pripni televizorju ali skupini.',
        step_4:            'Televizor ugasni in prižgi: namesti »Imprion TV«, ki pokaže kodo za povezovanje.',
        step_5:            'Naprave (ali Teren → Nova naprava): vpiši kodo in izberi sobo.',
        update_note:       'Novo različico televizor namesti sam ob naslednjem vklopu, ko je objavljena različica višja od njegove. Sobni zaslon se posodobi z vsako objavo, kot povsod.',
        certificate_note:  'Testni certifikat velja samo za televizorje, katerih DUID je vpisan vanj. Za več televizorjev in za upravljanje televizorja (vklop, vhod, glasnost) je potreben Samsungov partnerski certifikat.',
        drawer_title:      'Samsung TV',
        no_facts:          'Televizor še ni poslal svojih podatkov.',
        model:             'Model',
        real_model:        'Prodajni model',
        firmware:          'Programska oprema',
        tizen_version:     'Tizen',
        duid:              'DUID',
        serial:            'Serijska številka',
        serial_unreadable: 'ni berljiva brez partnerskega certifikata',
        page_apis:         'API-ji na strani',
        page_apis_none:    'stran ne doseže API-jev televizorja',
        boot:              'Zagon zaslona',
        boot_inapp:        'v aplikaciji televizorja',
        boot_navigate:     'kot spletna stran (rezervna pot)',
        boot_legacy:       'kot spletna stran (aplikacija 1.0)',
        b2b_absent:        'Upravljanje TV (b2b): ni na voljo — potreben partnerski certifikat',
        b2b_detected:      'Upravljanje TV (b2b): zaznano (še ni vgrajeno)',
        no_cec:            'HDMI-CEC ni potreben: zaslon je televizor sam.',
        tv_itself:         'televizor sam',
    },
    apk: {
        title:                 'Android aplikacija',
        subtitle:              'Različice aplikacije Imprion TV za Android, ki se na napravah posodobijo same — vsaka izvedba v svoji vrsti.',
        upload:                'Naloži APK',
        upload_help:           'Paket, različico, najnižji Android in podpisni ključ strežnik prebere iz APK-ja.',
        upload_failed:         'Nalaganje ni uspelo.',
        uploaded_ok:           'Različica {name} je naložena (še ni trenutna).',
        drop:                  'Spustite datoteko .apk sem',
        file:                  'Datoteka',
        not_apk:               'Datoteka ni .apk.',
        too_large:             'Datoteka je večja od 200 MB.',
        version:               'Različica',
        version_exists:        'Ta koda različice je za to izvedbo že naložena.',
        version_too_low:       'Koda različice {code} je nižja od najvišje naložene izvedbe {variant} ({highest}). Zvišajte versionCode in APK zgradite znova.',
        invalid:               'Datoteka ni berljiv APK.',
        invalid_reason:        'Datoteka ni berljiv APK ({reason}).',
        wrong_package:         'To ni aplikacija Imprion TV (paket {name}).',
        unsigned:              'APK ni podpisan s shemo APK Signature v2/v3, zato ga naprave ne bi namestile.',
        wrong_signer:          'APK je podpisan z drugim ključem ({signer}), z nobenim od dovoljenih podpisnih ključev. Naprave ga ne bi namestile čez obstoječo aplikacijo.',
        read_title:            'Naložena različica {name}',
        read_help:             'To je strežnik prebral iz APK-ja. Različica še ni trenutna.',
        package:               'Paket',
        min_sdk:               'Najnižji Android',
        api_level:             'API {level}',
        size:                  'Velikost',
        signer:                'Podpisni ključ',
        signer_copied:         'Prstni odtis ključa je kopiran.',
        code:                  'koda {code}',
        notes:                 'Opombe',
        make_current:          'Naredi trenutno',
        now_current:           '{name} je zdaj trenutna — naprave jo namestijo ob naslednjem preverjanju.',
        confirm_current:       'Naredim {name} trenutno za izvedbo {variant}? Njene naprave jo namestijo ob naslednjem preverjanju.',
        confirm_current_older: '{name} je starejša od trenutne. Naprave z novejšo ostanejo na svoji (aplikacija namesti le višjo kodo). Nadaljujem?',
        confirm_delete:        'Izbrišem različico {name}?',
        no_current_help:       'Naprave te izvedbe ne dobijo posodobitev, dokler ene ne naredite trenutne.',
        devices:               'Naprav na njej',
        uploaded:              'Naloženo',
        download:              'Prenesi',
        copied:                'SHA-256 je kopiran.',
        copy_failed:           'Kopiranje ni uspelo.',
        empty:                 'Ni še naložene različice.',
        variant:               'Izvedba',
        variant_all:           'Vse izvedbe',
        variant_standard:      'Standardna',
        variant_platform:      'Platformna',
        variant_help_standard: 'Podpisana z izdajnim ključem Imprion. Deluje na vsaki napravi; tihe posodobitve le kot Device Owner.',
        variant_help_platform: 'Podpisana s platformnim ključem vdelane programske opreme. Tihe posodobitve in ponovni zagon na napravah, katerih programska oprema je podpisana s tem ključem.',
        current_of:            'Trenutna · {variant}',
        no_current_of:         'Nobena različica izvedbe {variant} ni trenutna',
        empty_variant:         'Ni še naložene različice izvedbe {variant}.',
    },
    component: {
        title:            'Sistemske komponente — WebView',
        subtitle:         'WebView, v katerem teče sobni zaslon. Platformne naprave z dovoljenjem za tiho nameščanje ga posodobijo same, ponoči med 2. in 5. uro (ali takoj z ukazom »Posodobi WebView zdaj«), le čez obstoječega in le z enakim podpisnim ključem.',
        upload:           'Naloži WebView',
        upload_help:      'APK sistemskega WebViewa (com.android.webview ali com.google.android.webview). Podpisni ključ mora biti enak kot pri WebViewu na napravah, sicer ga Android zavrne.',
        uploaded_ok:      'WebView {name} je naložen.',
        read_title:       'Naložen WebView {name}',
        read_help:        'To je strežnik prebral iz APK-ja. Primerjajte podpisni ključ s tistim na napravah.',
        empty:            'Ni še naloženega WebViewa.',
        current:          'Trenutni',
        wrong_package:    'To ni sistemski WebView (paket {name}). Dovoljena sta com.android.webview in com.google.android.webview.',
        version_too_low:  'Koda različice {code} je nižja od najvišje naložene za {name} ({highest}).',
        version_exists:   'Ta koda različice je za ta paket že naložena.',
        confirm_current:  'Naredim WebView {name} trenutnega? Platformne naprave ga namestijo ponoči ob naslednjem preverjanju.',
        confirm_current_older: 'WebView {name} je starejši od trenutnega. Naprave z novejšim ostanejo na svojem. Nadaljujem?',
        now_current:      'WebView {name} je zdaj trenutni.',
        confirm_delete:   'Izbrišem WebView {name}?',
        no_current:       'Noben WebView paketa {name} ni trenuten — naprave ne dobijo posodobitev.',
        devices_help:     'Naprave, ki po zadnjem poročilu sobnega zaslona tečejo na tem WebViewu.',
        signer_help:      'Android namesti WebView le čez obstoječega z enakim ključem. Testne naprave Droidlogic: AOSP testni ključ A4:0D:A8:0A … A7:1B:F5:DC.',
    },
    tile_status: {
        shown:      'Na TV-ju',
        hidden:     'Skrita — {reason}',
        never_tile: 'Ni ploščica (zaslon se odpre od drugod)',
        help:       'Stanje velja za sobni TV danes; osveži se ob shranjevanju.',
        reason: {
            merged_into_house:   'v ploščici Hiša',
            merged_into_tv:      'v ploščici Televizija',
            no_services:         'ni vidnih storitev',
            no_local:            'ni dogodkov ne krajev',
            program_unpublished: 'današnji program ni objavljen',
            no_program:          'za danes ni programa',
            needs_connector:     'potrebna je povezava s PMS',
            no_channels:         'ni vklopljenih kanalov',
            no_stations:         'ni aktivnih postaj za sobe',
            no_partners:         'PMS nima partnerjev s ponudbo',
            no_invitations:      'ni vabil na zaslonu',
            no_room_service:     'ni postrežbe v sobo (ne iz PMS ne lastne)',
            milo_off:            'Milo na sobnih TV-jih je izklopljen',
            no_requests:         'gost še nima zahtev',
            no_room_guide:       '»Vaša soba« še nima vsebine',
            feedback_off:        'mnenja gostov so izklopljena',
            alarm_on_clock:      'budilko odpre ura na vrhu (tema)',
        },
        explain: {
            merged_into_house: 'Pozdravno pismo je del zaslona Hiša, dokler je ploščica Hiša vidna.',
            merged_into_tv:    'Radijske postaje so na zaslonu Televizija, dokler je ta ploščica vidna.',
            milo_off:          'Ploščica se pokaže, ko je Milo vklopljen za sobne TV-je (Milo › Nastavitve) in ima ponudnik ključ.',
            no_requests:       'Ploščica se na TV-ju pokaže sama, ko ima gost v sobi naročilo ali rezervacijo — brez prijavljenega gosta je skrita.',
            feedback_off:      'Ploščica se pokaže, ko skrbnik vklopi mnenja gostov (Pohvale, pritožbe in predlogi › Nastavitve).',
        },
        fix: {
            services:  'Vsebina › Storitve',
            hotel:     'Vsebina › Hotel (Okolica)',
            program:   'Vsebina › Program',
            music:     'Vsebina › Glasba',
            channels:  'TV › Kanali',
            connector: 'Povezava s PMS',
            invitations: 'Vsebina › Vabila',
            room_service: 'Postrežba v sobo',
            milo:      'Milo › Nastavitve',
            room_guide: 'Vsebina › Vaša soba',
            feedback:  'Pohvale, pritožbe in predlogi › Nastavitve',
            themes:    'Vsebina › Teme',
        },
    },
    tile_size: {
        auto:  'Samodejno',
        small: 'Majhna 1×1',
        wide:  'Široka 2×1',
        tall:  'Visoka 1×2',
        large: 'Velika 2×2',
    },
    home_grid: {
        title:         'Postavitev domačega zaslona',
        size:          'Mreža {columns} × {rows}',
        estimate:      'Ocena — neshranjeno',
        auto:          'Vse vidne ploščice so »Samodejno«: ploščice razporedi okvir (privzeti videz ali aktivna tema). Mreža se vklopi, ko eni ploščici izberete velikost.',
        overflow_title: '{n} ploščica ne gre na zaslon | {n} ploščici ne gresta na zaslon | {n} ploščice ne gredo na zaslon | {n} ploščic ne gre na zaslon',
        does_not_fit:  'Ne gre na zaslon — zmanjšajte ali skrijte ploščico.',
        frame_none:    'Privzeti videz',
        sidebar_mode:  'Aktivna tema kaže levo vrstico z eno veliko kartico: velikosti ploščic v tem načinu ne veljajo, vse vidne ploščice so v seznamu.',
    },
    screen_mode: {
        light: 'Svetla',
        dark:  'Temna',
    },
    screen_navigation: {
        sidebar: 'Leva vrstica',
        cards:   'Kartice',
        both:    'Oboje',
    },
    screen_alarm_entry: {
        tile:  'Ploščica / vrstica v meniju',
        clock: 'Samo ura na vrhu',
    },
    screen_alarm_entry_help: {
        tile:  'Budilka (»Prebujanje po vaše«) je ena od ploščic domačega zaslona, kot doslej.',
        clock: 'Brez ploščice budilke: gost se z jezikov s puščico premakne na uro v glavi zaslona in z OK nastavi budilko. Ob uri je ikona budilke.',
    },
    screen_navigation_help: {
        sidebar: 'Levo seznam vseh ploščic, desno ena velika kartica izbrane s predogledom vsebine (Wi-Fi, kaj je na sporedu, vabilo …). Velikosti ploščic tu ne veljajo.',
        cards:   'Brez levega stolpca: kartice čez cel domači zaslon, logotip v glavi.',
        both:    'Levi stolpec in kartice hkrati — vsaka ploščica je na zaslonu dvakrat.',
    },
    screen_qr_position: {
        welcome: 'Ob pozdravu',
        header:  'Zgoraj desno, pod uro',
        sidebar: 'Na dnu stranskega stolpca',
        hidden:  'Skrito',
    },
    screen_room_label_position: {
        sidebar: 'V stranskem stolpcu, pod logotipom',
        header:  'Ob uri, zgoraj desno',
        welcome: 'V vrstici z datumom odhoda',
    },
    screen_layout: {
        rows:       'Vrstice',
        rows_brand: 'Vrstice z znamko',
        sidebar:    'Stranski stolpec',
        strip:      'Trak',
        split:      'Deljeno',
        classic:    'Izvirna',
    },
    screen_layout_help: {
        rows:       'Glava čez celo širino, pozdrav, prva vrsta velikih kartic, ostale pod njimi (Natura, Rural).',
        rows_brand: 'Levo stolpec znamke z logotipom in motom, desno vrstice (Heritage).',
        sidebar:    'Levo navigacijski stolpec z vsemi ploščicami, desno pozdrav in kartice (Alpine, Ember).',
        strip:      'Slika ozadja čez cel zaslon, vse ploščice v eni spodnji vrsti (Riviera).',
        split:      'Levi dve tretjini vsebina, desna tretjina visoka slika (Metropolitan).',
        classic:    'Prvotni videz sobnega zaslona: levo pozdrav in ploščice, desno vremenska napoved in izpostavljeno vabilo (Izvirna). Iz teme se vzamejo le podlaga, plošče, poudarek, besedilo, vogali in pisava.',
    },
    screen_card: {
        caption_bar: 'Slika s pasom',
        overlay:     'Napis čez sliko',
        icon:        'Ikona brez slike',
        photo_top:   'Slika zgoraj',
        photo_fade:  'Slika se preliva',
        list:        'Seznam',
    },
    screen_mark: {
        none:      'Brez',
        dot:       'Pika',
        arrow:     'Puščica',
        arrow_all: 'Puščica na vseh',
    },
    screen_mark_help: {
        none:      'Izbrano kartico označi samo obroba.',
        dot:       'Majhna pika poudarka v kotu izbrane kartice (Natura).',
        arrow:     'Okrogel gumb s puščico na izbrani kartici (Metropolitan, Heritage, Rural).',
        arrow_all: 'Puščica na vsaki kartici, na izbrani v barvi poudarka (Alpine).',
    },
    screen_ornament: {
        none:              'Brez',
        leaves:            'Listi',
        copper_glow:       'Bakren sij',
        mountains_lineart: 'Gore (črte)',
        arches_lineart:    'Loki (črte)',
        wheat_flowers:     'Žito in cvetlice',
    },
    screen_ornament_help: {
        none:              'Brez okrasa.',
        leaves:            'Mehka veja v zgornjem kotu in ob dnu (Natura).',
        copper_glow:       'Topel sij za glavo zaslona (Ember).',
        mountains_lineart: 'Gorski greben ob dnu navigacijskega stolpca (Alpine).',
        arches_lineart:    'Loki in steber v spodnjem delu stolpca znamke (Heritage).',
        wheat_flowers:     'Žitni klasi in travniške cvetlice ob obeh robovih (Rural).',
    },
    logo_treatment: {
        as_is:       'Kot je naložen',
        light_plate: 'Na svetli ploščici',
        inverted:    'Bel obris',
    },
    logo_treatment_help: {
        as_is:       'Logotip hotela je narisan, kot je naložen.',
        light_plate: 'Na majhni ploščici v barvi površine — temen logotip na temni temi.',
        inverted:    'Kot bel obris — za temen stolpec ali temno podlago.',
    },
    screen_font_kind: {
        serif: 'serif',
        sans:  'sans',
    },
    screen_color: {
        background:              'Podlaga',
        background_alt:          'Topli kot podlage',
        surface:                 'Površina',
        surface_alt:             'Druga površina',
        surface_border:          'Obroba površine',
        surface_text:            'Naslovi na površini',
        surface_text_muted:      'Podnaslovi na površini',
        heading:                 'Pozdrav in naslovi',
        text:                    'Besedilo',
        text_muted:              'Drugotno besedilo',
        accent:                  'Poudarek',
        accent_text:             'Besedilo na poudarku',
        focus_ring:              'Obroba izbrane kartice',
        nav_background:          'Ozadje stolpca',
        nav_text:                'Naslovi v stolpcu',
        nav_text_muted:          'Podnaslovi in moto v stolpcu',
        nav_active:              'Črta izbrane postavke',
        nav_active_background:   'Ozadje izbrane postavke',
        card_caption_background: 'Pas pod sliko',
        card_caption_text:       'Naslov v pasu',
        card_caption_text_muted: 'Podnaslov v pasu',
        overlay_text:            'Besedilo čez sliko',
        overlay_scrim:           'Senca pod besedilom',
        chip_background:         'Čip',
        chip_border:             'Obroba čipa',
        chip_text:               'Besedilo čipa',
        chip_active_background:  'Izbran čip',
        chip_active_border:      'Obroba izbranega čipa',
        chip_active_text:        'Besedilo izbranega čipa',
        divider:                 'Ločilo',
        ornament:                'Okras',
    },
    screen_color_help: {
        background:              'Osnova vsakega zaslona (tudi črni robovi).',
        background_alt:          'Podlaga je preliv od tega kota proti podlagi.',
        surface:                 'Ploskev kartice ali panela.',
        surface_alt:             'Vgnezdene vrstice, polje ikone, stranski panel brez slike.',
        surface_border:          'Tanka obroba kartice.',
        surface_text:            'Naslovi na kartici.',
        surface_text_muted:      'Podnaslovi na kartici.',
        heading:                 'Pozdrav, ime hiše, naslovi zaslonov.',
        text:                    'Besedilo na podlagi.',
        text_muted:              'Podnaslov, datum, namigi tipk.',
        accent:                  'Naslovi razdelkov, ikone, cene, oznake izbrane kartice.',
        accent_text:             'Besedilo ali puščica na poudarku.',
        focus_ring:              'Obroba kartice, ki je izbrana z daljincem.',
        nav_background:          'Navigacijski stolpec in stolpec znamke.',
        nav_text:                'Naslovi v stolpcu, obrnjen logotip v njem.',
        nav_text_muted:          'Podnaslovi in moto v stolpcu.',
        nav_active:              'Črta ob izbrani postavki.',
        nav_active_background:   'Vrstica izbrane postavke.',
        card_caption_background: 'Pas pod sliko kartice »Slika s pasom«.',
        card_caption_text:       'Naslov v tem pasu.',
        card_caption_text_muted: 'Podnaslov v tem pasu.',
        overlay_text:            'Napis čez sliko (kartice »Napis čez sliko«, pozdrav na traku).',
        overlay_scrim:           'Preliv pod napisom, od vrha proti dnu.',
        chip_background:         'Jezikovni čipi, čip sobe, narisane tipke.',
        chip_border:             'Njihova obroba.',
        chip_text:               'Njihovo besedilo.',
        chip_active_background:  'Izbrani jezik, tipka OK.',
        chip_active_border:      'Njegova obroba.',
        chip_active_text:        'Njegovo besedilo.',
        divider:                 'Tanke črte in ločila.',
        ornament:                'Črte okrasa.',
    },
    screen_opacity: {
        surface:            'Prosojnost',
        card_caption:       'Prosojnost',
        overlay_scrim_from: 'Zgoraj',
        overlay_scrim_to:   'Spodaj',
        ornament:           'Prosojnost',
    },
    season: {
        title:              'Sezonske teme',
        subtitle:           'Milo iz osnovne teme pripravi videz za praznik ali razpoloženje — barve, ozadje, okrasje, pozdrav in animacijo. Sobni zasloni ga v izbranem obdobju prikažejo sami, nato se vrnejo na osnovno temo.',
        create:             'Nova sezonska tema',
        create_title:       'Nova sezonska tema',
        create_help:        'Izberite priložnost, osnovno temo, obdobje in kaj naj Milo prilagodi. Tema se shrani takoj; pozneje jo lahko uredite v urejevalniku teme.',
        regenerate_title:   'Ustvari znova',
        regenerate_help:    'Milo znova pripravi samo izbrane dele; ostali ostanejo, kot so.',
        occasion:           'Priložnost',
        prompt:             'Razpoloženje',
        prompt_help:        'Neobvezno pri prazniku, obvezno pri »Po meri«. Npr. »toplo, družinsko, z zlatimi poudarki«.',
        prompt_placeholder: 'Npr. elegantno, umirjeno, zimski gozd …',
        prompt_required:    'Opišite razpoloženje.',
        base:               'Osnovna tema',
        base_help:          'Postavitev, kartice in pisave ostanejo iz osnovne teme.',
        own_theme:          'lastna',
        from:               'Od',
        to:                 'Do',
        to_before_from:     'Konec je pred začetkom (za obdobje čez novo leto vklopite »Vsako leto«).',
        yearly:             'Vsako leto',
        yearly_help:        'Ponovi se vsako leto ob istih dneh; obdobje lahko teče čez novo leto (24. 12.–6. 1.).',
        scheduled:          'Vklopi po urniku',
        scheduled_help:     'Zasloni temo v obdobju prikažejo sami. Izklopljeno: tema ostane pripravljena.',
        parts:              'Kaj naj Milo prilagodi',
        parts_help:         'Neizbrani deli ostanejo kot v osnovni temi. Vsak del lahko pozneje vklopite ali izklopite brez ponovne priprave.',
        parts_regenerate_help: 'Milo znova pripravi samo označene dele (in jih vklopi).',
        parts_required:     'Izberite vsaj en del.',
        no_image_provider_ahead: 'Slike ozadja trenutno ni mogoče ustvariti: ponudnik še ni vnesel ključa za slike (OpenAI) v Ponudnik › Milo. Barve, okrasje, pozdrav in animacija bodo vseeno pripravljeni.',
        generate:           'Ustvari z Milom',
        regenerate:         'Ustvari znova',
        working:            'Milo pripravlja temo …',
        working_help:       'Navadno traja do pol minute, s sliko do dve minuti. Okno lahko zaprete — Milo dela naprej.',
        working_short:      'Milo dela',
        step_palette:       'Barve, okrasje, pozdrav in animacija',
        step_image:         'Slika ozadja',
        step_saving:        'Shranjevanje',
        failed:             'Milo teme ni mogel pripraviti',
        failed_short:       'Ni uspelo',
        failure_provider:   'Ponudnik AI je vrnil napako. Poskusite znova čez nekaj minut.',
        failure_timeout:    'Ponudnik AI se ni odzval pravočasno. Poskusite znova.',
        failure_refused:    'Ponudnik AI je zahtevo zavrnil. Spremenite razpoloženje in poskusite znova.',
        failure_bad_answer: 'Milov odgovor ni bil uporaben (npr. neveljavna barva). Poskusite znova.',
        failure_interrupted: 'Priprava je bila prekinjena (ponovni zagon strežnika). Poskusite znova.',
        load_failed:        'Sezonskih tem ni bilo mogoče prebrati.',
        bases_failed:       'Tem za osnovo ni bilo mogoče prebrati.',
        follow_failed:      'Napredka ni bilo mogoče prebrati; Milo dela naprej — stanje je na seznamu.',
        try_again:          'Poskusi znova',
        open_editor:        'Uredi v urejevalniku teme',
        done:               'Končano',
        image_no_provider:  'Slike ozadja ni: ponudnik še ni vnesel ključa za slike (OpenAI) v Ponudnik › Milo. Ozadje ostane iz osnovne teme; ko bo ključ vnesen, izberite »Ustvari znova« in samo »Ozadje«.',
        image_no_provider_kept: 'Nove slike ozadja ni: ponudnik še ni vnesel ključa za slike (OpenAI) v Ponudnik › Milo. Ostane prejšnja Milova slika.',
        image_failed_kept:  'Nove slike ozadja ni bilo mogoče ustvariti ({reason}); ostane prejšnja Milova slika.',
        image_failed_reason: 'Slike ozadja ni bilo mogoče ustvariti ({reason}); ozadje ostane iz osnovne teme.',
        contrast_fixed:     'Za berljivost je bila popravljena {n} barva besedila. | Za berljivost sta bili popravljeni {n} barvi besedila. | Za berljivost so bile popravljene {n} barve besedila. | Za berljivost je bilo popravljenih {n} barv besedila.',
        parts_missing:      'Vklopljeno, a še ne pripravljeno: {list}. Pripravite z »Ustvari znova«.',
        in_force:           'V veljavi',
        every_year:         'vsako leto',
        based_on:           'iz {name}',
        activation:         'Prikaz',
        parts_on:           'Vklopljeni deli',
        preview:            'Predogled',
        now:                'Zasloni zdaj prikazujejo »{name}«',
        now_none:           'Zasloni zdaj prikazujejo osnovno temo',
        precedence:         'Kadar se obdobja prekrivajo, velja tema s krajšim obdobjem (npr. božični teden znotraj zime), nato tista, ki se je začela kasneje. Ročno »Vklopljeno« ima prednost pred urnikom; »Izklopljeno« se ne prikaže nikoli.',
        empty:              'Še ni sezonskih tem. Z »Nova sezonska tema« Milo pripravi prvo.',
        confirm_delete:     'Izbrišem sezonsko temo »{name}«? Milova slika ozadja se izbriše z njo.',
        error_not_configured: 'Milo še ni nastavljen: ponudnik še ni vnesel ključa za AI.',
        error_limit:        'Današnja omejitev za Mila je porabljena. Poskusite jutri.',
        error_failed:       'Milo teme ni mogel pripraviti.',
        error_busy:         'Milo to temo že pripravlja.',
        greeting_edit:      'Uredi pozdrav',
        greeting_title:     'Pozdrav na sobnem zaslonu',
        greeting_help:      'Vrstica pod dobrodošlico, v vsakem jeziku hotela (do 90 znakov). »Ustvari znova« drugih delov vašega besedila ne spremeni; zamenja ga le, če označite »Pozdrav«.',
        greeting_off:       'Del »Pozdrav« je izklopljen — zasloni pozdrava ne kažejo. Vklopite ga med vklopljenimi deli na kartici.',
        greeting_saved:     'Pozdrav je shranjen.',
        greeting_replace:   'Pozdrav ste uredili sami — če je »Pozdrav« označen, ga Milo zamenja s svojim.',
        step_tiles:         'Okraski na slikah ploščic ({done} od {total})',
        no_image_provider_tiles_ahead: 'Slik ploščic trenutno ni mogoče ustvariti: ponudnik še ni vnesel ključa za slike (OpenAI) v Ponudnik › Milo. Ploščice ostanejo z izvirnimi slikami.',
        tiles_title:        'Slike ploščic',
        tiles_help:         'Levo slika hotela, desno z okraski. Zasloni okrašene pokažejo le, dokler tema velja; izvirniki ostanejo nespremenjeni.',
        tiles_off:          'Del »Slike ploščic« je izklopljen — zasloni kažejo izvirne slike.',
        tiles_empty:        'Še ni okrašenih slik ploščic.',
        tile_original:      'Izvirnik',
        tile_decorated:     'Z okraski',
        tile_stale:         'Nova slika',
        tile_stale_help:    'Slika ploščice je bila medtem zamenjana — zasloni kažejo novo sliko. »Ustvari znova« okrasi novo.',
        tile_regenerate:    'Ustvari znova',
        tile_remove:        'Odstrani okras',
        confirm_tile_remove: 'Odstranim okras s ploščice »{name}«? Zasloni bodo kazali izvirno sliko.',
        tile_removed:       'Okras je odstranjen.',
        tiles_no_provider:  'Slik ploščic ni: ponudnik še ni vnesel ključa za slike (OpenAI) v Ponudnik › Milo. Ploščice ostanejo z izvirnimi slikami.',
        tiles_failed:       'Nekaterih slik ploščic ni bilo mogoče ustvariti: {list}.',
        tiles_skipped:      'Preskočeno: {list}. Sliko ploščici dodate v Vsebina › Domači zaslon.',
        tiles_none:         'Nobene ploščice ni bilo mogoče okrasiti — nobena nima slike. Sliko dodate v Vsebina › Domači zaslon.',
    },
    season_occasion: {
        christmas:  'Božič',
        new_year:   'Novo leto',
        easter:     'Velika noč',
        valentines: 'Valentinovo',
        halloween:  'Noč čarovnic',
        spring:     'Pomlad',
        summer:     'Poletje',
        autumn:     'Jesen',
        winter:     'Zima',
        custom:     'Po meri',
    },
    season_part: {
        colors:     'Barve',
        background: 'Ozadje',
        ornament:   'Okrasje',
        greeting:   'Pozdrav',
        animation:  'Animacija',
        tiles:      'Slike ploščic',
    },
    season_part_help: {
        colors:     'Paleta in prosojnost, z berljivim kontrastom.',
        background: 'Okrasna slika ozadja, prilagojena postavitvi.',
        ornament:   'Okras teme (veje, sij, …).',
        greeting:   'Vrstica pod »Dobrodošli« v vseh jezikih hotela.',
        animation:  'Nežna animacija: sneg, listje, iskrice, netopirji …',
        tiles:      'Okraski za priložnost na slikah ploščic domačega zaslona (do 12 slik); slike hotela ostanejo.',
    },
    season_tile_skip: {
        no_picture: 'brez slike',
        milo:       'Milo nima slike',
        limit:      'več kot 12',
    },
    season_activation: {
        schedule: 'Po urniku',
        on:       'Vklopljeno',
        off:      'Izklopljeno',
    },
    screen_theme: {
        title:          'Teme sobnega zaslona',
        subtitle:       'Celoten videz sobnega zaslona: barve, pisave, postavitev domačega zaslona, kartice, okras in ozadje.',
        none:           'Brez teme',
        active_now:     'Aktivna tema: {name}',
        none_active:    'Brez teme — sobni zaslon ima privzeti videz.',
        overrides_help: 'Barvi, zaobljenost in logotip iz Splošno › Videz ostanejo hotelovi in se upoštevajo tudi s temo; pisava in svetla/temna podlaga sta teme.',
        admin_chooses:  'Temo izbere skrbnik hotela. Lastne teme lahko ustvarite in urejate.',
        library:        'Knjižnica',
        own:            'Lastne teme',
        own_empty:      'Še nimate lastne teme. Izberite temo iz knjižnice in »Podvoji in uredi«.',
        use:            'Uporabi',
        duplicate:      'Podvoji',
        duplicate_edit: 'Podvoji in uredi',
        view:           'Ogled',
        active:         'Aktivna',
        based_on:       'iz {key}',
        used_by:        '{n} stranka | {n} stranki | {n} stranke | {n} strank',
        confirm_delete: 'Izbrišem temo »{name}«?',
        confirm_delete_used: 'Tema »{name}« je aktivna pri {n} strankah in je ni mogoče izbrisati, dokler jo uporabljajo. Vseeno poskusim?',
        activated:      'Tema »{name}« je zdaj na sobnih zaslonih.',
        deactivated:    'Sobni zasloni imajo spet privzeti videz.',
        duplicated:     'Ustvarjena je kopija »{name}«.',
        key_taken:      'Ključ »{key}« je že zaseden.',
        library_title:    'Teme (knjižnica)',
        library_subtitle: 'Vgrajene teme, ki jih vidijo vse stranke in jih podvojijo v svoje.',
        library_empty:    'V knjižnici ni tem.',
        library_help:     'Sprememba vgrajene teme doseže vse stranke, ki jo imajo aktivno, v eni minuti. Kopije strank ostanejo nespremenjene.',
        new:              'Nova tema',
        back:             'Nazaj',
        unsaved:          'Neshranjeno',
        use_after_save:   'Najprej shranite spremembe.',
        leave_unsaved:    'Sprememb teme niste shranili. Zapustim urejevalnik?',
        leave:            'Zapusti',
        builtin_read_only:      'Vgrajena tema iz knjižnice',
        builtin_read_only_help: 'Vgrajenih tem ne morete spreminjati. Podvojite jo in uredite svojo kopijo.',
        editor:           'Tema',
        editor_title:     'Tema »{name}«',
        editor_subtitle:  'Videz sobnega zaslona.',
        editor_meta:      '{scope} · ključ {key} · različica {revision}',
        scope_builtin:    'Knjižnica',
        scope_tenant:     'Lastna tema',
        section_about:    'Opis',
        section_colors:   'Barve',
        section_fonts:    'Pisave',
        section_layout:   'Postavitev',
        section_ornament: 'Okras',
        section_ground:   'Ozadje',
        section_logo:     'Logotip',
        name:             'Ime',
        name_help:        'Vsaj v enem jeziku.',
        description:      'Opis',
        key:              'Ključ',
        key_help:         'Male črke, številke in vezaji, 2–40 znakov; pozneje se ne spreminja.',
        key_fixed:        'Ključ se ne spreminja.',
        order:            'Vrstni red v knjižnici',
        order_help:       '0–1000; manjši je prej.',
        preview_picture:  'Slika za galerijo',
        preview_picture_help: 'Posnetek zaslona v tej temi, ki ga galerija pokaže na kartici.',
        based_on_help:    'Kopija teme »{key}«.',
        mode:             'Podlaga',
        mode_help:        'Svetla ali temna — po njej se ravnajo vsi zasloni.',
        group_ground:     'Podlaga in površine',
        group_text:       'Besedilo in poudarek',
        group_nav:        'Navigacijski stolpec',
        group_caption:    'Napisi kartic in prekrivanje',
        group_chips:      'Čipi',
        group_divider:    'Ločilo in okras',
        display_family:   'Pisava naslovov',
        display_family_help: 'Pozdrav, naslovi in naslovi ploščic.',
        display_weight:   'Debelina naslovov',
        letter_spacing_display: 'Razmik črk v pozdravu',
        body_family:      'Pisava besedila',
        body_family_help: 'Vse ostalo; samo pisava sans ali sistemska.',
        letter_spacing_label: 'Razmik črk v oznakah',
        letter_spacing_label_help: 'Male oznake z velikimi črkami in moto.',
        font_real:        'Pisava zaslona ({name})',
        font_approx:      'Približek — datoteke pisave {name} sobnega zaslona tu ni mogoče naložiti',
        font_loading:     'Nalagam pisavo {name} …',
        fonts_approx:     'Predogled riše {list} s približkom: datoteke sobnega zaslona niso dosegljive.',
        layout:           'Postavitev domačega zaslona',
        hero_tiles:       'Kartic v prvi vrsti',
        hero_tiles_help:  'Koliko ploščic si deli prvo vrsto — kolikor postavitev dopušča.',
        hero_n:           '{n}',
        navigation:       'Navigacija',
        navigation_help:  'Kako gost izbira: v levi vrstici z eno veliko kartico, s karticami ali z obojim.',
        navigation_cards_only: 'Ta postavitev nima levega stolpca, zato so na voljo samo kartice.',
        qr_position:      'Mesto QR-kod (Wi-Fi in aplikacija)',
        qr_position_help: 'Kje na domačem zaslonu stojita kodi za Wi-Fi in za aplikacijo v telefonu. Vedno ostaneta dovolj narazen, da gost s telefonom cilja eno.',
        room_label_position:      'Mesto oznake sobe',
        room_label_position_help: 'Kje stoji ime sobe (npr. »Hiška 1«) z ikono postelje.',
        order_ticker:      'Prikaz odprtih naročil',
        order_ticker_help: 'Na zaslonu stalno prikaže odprta naročila in zahteve gosta z odštevanjem časa.',
        alarm_entry:       'Budilka na domačem zaslonu',
        alarm_entry_help:  'Kako gost na sobnem TV-ju pride do budilke. Ura v glavi zaslona jo odpre v obeh primerih (OK na daljincu ali klik).',
        position_own:      'privzeto',
        position_fallback: 'Ta postavitev tega mesta nima — zaslon ga riše na privzetem mestu: {place}.',
        card_hero:        'Prva kartica',
        card_secondary:   'Druga kartica',
        card_rest:        'Ostale kartice',
        cards_help:       '»Seznam« je samo za ostale. Ploščica brez slike je v slikovnem slogu narisana kot ikona.',
        mark:             'Oznaka izbrane kartice',
        radius:           'Zaobljenost kartic',
        radius_help:      '0 = vse oglato; čipi in tipke sledijo.',
        ornament:         'Okras',
        ornament_help:    'Barva in prosojnost okrasa sta v Barve › Ločilo in okras.',
        background_kind:  'Ozadje',
        background_color: 'Barve teme',
        background_image: 'Slika',
        background_color_help: 'Podlaga je preliv iz barv »Topli kot podlage« in »Podlaga« (Barve).',
        background_picture: 'Slika ozadja',
        background_picture_help: 'Pri postavitvi »Deljeno« je slika v desnem panelu, pri »Trak« čez cel zaslon.',
        scrim:            'Tančica čez sliko',
        scrim_help:       'Barva podlage čez sliko, da je besedilo berljivo.',
        logo_treatment:   'Logotip hotela',
        logo_treatment_help: 'Logotip naloži hotel v Splošno › Videz; brez njega je monogram na poudarku.',
        preview:          'Predogled',
        preview_help:     'Domači zaslon 1920 × 1080 v tej temi. Del, ki ga spreminja nastavitev pod kazalcem ali v urejanju, je obrobljen z rdečo; klik na del zaslona pokaže nastavitve, ki ga rišejo.',
        highlight_unseen: 'V tej postavitvi ni vidno',
        preview_note:     'Predogled riše konzola po pravilih teme; sobni zaslon riše enako, a s svojimi ikonami in okrasom.',
        preview_welcome:  'Dobrodošli',
        preview_welcome_sub: 'Vse za prijetno bivanje na enem mestu.',
        preview_room:     'Soba 204',
        preview_qr_wifi:  'Wi-Fi: Hotel-Gost',
        preview_qr_app:   'Hotel v vašem telefonu',
        preview_select:   'Izbira',
        preview_open:     'Odpri',
        preview_body:     'Zajtrk je postrežen od 7. do 10. ure v restavraciji v pritličju.',
        preview_label:    'Koncept · Tradicija · Udobje',
        preview_hotel:    'Vaš hotel',
        preview_tagline:  'Tradicija · Udobje',
        apply_overrides:  'S prilagoditvami hotela ({list})',
        override_accent:  'poudarek',
        override_primary: 'primarna barva',
        override_radius:  'oglati robovi',
        sample_photos:    'Nadomestne slike za ploščice brez fotografije',
        provider_assets:  'Slike ponudnika',
        hex_rule:         'Barva v obliki #RRGGBB.',
        key_rule:         'Male črke, številke in vezaji; začne se s črko; 2–40 znakov.',
        name_required:    'Vpišite ime vsaj v enem jeziku.',
        hero_rule:        'Ta postavitev dopušča drugo število.',
        navigation_rule:  'Ta postavitev te navigacije ne dopušča.',
        weight_rule:      'Ta pisava nima te debeline.',
        body_rule:        'Za besedilo je dovoljena samo pisava sans ali sistemska.',
        picture_required: 'Izberite sliko ozadja.',
        fix_errors:       'Popravite označena polja.',
        field_refused:    'Strežnik te vrednosti ni sprejel.',
        sample: {
            services: 'Restavracija',
            local:    'Doživetja',
            house:    'Vaše bivanje',
            program:  'Program dneva',
            tv:       'TV in glasba',
        },
        sample_sub: {
            services:     'Odkrijte kulinarično ponudbo',
            local:        'Raziščite okolico',
            house:        'Informacije in kontakti',
            program:      'Dogodki in obvestila',
            tv:           'Kanali, radio in budilka',
            letter:       'Pozdravno pismo',
            bill:         'Pregled računa',
            music:        'Radijske postaje',
            alarm:        'Budilka',
            room_service: 'Naročila v sobo',
            nearby:       'Ponudba v bližini',
            invitations:  'Vabila hiše',
            cast:         'Aplikacije',
            milo:         'Vprašajte hotelskega pomočnika',
            requests:     'Naročila in rezervacije',
            room_guide:   'Kje je kaj, kako deluje, minibar',
            feedback:     'Povejte nam, kako ste zadovoljni',
        },
    },
    requests: {
        title:                  'Zahteve gostov',
        subtitle:               'Rezervacije vabil, naročila v sobo, gospodinjstvo in zahteve za recepcijo s TV-ja in telefona gosta',
        subtitle_kitchen:       'Naročila v sobo s TV-ja in telefona gosta',
        subtitle_housekeeping:  'Kaj gostom manjka ali ne deluje v sobi — s TV-ja in telefona gosta',
        done:                   'Urejeno',
        housekeeping_free:      'Brez opisa',
        housekeeping_from_guide: 'Iz »Vaša soba«',
        housekeeping_own_words: 'Gostove besede',
        reception_now:          'zdaj',
        reception_nights:       'Noči: {n}',
        reception_persons:      'Oseb: {n}',
        reception_time:         'Čas',
        reception_departure:    'Odhod po rezervaciji',
        reception_help:         'Uredite na recepciji (tudi v PMS) — Imprion TV ničesar ne spremeni sam.',
        reason_not_available:   'Žal tega trenutno nimamo.',
        reason_technician:      'Poklicali smo vzdrževalca, pride čim prej.',
        live:                   'V živo',
        offline:                'Brez povezave',
        sound_on:               'Zvok vklopljen',
        sound_off:              'Zvok izklopljen',
        notifications_enable:   'Vklopi obvestila',
        view_open:              'Odprte',
        view_all:               'Vse (zadnja 2 dni)',
        kind_all:               'Vse vrste',
        none_open:              'Ni odprtih zahtev.',
        none:                   'V zadnjih dveh dneh ni bilo zahtev.',
        pick:                   'Izberite zahtevo na seznamu.',
        asap:                   'čim prej',
        persons_n:              '{n} os.',
        persons:                'Oseb',
        slot:                   'Termin',
        desired:                'Želeni čas',
        proposed_at:            'Predlagani čas',
        invitation:             'Vabilo',
        created:                'Oddano',
        payment:                'Plačilo',
        payment_room:           'Na račun sobe',
        total:                  'Skupaj',
        note:                   'Opomba gosta',
        reason:                 'Razlog',
        history:                'Zgodovina',
        by_guest:               'gost',
        accept:                 'Sprejmi',
        accept_other_time:      'Sprejmi z drugim časom',
        accept_other_time_help: 'Gost na TV-ju in telefonu vidi, da je zahteva sprejeta za ta čas.',
        decline:                'Zavrni',
        decline_title:          'Zavrni zahtevo #{number}',
        decline_help:           'Razlog gost vidi na TV-ju in telefonu.',
        on_the_way:             'Na poti',
        delivered:              'Dostavljeno',
        reason_sold_out:        'Žal je razprodano.',
        reason_kitchen_closed:  'Kuhinja je zaprta.',
        reason_call_reception:  'Prosimo, pokličite recepcijo.',
        reason_full:            'Termin je zaseden.',
        reason_closed:          'Ta dan ne sprejemamo rezervacij.',
        delivery_line:          'Dostava',
        eta:                    'Predvideno pripravljeno',
        eta_in:                 'čez {n} min',
        eta_at_time:            'ob …',
        eta_pick:               'Kdaj bo pripravljeno?',
        accept_eta:             'Sprejmi — čez {n} min',
        accept_as_asked:        'Sprejmi (ob {time})',
        accept_at_title:        'Sprejmi — pripravljeno ob',
        accept_at_help:         'Gost na TV-ju in telefonu vidi, da bo naročilo pripravljeno okoli tega časa.',
        eta_at:                 'Pripravljeno ob',
        late_cancel:            'Gost je preklical (prepozno)',
        late_cancel_help:       'Gost je naročilo preklical, ker je bil sprejeti čas poznejši od želenega. Ne pripravljajte ga.',
        cancel_open:            'Gost lahko prekliče do {time}',
        cancel_open_help:       'Sprejeti čas je poznejši od želenega, zato lahko gost še prekliče naročilo.',
    },
    request_status: {
        pending:    'Čaka',
        accepted:   'Sprejeto',
        declined:   'Zavrnjeno',
        on_the_way: 'Na poti',
        delivered:  'Dostavljeno',
        cancelled:  'Preklicano',
        done:       'Urejeno',
    },
    request_kind: {
        booking:      'Rezervacija',
        room_service: 'Naročilo v sobo',
        housekeeping: 'Gospodinjstvo',
        reception:    'Recepcija',
    },
    reception_type: {
        late_checkout: 'Pozna odjava',
        extra_night:   'Dodatna noč',
        taxi:          'Taksi',
        table:         'Miza v restavraciji',
    },
    housekeeping_type: {
        missing_item: 'Manjka',
        extra_item:   'Potrebujem še',
        replace:      'Zamenjaj',
        broken:       'Ne deluje',
    },
    feedback_status: {
        new:         'Novo',
        in_progress: 'V obravnavi',
        resolved:    'Rešeno',
    },
    feedback_kind: {
        praise:     'Pohvala',
        complaint:  'Pritožba',
        suggestion: 'Predlog',
    },
    feedback_category: {
        room:        'Soba',
        cleanliness: 'Čistoča',
        food:        'Hrana',
        staff:       'Osebje',
        noise:       'Hrup',
        wifi_tv:     'Wi-Fi/TV',
        other:       'Drugo',
    },
    feedback_preset: {
        room_comfortable: 'Soba je zelo udobna',
        room_spotless:    'Vse je brezhibno čisto',
        food_delicious:   'Hrana je odlična',
        staff_friendly:   'Osebje je zelo prijazno',
        quiet_room:       'Soba je mirna',
        wifi_fast:        'Wi-Fi in TV delujeta odlično',
        would_return:     'Radi se vrnemo',
        room_temperature: 'V sobi je prevroče ali premrzlo',
        room_not_clean:   'Soba ni čista',
        food_slow:        'Hrana je prišla pozno ali hladna',
        staff_waiting:    'Predolgo sem čakal(a)',
        noise_night:      'Ponoči je hrupno',
        wifi_slow:        'Wi-Fi je počasen',
        tv_not_working:   'TV ne deluje',
        something_broken: 'Nekaj je pokvarjeno',
        breakfast_choice: 'Več izbire pri zajtrku',
        coffee_in_room:   'Kavni aparat v sobi',
        towels_on_request: 'Menjava brisač le na željo',
        soundproofing:    'Boljša zvočna izolacija',
        more_channels:    'Več TV programov',
        local_tips:       'Več nasvetov za izlete',
        eco_friendly:     'Več okolju prijaznih rešitev',
    },
    review_site: {
        google:      'Google',
        tripadvisor: 'Tripadvisor',
        booking:     'Booking.com',
        facebook:    'Facebook',
        instagram:   'Instagram',
    },
    review_invite: {
        after_feedback: 'Po poslanem mnenju',
        departure_day:  'Na dan odhoda',
        after_checkout: 'Po odjavi (telefon)',
    },
    review_invite_help: {
        after_feedback: 'Gost po vsaki pohvali, pritožbi ali predlogu vidi povezave »Ocenite nas« — po pohvali vidno, po pritožbi ali predlogu pod potrditvijo, tišje.',
        departure_day:  'Od izbrane ure večer pred odhodom do konca dneva odhoda TV enkrat na dan pokaže tiho obvestilo s QR kodami, telefon kartico na Domov.',
        after_checkout: 'Ko se povezava telefona po odjavi izteče, njen zaslon »povezava je potekla« ponudi povezave za oceno.',
    },
    feedback: {
        title:               'Pohvale, pritožbe in predlogi',
        subtitle:            'Mnenja gostov s TV-ja in telefona — pritožbe med bivanjem najprej',
        tab_inbox:           'Pregled',
        tab_analysis:        'Analiza',
        tab_settings:        'Nastavitve',
        summary_title:       'Zadnjih {n} dni',
        tile_rating:         'Povprečna ocena pohval',
        tile_rating_hint:    'iz {n} ocen',
        review_clicks:       'Kliki na »Ocenite nas«',
        from_tv:             'TV',
        from_phone:          'Telefon',
        from_checkout:       'Po odjavi',
        filter_status:       'Stanje',
        filter_kind:         'Vrsta',
        filter_category:     'Kategorija',
        filter_days:         'Obdobje',
        filter_help:         'Odprte so vedno na seznamu.',
        all_statuses:        'Vsa stanja',
        open_only:           'Samo odprte',
        all_kinds:           'Vse vrste',
        all_categories:      'Vse kategorije',
        last_days:           'Zadnjih {n} dni',
        none:                'Ni mnenj, ki bi ustrezala izbiri.',
        pick:                'Izberite mnenje na seznamu.',
        during_stay:         'Gost je še v sobi',
        during_stay_help:    'Pritožbo lahko uredite, preden gost odide — pokličite ali se oglasite v sobi.',
        nothing_said:        'Samo ocena',
        replied:             'Odgovorjeno',
        rating_n:            'Ocena {n} od 5',
        no_rating:           'brez ocene',
        status:              'Stanje',
        photo_n:             'Fotografija {n}',
        reply:               'Odgovor gostu',
        replied_by:          '{name}, {when}',
        reply_placeholder:   'Npr. »Opravičujemo se, sobo smo takoj uredili.«',
        reply_help:          'Gost odgovor vidi na telefonu in kot obvestilo na TV-ju v sobi. Novo mnenje gre s tem v obravnavo.',
        reply_help_gone:     'Gost je že odšel — odgovor bo viden le, če ima še odprto povezavo na telefonu.',
        reply_send:          'Pošlji odgovor',
        reply_replace:       'Zamenjaj odgovor',
        reply_sent:          'Odgovor je poslan.',
        reply_draft:         'Predlagaj odgovor',
        reply_draft_help:    'Osnutek napiše AI v jeziku gosta — preberite in popravite pred pošiljanjem.',
        reply_draft_replace: 'Besedilo, ki ste ga napisali, bo zamenjal nov osnutek. Nadaljujem?',
        reply_draft_confirm: 'Zamenjaj z osnutkom',
        notes:               'Interni zapiski',
        note_placeholder:    'Zapisek za ekipo (gost ga ne vidi)',
        note_add:            'Dodaj',
        expires:             'Izbriše se {when}.',
    },
    feedback_analysis: {
        open_analysis:          'Analiza',
        period_help:            'Isto obdobje kot seznam v Pregledu; odprte pritožbe štejejo ne glede na starost.',
        empty_title:            'Ni povratnih informacij',
        empty:                  'V zadnjih {n} dneh gostje niso poslali pohval, pritožb ali predlogov.',
        tile_total:             'Povratne informacije',
        tile_total_hint:        'pohvale: {praise} · predlogi: {suggestions}',
        tile_complaints:        'Pritožbe',
        tile_complaints_hint:   '{share} vseh',
        tile_open:              'Odprte pritožbe',
        tile_open_hint:         'nove in v obravnavi, kadarkoli poslane',
        tile_open_here:         'gost še v sobi: {n}',
        tile_overdue:           'Brez odgovora > {h} h',
        tile_overdue_hint:      'odprte pritožbe čakajo na odgovor gostu',
        tile_first_reply:       'Prvi odgovor (mediana)',
        tile_first_reply_hint:  'odgovorjenih {n} od {m} pritožb',
        tile_resolve:           'Do rešitve (mediana)',
        tile_resolve_hint:      'rešenih {n} od {m} pritožb',
        no_complaints_short:    'v obdobju ni pritožb',
        delta:                  '{change} glede na prejšnjih {n} dni',
        delta_same:             'enako kot prejšnjih {n} dni',
        under_minute:           'manj kot 1 min',
        minutes:                '{m} min',
        hours:                  '{h} h {m} min',
        days:                   '{d} d {h} h',
        trend_title:            'Gibanje',
        trend_subtitle:         'Pohvale in predlogi nad črto, pritožbe pod njo',
        legend_praise:          'Pohvale',
        legend_suggestions:     'Predlogi',
        legend_complaints:      'Pritožbe',
        bucket_day:             'po dnevih',
        bucket_week:            'po tednih (od ponedeljka)',
        bucket_month:           'po mesecih',
        trend_aria:             'Gibanje po obdobjih ({n}): pohvale {praise}, predlogi {suggestions}, pritožbe {complaints}. Puščici levo in desno pokažeta posamezno obdobje.',
        period:                 'Obdobje',
        show_table:             'Tabela',
        show_chart:             'Graf',
        category_title:         'Po kategorijah',
        category_subtitle:      'Razvrščeno po številu pritožb',
        legend_complaints_left: 'Pritožbe (levo)',
        legend_praise_right:    'Pohvale (desno)',
        legend_suggestions_right: 'Predlogi (desno, za pohvalami)',
        complaints_short:       'Pritožbe',
        praise_short:           'Pohvale',
        praise_suggestions_short: 'Pohvale · predlogi',
        rating_short:           'Ocena',
        row_counts:             'pritožbe {complaints}, pohvale {praise}, predlogi {suggestions}',
        row_rating:             'ocena pohval {rating} ★',
        no_feedback_in:         'Brez povratnih informacij: {list}.',
        topics_title:           'Kaj gostje najpogosteje izberejo',
        topics_subtitle:        'Pripravljeni stavki s TV-ja in telefona',
        top_complaints:         'Pri pritožbah',
        top_praise:             'Pri pohvalah',
        top_suggestions:        'Pri predlogih',
        top_none:               'Noben pripravljen stavek ni bil izbran.',
        share_of_complaints:    'v {share} pritožb',
        share_of_praise:        'v {share} pohval',
        share_of_suggestions:   'v {share} predlogov',
        rooms_title:            'Sobe',
        rooms_subtitle:         'Kje se pritožbe ponavljajo',
        rooms_list:             'Največ pritožb',
        rooms_none:             'V tem obdobju ni pritožb.',
        room_open:              'odprte: {n}',
        room_praise:            'pohvale: {n}',
        room_suggestions:       'predlogi: {n}',
        response_title:         'Odzivnost',
        response_subtitle:      'Kako hitro in kdaj smo odgovorili na pritožbe',
        no_complaints:          'V tem obdobju ni pritožb.',
        replied:                'Gost je dobil odgovor',
        n_of_m:                 '{n} od {m}',
        reply_when:             'Odgovor je prišel',
        during_stay:            'med bivanjem',
        after_stay:             'po odhodu',
        overdue_title:          'Brez odgovora več kot {h} h',
        overdue_none:           'Nobena odprta pritožba ne čaka dlje kot {h} h.',
        overdue_more:           'In še {n} — vse so na seznamu v Pregledu.',
        review_none:            'Povezave za ocene niso nastavljene.',
        review_set:             'Nastavite jih',
        review_admin:           'Nastavi jih skrbnik v Nastavitvah.',
        review_no_clicks:       'V tem obdobju ni klikov ({sites}).',
    },
    feedback_settings: {
        title:               'Mnenja gostov',
        subtitle:            'Ploščica »Pohvale, pritožbe in predlogi« na TV-ju in stran na telefonu gosta',
        enabled:             'Gostje lahko pošljejo mnenje',
        enabled_help:        'Izklopljeno: ploščice in strani ni.',
        retention_days:      'Hrani (dni)',
        retention_help:      '30–1095; sprememba velja tudi za že shranjena.',
        retention_rule:      'Od 30 do 1095 dni.',
        max_per_day:         'Največ na dan',
        max_per_day_help:    'Mnenj na bivanje na dan, 1–50.',
        max_rule:            'Od 1 do 50.',
        review_title:        'Ocenite nas',
        review_subtitle:     'Povezave za oceno hotela in kdaj jih gost vidi',
        policy_title:        'Povabilo dobi vsak gost',
        policy:              'Povezave za oceno so ponujene vsem gostom ne glede na oceno ali pritožbo — Google prepoveduje, da bi za oceno prosili samo zadovoljne goste. Pritožba gre najprej k recepciji, povezave so pod njo tišje.',
        google_help:         'Povezava za ocene iz Google Maps (»Prosite za ocene«).',
        https_only:          'Samo naslov https://',
        invite:              'Kdaj povabimo',
        invite_none:         'Brez izbire gost povezav ne vidi nikjer.',
        departure_from:      'Večer pred odhodom od',
        departure_from_help: 'Velja za »Na dan odhoda«.',
    },
    request_source: {
        tv:        'TV v sobi',
        guest_app: 'Telefon gosta',
    },
    booking_mode: {
        slots: 'Termini hiše',
        free:  'Prost čas v oknu',
    },
    invitation_booking: {
        title:         'Rezervacija v aplikaciji',
        enabled:       'Gost lahko rezervira na TV-ju in telefonu',
        enabled_help:  'Gumb »Rezerviraj«; rezervacijo potrdi recepcija v Zahtevah gostov. Plačilo na račun sobe.',
        mode:          'Kako gost izbere čas',
        slots:         'Termini',
        slots_help:    'Datum in ura termina ter največ oseb (0 = brez omejitve).',
        slot_add:      'Dodaj termin',
        capacity:      'Največ oseb',
        capacity_help: 'Na posamezen čas; 0 = brez omejitve.',
        persons_max:   'največ {n} oseb',
        no_limit:      'brez omejitve',
        from:          'Od',
        to:            'Do',
        step:          'Na',
        every_minutes: 'vsakih {n} min',
        max_persons:   'Največ oseb na rezervacijo',
        cutoff:        'Rezervacija najkasneje (minut prej)',
        cutoff_help:   'Termin izgine z izbire toliko minut pred začetkom.',
        slot_twice:    'Termin je naveden dvakrat.',
        slots_required: 'Dodajte vsaj en termin.',
        window_order:  'Konec okna mora biti za začetkom.',
    },
    room_service_menu: {
        title:              'Postrežba v sobo',
        subtitle:           'Jedilnik iz Imprion POS za naročila s TV-ja in telefona gosta',
        import:             'Uvozi jedilnik',
        sync:               'Osveži iz POS',
        synced:             'Osveženo: {items} artiklov, novih {added}, odstranjenih iz POS {missing}.',
        settings:           'Nastavitve',
        settings_help:      'Kdaj in kako gostje naročajo v sobo',
        enabled:            'Postrežba v sobo vklopljena',
        enabled_help:       'Gostje naročajo s TV-ja in telefona; plačilo na račun sobe.',
        hours:              'Čas naročanja',
        hours_help:         'Brez okna = ves dan. Okno, ki se konča pred začetkom, gre čez polnoč.',
        hours_add:          'Dodaj okno',
        clock_invalid:      'Vpišite uro kot HH:MM.',
        note:               'Vrstica na jedilniku',
        note_help:          'Npr. »Postrežba v sobo od 7.00 do 23.00«.',
        lead_minutes:       'Najkrajši čas do dostave (min)',
        lead_minutes_help:  'Za izbrani čas dostave.',
        prep_minutes:       'Običajen čas priprave (min)',
        prep_minutes_help:  'Ponujen kot predviden čas, ko kuhinja sprejme naročilo »čim prej«.',
        late_minutes:       'Prepozno po (min)',
        late_minutes_help:  'Če je predviden čas pri »čim prej« daljši od tega, lahko gost prekliče.',
        late_cancel_minutes:      'Preklic gosta še (min)',
        late_cancel_minutes_help: 'Koliko časa po sprejemu lahko gost prekliče prepozno naročilo; 0 = nikoli.',
        delivery:           'Dostava',
        delivery_help:      'Zaračuna se na vsakem naročilu v sobo kot svoja vrstica, ki je gost ne more odstraniti.',
        delivery_mode:      'Zaračunaj dostavo',
        delivery_off:       'Ne',
        delivery_item:      'Artikel z blagajne',
        delivery_fixed:     'Fiksni znesek',
        delivery_item_pick: 'Artikel',
        delivery_item_help: 'Kateri koli uvoženi artikel, tudi če ni ponujen gostom (npr. v skupini HOTELSKE STORITVE). Cena je cena z blagajne.',
        delivery_item_none: 'Izberite artikel',
        delivery_amount:    'Znesek ({currency})',
        delivery_name:      'Ime vrstice za gosta',
        delivery_name_help: 'Prazno = ime artikla (artikel z blagajne) oziroma »Dostava« v jeziku gosta.',
        delivery_free_from: 'Brezplačno nad ({currency})',
        delivery_free_from_help: 'Ko artikli dosežejo ta znesek, dostave ni; 0 = vedno.',
        max_open:           'Največ odprtih zahtev na sobo',
        max_open_help:      'Rezervacije in naročila skupaj.',
        max_quantity:       'Največja količina na vrstico',
        menu:               'Jedilnik',
        menu_help:          'Vklopite skupine in artikle, ki jih ponujate. Novi artikli iz POS pridejo vklopljeni, nove skupine izklopljene.',
        menu_help_kitchen:  'Artikel, ki ga zmanjka, označite kot razprodan.',
        search:             'Iskanje',
        empty:              'Jedilnik še ni uvožen iz Imprion POS.',
        items:              'Artikli',
        on_menu_n:          '{n} / {total} na jedilniku',
        group_edit:         'Skupina jedilnika',
        group_from_pos:     'V POS: {category} › {name}',
        group_name_help:    'Prazno = ime iz POS.',
        group_offered:      'Skupina je na jedilniku',
        item_edit:          'Artikel jedilnika',
        from_pos:           'Šifra {code} · {price} · DDV {vat}',
        guest_name:         'Ime za goste',
        guest_name_help:    'Prazno = ime iz POS.',
        description:        'Opis',
        photo:              'Fotografija',
        offered:            'Ponujamo v sobo',
        offered_help:       'Izklopljen artikel gost ne vidi.',
        offered_short:      'Ponujamo',
        available:          'Na voljo',
        sold_out:           'Razprodano',
        pos_missing:        'Ni več v POS',
        pos_disabled:       'Izklopljen v POS',
        last_sync:          'Zadnja osvežitev iz POS: {when}',
    },
    pos: {
        title:             'Imprion POS',
        subtitle:          'Od kod pride jedilnik (samo branje, blagajna ostane nedotaknjena)',
        base_url:          'Naslov API',
        base_url_help:     'Prazno = privzeti naslov Imprion POS.',
        https_only:        'Naslov mora biti https://.',
        token:             'Žeton API',
        token_help:        'Žeton iz blagajne Imprion POS (External API).',
        token_set:         'Žeton je shranjen. Prazno polje ga ohrani.',
        business_unit:     'Poslovna enota',
        business_unit_help:'Katere enote artikli so na jedilniku. »Preizkusi« jih našteje.',
        disabled:          'izklopljena',
        test:              'Preizkusi',
        test_ok:           'Povezava deluje: {n} poslovnih enot.',
        test_failed:       'Povezava ne deluje.',
    },
    milo: {
        figure:                  'Milo, pomočnik Imprion TV',
        title:                   'Milo',
        intro:                   'Vprašajte o nastavitvah prostora ali o tem, kako kaj naredite v konzoli.',
        intro_kitchen:           'Vprašajte o naročilih v sobo — Milo jih sprejme, označi kot dostavljena ali zavrne, ko potrdite.',
        intro_housekeeping:      'Vprašajte o zahtevah gostov — Milo jih sprejme, označi kot urejene ali zavrne, ko potrdite.',
        open:                    'Vprašaj Mila',
        new_conversation:        'Nov pogovor',
        not_configured_title:    'Milo še ni nastavljen',
        not_configured_help:     'Ponudnik še ni vnesel ključa za AI ali pa je Milo izklopljen za celotno namestitev.',
        open_provider_settings:  'Odpri nastavitve ponudnika',
        thinking:                'Razmišljam …',
        looking_up:              'Preverjam …',
        placeholder:             'Vprašajte Mila …',
        send:                    'Pošlji',
        left_today:              'še {n} sporočilo danes | še {n} sporočili danes | še {n} sporočila danes | še {n} sporočil danes',
        tool: {
            overview:         'Preverjam pregled prostora …',
            list_devices:     'Preverjam naprave …',
            list_rooms:       'Preverjam sobe …',
            list_channels:    'Preverjam kanale …',
            home_tiles:       'Preverjam ploščice domačega zaslona …',
            list_invitations: 'Preverjam vabila …',
            screen_themes:    'Preverjam teme zaslona …',
            find_local_events:   'Iščem dogodke v okolici …',
            propose_local_event: 'Pripravljam predlog dogodka …',
            transit_departures:  'Preverjam javni prevoz …',
            travel_time:         'Računam čas poti …',
        },
        error_rate:              'Preveč sporočil v kratkem času — počakajte trenutek.',
        error_not_configured:    'Milo še ni nastavljen.',
        error_unavailable:       'Milo trenutno ni na voljo.',
        error_budget:            'Današnja poraba za Mila je dosežena — poskusite znova jutri.',
        error_limit:             'Danes ste Mila že veliko spraševali — poskusite znova jutri.',
        error_full:              'Pogovor je dolg — začnite novega.',
        error_failed:            'Milo se ta trenutek ne more povezati. Poskusite znova.',
        error_closed:            'Ta predlog ni več veljaven.',
        error_busy:              'Milo še odgovarja na prejšnje vprašanje.',
        error_voice_unavailable: 'Glas ni na voljo.',
        error_voice_failed:      'Storitev za glas se ni odzvala.',
        error_network:           'Povezava s strežnikom ni uspela.',
        state_pending:           'Čaka potrditev',
        state_done:              'Opravljeno',
        state_failed:            'Ni uspelo',
        state_cancelled:         'Preklicano',
        state_expired:           'Poteklo',
        pms_conflict:            'PMS za to sobo poroča bivanje — na TV velja bivanje iz PMS.',
        proposal_feedback_reply:  'Odgovor gostu',
        proposal_feedback_status: 'Status povratne informacije',
        proposal_reply_language:  'Jezik: {language}',
        proposal_then_status:     'Nato status: {status}',
        proposal_new_status:      'Nov status: {status}',
        proposal_note:            'Interni zapisek',
        proposal_reply_hint:      'Gost ga prejme na telefon (in na sobni TV, dokler je v hiši) šele, ko potrdite.',
        proposal_local_event:     'Dogodek za Okolico',
        event_at:                 '{day} ob {time}',
        event_source:             'Vir:',
        event_picture_source:     'Slika: stran organizatorja',
        event_picture_ai:         'Slika: pripravil Milo (AI)',
        event_picture_later:      'Fotografijo dodate v Vsebina › Hotel.',
        event_picture_use_source: 'Slika s strani',
        event_picture_use_ai:     'Slika AI',
        event_picture_regenerate: 'Druga slika AI',
        event_picture_remove:     'Brez slike',
        proposal_remove_local_event: 'Odstranitev dogodka iz Okolice',
        event_past:               'pretekli',
        event_remove_note:        'Dogodek bo odstranjen z Okolice.',
        proposal_seasonal_tiles:  'Okraski na slikah ploščic',
        season_tiles_new:         'Nova sezonska tema samo s slikami ploščic, osnova {base}.',
        season_tiles_skipped:     'Preskočeno: {list}',
        season_tiles_note:        'Slike hotela ostanejo; okrašene se pokažejo le v obdobju teme. Priprava traja nekaj minut.',
        event_translating:        'Milo prevaja v jezike hotela in shranjuje …',
    },
    milo_surface: {
        guest:   'Telefon gosta',
        screen:  'Sobni TV',
        console: 'Konzola',
    },
    milo_language: {
        sl: 'Slovenščina',
        en: 'Angleščina',
        de: 'Nemščina',
        it: 'Italijanščina',
        hr: 'Hrvaščina',
        hu: 'Madžarščina',
    },
    milo_error_kind: {
        credit:      'Na računu ponudnika ni dobroimetja — dopolnite ga v konzoli ponudnika.',
        auth:        'Ponudnik ključa ne sprejme: ni veljaven ali je bil preklican.',
        rate_limit:  'Ponudnik omejuje število zahtev — poskusite čez nekaj minut.',
        overloaded:  'Ponudnik je trenutno preobremenjen — poskusite znova kasneje.',
        server:      'Napaka na strani ponudnika.',
        request:     'Ponudnik je zahtevo zavrnil (npr. model za ta ključ ni na voljo).',
        timeout:     'Ponudnik ni odgovoril pravočasno.',
        unreachable: 'Ponudnik ni dosegljiv (omrežje ali DNS).',
    },
    milo_gender: {
        male:    'moški',
        female:  'ženski',
        neutral: 'nevtralen',
    },
    milo_ai_provider: {
        anthropic: 'Anthropic (Claude)',
        openai:    'OpenAI (GPT)',
        soniox:    'Soniox',
        tomtom:    'TomTom',
    },
    milo_settings: {
        title:                    'Milo',
        subtitle:                 'AI pomočnik za goste na telefonu in TV ter za osebje v konzoli.',
        tab_settings:             'Nastavitve',
        tab_usage:                'Poraba',
        not_configured_tenant:    'Ponudnik še ni vnesel ključa za AI. Nastavitve lahko pripravite že zdaj; Milo začne delovati, ko bo ključ vnesen.',
        where:                    'Kje je Milo na voljo',
        where_help:               'Trije neodvisni stikali — za telefon gosta in sobni TV je Milo izklopljen, dokler ga ne vklopite.',
        phone_enabled:            'V aplikaciji za goste (telefon)',
        phone_enabled_help:       'Gost klepeta z Milom v aplikaciji, ki jo odpre s QR kodo na TV.',
        tv_enabled:               'Na sobnih televizorjih',
        tv_enabled_help:          'Na domačem zaslonu TV se prikaže ploščica Milo s predlaganimi vprašanji.',
        console_enabled:          'V konzoli za osebje',
        console_enabled_help:     'Gumb Milo v zgornji vrstici konzole.',
        figure:                   'Videz',
        figure_help:              'Kako je Milo narisan na sobnem TV-ju, v aplikaciji za goste in v konzoli.',
        figure_3d:                'Gibljiva 3D glava',
        figure_3d_help:           'Izklopljeno: Milo se pokaže s statičnimi pozami, ki se še vedno menjajo glede na to, kaj počne. Primerno za šibkejše TV-je.',
        traffic:                  'Promet (TomTom)',
        traffic_help:             'Milo pove, koliko traja vožnja z upoštevanjem prometa, in opozori na zastoje, nesreče, dela na cesti in zapore v okolici ali na poti.',
        traffic_not_configured:   'Ponudnik še ni vnesel ključa TomTom — Milo časov vožnje računa brez prometa.',
        traffic_enabled:          'Milo upošteva promet',
        traffic_enabled_help:     'Izklopljeno: Milo za ta hotel ne bere podatkov o prometu.',
        instructions:             'Navodila hiše',
        instructions_help:        'Ton, kaj naj Milo priporoča in o čem naj ne govori. Navodila ne morejo razširiti tega, kar Milo sme narediti.',
        instructions_placeholder: 'Npr.: Gostom vedno priporoči večerjo v naši restavraciji. Tikaj goste. O cenah sob ne govori.',
        voice:                    'Glas',
        voice_help:               'Milo lahko odgovore izgovori in na telefonu posluša (Soniox).',
        voice_not_configured:     'Ponudnik še ni vnesel ključa za glas (Soniox), zato je glas skrit.',
        tts_tv:                   'Milo govori na TV',
        tts_tv_help:              'Odgovori se predvajajo na sobnem televizorju; velja glasnost TV.',
        tts_phone:                'Milo govori na telefonu',
        tts_phone_help:           'Gost lahko zvok v aplikaciji utiša.',
        stt_phone:                'Mikrofon na telefonu',
        stt_phone_help:           'Gost vprašanje izgovori namesto da ga natipka.',
        speed:                    'Hitrost govora',
        speed_help:               'Od 0,70 do 1,30 (1,00 = običajno).',
        tts_characters:           'Izgovorjeni znaki na bivanje na dan',
        tts_characters_help:      'Ko so porabljeni, so odgovori le še besedilo. 0 = brez govora.',
        voices:                   'Glas po jezikih',
        voices_reload:            'Ponovno naloži glasove',
        voice_default:            'Privzeti glas ponudnika',
        voice_cloned:             'klonirani',
        limits:                   'Dnevne omejitve',
        limits_help:              'Štejejo sporočila; nov pogovor jih ne ponastavi.',
        stay_daily:               'Sporočila gosta na dan',
        stay_daily_help:          'Na bivanje, telefon in TV skupaj.',
        user_daily:               'Sporočila zaposlenega na dan',
        tenant_daily:             'Vsa sporočila prostora na dan',
        retention:                'Hramba pogovorov (dni)',
        retention_help:           'Sporočila se izbrišejo po toliko dneh. Sprememba velja za nova sporočila.',
        turns:                    'Dolžina pogovora',
        turns_help:               'Koliko vprašanj sprejme en pogovor z Milom.',
        guest_turns:              'Vprašanja gosta v enem pogovoru',
        guest_turns_help:         'Največ vprašanj v enem pogovoru z Milom — na telefonu in televizorju. Ko jih gost porabi, začne nov pogovor.',
        console_turns:            'Vprašanja zaposlenega v enem pogovoru',
        console_turns_help:       'Največ vprašanj v enem pogovoru z Milom v konzoli. Ko jih porabi, začne nov pogovor.',
        models:                   'Modeli',
        models_help:              'Modele izbere ponudnik.',
        models_help_provider:     'Prazno = model ponudnika. Preglasitev velja samo za ta prostor (isti ponudnik).',
        guest_model:              'Model za goste',
        console_model:            'Model za konzolo',
        effective:                'V uporabi: {model}',
        model_default:            'model ponudnika',
        model_invalid:            'Neveljavno ime modela.',
        range:                    'Med {min} in {max}.',
        preview:                  'Preizkusi',
        preview_stop:             'Ustavi',
        preview_aria:             'Preizkusi glas: {language}',
        preview_text:             'Besedilo za preizkus',
        preview_text_help:        'Prazno = Milov pozdrav v imenu hotela v jeziku glasu. Upošteva trenutno hitrost govora, tudi neshranjeno.',
        preview_text_placeholder: 'Npr.: Zajtrk strežemo od 7. do 10. ure v restavraciji v pritličju.',
        preview_unplayable:       'Brskalnik posnetka ne more predvajati.',
    },
    milo_usage: {
        window:         'Od {from} do {to}',
        last_days:      'Zadnjih {n} dni',
        tokens_line:    'Žetoni: {input} vhod · {output} izhod',
        voice_line:     'Glas: {characters} znakov · mikrofon {sessions}×',
        by_day:         'Po dnevih',
        empty:          'V tem obdobju Mila ni nihče uporabljal.',
        date:           'Dan',
        surface:        'Kje',
        messages:       'Sporočila',
        calls:          'Klici modela',
        input_tokens:   'Vhodni žetoni',
        output_tokens:  'Izhodni žetoni',
        cache_tokens:   'Predpomnilnik',
        tts_characters: 'Izgovorjeni znaki',
        stt:            'Mikrofon',
        stt_cell:       '{sessions}× · {seconds} s',
        errors:         'Napake',
        by_tenant:      'Poraba po strankah',
        by_tenant_help: 'Vse stranke skupaj v izbranem obdobju, najbolj dejavne najprej.',
        tenant:         'Stranka',
    },
    milo_provider: {
        title:                 'Milo — ponudniki AI',
        subtitle:              'Ključi, modeli in glas za vse stranke te namestitve.',
        tab_ai:                'Razmišljanje',
        tab_voice:             'Glas',
        tab_usage:             'Poraba',
        tab_traffic:           'Promet',
        master:                'Milo za celotno namestitev',
        master_help:           'Izklop ustavi Mila povsod, ne glede na nastavitve strank.',
        enabled:               'Milo je vklopljen',
        keys:                  'Ključi API',
        keys_help:             'Ključ se shrani šifrirano in se nikoli več ne prikaže — vidni so le zadnji štirje znaki.',
        models:                'Ponudnik in model',
        models_help:           'Za vsako skupino posebej. Model lahko izberete s seznama (po preizkusu povezave) ali ga vpišete.',
        group_guest:           'Gostje (telefon in TV)',
        group_console:         'Konzola (osebje)',
        model:                 'Model',
        model_no_key:          'Za tega ponudnika ključ ni shranjen — Milo tu ne bo deloval.',
        model_help:            'Prazno = {model}',
        image_model:           'Model za slike dogodkov (OpenAI)',
        image_model_help:      'Prazno = {model}. Milo z njim pripravi sliko dogodka, ko stran dogodka nima svoje; potreben je ključ OpenAI.',
        budget:                'Dnevni proračun žetonov',
        budget_help:           'Vhodni in izhodni žetoni na dan (UTC), vse stranke skupaj. 0 = brez omejitve.',
        soniox:                'Soniox',
        soniox_help:           'Govor v besedilo (mikrofon na telefonu) in besedilo v govor (TV in telefon). Regija ključa se zazna sama.',
        voice_models:          'Modeli za glas',
        voice_models_help:     'Prazno = privzeti model. Seznam se napolni po preizkusu povezave.',
        stt_model:             'Govor v besedilo (v živo)',
        stt_async_model:       'Govor v besedilo (posnetek)',
        stt_async_help:        'Za brskalnike, ki zvoka ne morejo pošiljati sproti.',
        tts_model:             'Besedilo v govor',
        default_voice:         'Privzeti glas',
        default_voice_help:    'Velja, kjer stranka za jezik ni izbrala svojega glasu. Seznam vključuje klonirane glasove projekta.',
        voices_need_key:       'Najprej shranite ključ Soniox.',
        voice_placeholder:     'Izberite glas',
        voice_languages:       'Jeziki modela: {list}',
        soniox_rejected:       'Ključa Soniox ni sprejela nobena regija.',
        key_removing:          'Odstranjen ob shranjevanju',
        key_set:               'Ključ je nastavljen {hint}',
        key_unset:             'Ni nastavljen',
        region:                'Regija {region}',
        key_remove:            'Odstrani',
        key_keep:              'Obdrži',
        key_replace_help:      'Vpišite nov ključ, da zamenjate shranjenega; prazno ga obdrži.',
        key_help:              'Ključ se shrani ob shranjevanju.',
        test:                  'Preizkusi povezavo',
        test_ok:               'Povezava deluje — modelov: {count}',
        test_failed:           'Povezava ni uspela',
        pick_model:            'Izberi model',
        pick_model_test_first: 'Za seznam modelov najprej preizkusite povezavo',
        last_error:            'Zadnja napaka ponudnika',
        last_error_meta:       '{time} · {where}',
        last_error_status:     'HTTP {status}: {message}',
        last_error_cleared:    'Izgine ob prvem uspešnem klicu ali preizkusu povezave. Gostje vidijo le »Milo se ta trenutek ne more povezati«.',
        where_guest:           'telefon gosta',
        where_screen:          'sobni TV',
        where_console:         'konzola',
        where_settings:        'nastavitve',
        preview_language:      'Jezik preizkusa',
        preview_text_help:     'Prazno = Milov pozdrav v izbranem jeziku. Velja vpisani model in glas, tudi neshranjena.',
        traffic:               'Promet (TomTom)',
        traffic_help:          'En račun TomTom za vse hotele: Milo z njim računa čas vožnje s prometom in pove za zastoje, nesreče, dela na cesti in zapore. Ključ ustvarite na developer.tomtom.com.',
        tomtom_ok:             'Povezava deluje — ključ je sprejet',
        traffic_usage:         'Ta mesec: poti {routing} / {routing_free}, dogodki {incidents} / {incidents_free}, iskanje {geocode} / {geocode_free}',
        traffic_usage_help:    'Zahtevki v tem mesecu (UTC) proti brezplačni kvoti TomTom. Milo se ustavi prej — pri {routing} poteh, {incidents} dogodkih in {geocode} iskanjih — in gostu pove, da podatki o prometu trenutno niso na voljo.',
    },
    stay_state: {
        reserved: 'Rezervirano',
        in_room:  'V sobi',
        departed: 'Odjavljen',
    },
    stay_action: {
        created:     'Vneseno',
        updated:     'Spremenjeno',
        checked_in:  'Prijava',
        checked_out: 'Odjava',
    },
    occupancy: {
        title:              'Zasedenost',
        subtitle:           'Kdo je v kateri sobi: prihodi, odhodi in gostje. Ob prihodu TV v sobi pozdravi gosta, ob odhodu se ponastavi za naslednjega.',
        room:               'Soba',
        guest:              'Gost',
        new_stay:           'Nov gost',
        new_stay_help:      'Vnos bivanja — TV v sobi se ga dotakne šele ob prihodu.',
        settings:           'Nastavitve',
        settings_help:      'Ura prihoda in odhoda ter samodejna prijava in odjava.',
        today:              'Danes',
        week_back:          'Teden nazaj',
        week_forward:       'Teden naprej',
        zoom_two_weeks:     '2 tedna',
        zoom_month:         'Mesec',
        search:             'Išči sobo ali gosta',
        only_free_tonight:  'Samo proste nocoj',
        free_tonight:       'Prosta nocoj',
        tonight:            'Nocoj zasedenih {occupied} / {rooms}',
        from_pms:           'Iz PMS',
        conflict:           'Konflikt s PMS',
        conflicts:          '{n} bivanje se prekriva s PMS | {n} bivanji se prekrivata s PMS | {n} bivanja se prekrivajo s PMS | {n} bivanj se prekriva s PMS',
        conflict_help:      'Bivanje iz PMS ima prednost — TV v sobi pokaže gosta iz PMS. Ročno bivanje izbrišite ali prestavite.',
        pms_failed:         'PMS ni odgovoril — prikazana so le ročno vnesena bivanja.',
        pms_guest:          'Gost (PMS)',
        pms_stay:           'Bivanje iz PMS',
        pms_read_only:      'To bivanje prihaja iz PMS in ga tu ni mogoče spreminjati.',
        unassigned:         'Nedodeljeno (PMS)',
        unassigned_room:    '{type} — soba še ni dodeljena',
        unassigned_help:    'PMS temu bivanju še ni dal sobe: v Zasedenosti je pod vrsto sobe, TV ga ne pokaže.',
        pms_unmapped:       '{n} bivanje iz PMS je v sobi, ki ni povezana z našo | {n} bivanji iz PMS sta v sobah, ki niso povezane z našimi | {n} bivanja iz PMS so v sobah, ki niso povezane z našimi | {n} bivanj iz PMS je v sobah, ki niso povezane z našimi',
        pms_unmapped_help:  'Povežite sobe na strani Povezava s PMS — šele potem se pokažejo tu in na TV-ju.',
        open_connector:     'Povezava s PMS',
        arrival:            'Prihod',
        arrival_unknown:    'ni znan (trenutno bivanje)',
        departure:          'Odhod',
        departure_rule:     'Odhod mora biti za prihodom.',
        first_name:         'Ime',
        first_name_help:    'Na TV: »Dobrodošli, {name}«.',
        first_name_help_empty: 'TV v sobi gosta pozdravi s tem imenom.',
        last_name:          'Priimek',
        language:           'Jezik',
        language_help:      'TV v sobi bo v tem jeziku.',
        language_house:     'Jezik hiše',
        guests:             'Število gostov',
        note:               'Opomba',
        note_help:          'Samo za recepcijo — ni vidna na TV ali telefonu.',
        nights:             '{n} noč | {n} noči | {n} noči | {n} noči',
        check_in:           'Prijavi',
        check_out:          'Odjavi',
        check_in_help:      'Prijava pripravi sobo za gosta: nova koda QR, ime in jezik na TV-ju.',
        check_out_help:     'Odjava ponastavi TV za naslednjega gosta (aplikacije, budilka, jezik, nova koda QR).',
        confirm_check_in:   'Prijavim gosta {name} v sobo {room}? TV v sobi ga bo pozdravil v roku ene minute.',
        confirm_check_out:  'Odjavim gosta {name} iz sobe {room}? TV se ponastavi za naslednjega gosta.',
        checked_in:         'Gost je prijavljen.',
        checked_out:        'Gost je odjavljen, TV se ponastavlja.',
        checked_in_at:      'prijavljen {at}',
        checked_out_at:     'odjavljen {at}',
        delete:             'Izbriši bivanje',
        confirm_delete:     'Izbrišem rezervacijo za {name}?',
        history:            'Zgodovina',
        by_auto:            'samodejno',
        read_only:          'Bivanja lahko spreminjata le skrbnik in recepcija.',
        departed_read_only: 'Odjavljeno bivanje je zapis — ne spreminja se več.',
        clash:              'V tej sobi je takrat že drug gost.',
        clash_short:        'Zasedeno',
        retry:              'Poskusi znova',
        no_rooms:           'Hotel še nima sob — dodajte jih na strani Sobe.',
        no_match:           'Nobena soba ne ustreza filtru.',
        hint_mouse:         'Povlecite čez proste dni za nov vnos, povlecite konec za podaljšanje, povlecite pas v drugo sobo ali na druge dni, kliknite za urejanje.',
        hint_touch:         'Tapnite prost dan za nov vnos ali pas za urejanje; konec pasu lahko povlečete.',
        check_in_time:      'Ura prihoda',
        check_out_time:     'Ura odhoda',
        auto_check_in:      'Samodejna prijava',
        auto_check_in_help: 'Na dan prihoda ob uri prihoda se TV pripravi za gosta.',
        auto_check_out:     'Samodejna odjava',
        auto_check_out_help: 'Na dan odhoda ob uri odhoda se TV ponastavi za naslednjega gosta.',
        retention:          'Hrani dni po odhodu',
        retention_help:     'Potem se bivanje izbriše (30–3650 dni).',
        card_title:         'Zasedenost danes',
        open_chart:         'Zasedenost',
        rooms_occupied:     'sob zasedenih nocoj',
        arrivals_today:     'prihodov danes',
        departures_today:   'odhodov danes',
        field: {
            room_id:    'soba',
            arrival:    'prihod',
            departure:  'odhod',
            first_name: 'ime',
            last_name:  'priimek',
            language:   'jezik',
            guests:     'gostje',
            note:       'opomba',
        },
    },
}

export type MessageSchema = typeof sl

export default sl
